import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useParams, useNavigate, useLocation, Outlet, Routes, Route, Link, Navigate } from "react-router";
import { ReactiveCore, replaceRows, DexieDatabaseProvider, useReactor, useSuccessReactor, useErrorReactor } from "@livefree/reactive";
import { MqlRequestBuilder, RequestMetadata, dexieRequestBrokerAddon, RequestBroker, Paging, Sorting, HorizontalControl, HorizontalPager, RequestBrokerTable, RequestBrokerDataGrid } from "@livefree/request-broker";
import { RestClient, ApiError } from "@livefree/js-sdk";
import { EnvironmentConfigurations, useComponent, alerts, useRuntimeProvider, defineUserContext, useUserContext } from "@livefree/runtime";
import { objectPick, deepEquals, parseAndFormatDate, parseAndFormatDateTime, objectEquals, lowerCase, trim } from "@livefree/common";
import { App, RequirePolicy, useRecentView, createEmbed } from "@livefree/applications";
import { Tabs, Accordion, Chip, Card, Switch, Label, Spinner, Button, Alert, ListBox, Description, Table, Breadcrumbs, BreadcrumbsItem, Fieldset, Checkbox, CardContent, DisclosureGroup, Disclosure, Tooltip, Dropdown } from "@heroui/react";
import { CopyToClipboard, GoogleAutocomplete, formatPhone, NotFound, Loading, IconButton, DeleteButton, Pill, ResourceActions, withDrawerController, FlatCard, SearchComboBox, SearchPanel, ErrorListChip, ConfirmableButton, useDrawerController, Breadcrumb, validators, SaveButton, withModalController, masks, Forbidden, DrawerActions, ModifyButton, Grid, TextWithCopy } from "@livefree/react-ui";
import React, { useState, useCallback, useEffect, useReducer, createContext, useContext, useMemo, useRef, useImperativeHandle, createElement } from "react";
import { Uniformly, FieldStatus, TextField, SelectField, AutocompleteField, TextAreaField, CheckboxField, DateTimeField, FormField, EmailField, PhoneField, AddressForm as AddressForm$1, TabsField, CardField, BankAccountField, IconLabel, DateField } from "@livefree/uniformly";
import { Filters, FilterConstant, FilterSelect, FilterInput, FilterDateRange, FilterToggle } from "@livefree/filters";
import { EmptyState, Widget, ItemCard } from "@heroui-pro/react";
import { RiLinkM, RiAddLine } from "@remixicon/react";
const AccountType$1 = {
  Dealer: 1,
  Customer: 2
};
class AccountsClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "accounts");
  }
  /**
   * `POST /accounts/search` — find accounts of any kind by name, dealer, phone or account number.
   *
   * A POST for a read: the search text goes in the body rather than the URL, the same reasoning the
   * customer lookup uses. Answers an empty list when nothing matches.
   *
   * Guard against a blank `request.query` before calling — the endpoint rejects one with a 400
   * ("Query cannot be empty.") rather than answering with every account.
   */
  async search(request) {
    return await this.httpPOST({
      url: this.appendUrl("/search"),
      body: request
    });
  }
}
class CustomersClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "accounts/customers");
  }
  /**
   * `POST /accounts/customers` — create a customer together with its physical and shipping
   * addresses, and get the saved trio back.
   */
  async createCustomer(request) {
    return await this.create(request);
  }
  /** `GET /accounts/customers/{id}` — fetch a single customer by account id. */
  async getById(id) {
    return await this.fetch(id);
  }
  /**
   * `PUT /accounts/customers/{id}` — replace a customer and, when supplied, its addresses.
   *
   * The API replaces rather than patches, so `request.customer` must be the whole customer. Read
   * it with {@link getById} first and spread your changes over it.
   */
  async updateCustomer(id, request) {
    return await this.update(id, request);
  }
  /** `DELETE /accounts/customers/{id}` — soft-delete a customer. Answers 204 with no body. */
  async deleteCustomer(id) {
    await this.delete(id);
  }
  /**
   * `POST /accounts/customers/lookup` — every customer whose last name and billing postal code
   * match. A pair that matches nobody is an empty list, not a 404, so the caller decides what one,
   * none or several matches mean.
   *
   * A POST for a read, deliberately: the body keeps the name and postal code out of the URL,
   * request logs and browser history.
   */
  async lookup(request) {
    return await this.httpPOST({
      url: this.appendUrl("/lookup"),
      body: request
    });
  }
  /**
   * `POST /accounts/customers/query` — run an MQL query over customers and get back a page of
   * results (`values` plus `totalCount`/`skip`/`take`).
   *
   * Pass the request-broker `metadata` (the filters/sorting/paging the frontend collected); it's
   * compiled to an MQL string via {@link MqlRequestBuilder} before invoking the API. With no
   * metadata, all customers are fetched. Named `queryMql` because `query` is reserved on
   * `BaseEndpoint`.
   *
   * Sent as `text/plain`: the route binds a `PlainTextBody`, and the SDK's serializer only
   * JSON-encodes when the content type is `application/json` — the default would post a
   * JSON-quoted string the API cannot bind.
   */
  async queryMql(metadata) {
    const query = metadata ? MqlRequestBuilder.create(metadata).build("CustomerModel") : "FETCH CustomerModel;";
    return await this.httpPOST({
      url: this.appendUrl("/query"),
      body: query,
      options: { contentType: "text/plain" }
    });
  }
  /** `GET /accounts/customers/{id}/contacts` — every emergency contact for a customer. */
  async getContacts(id) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/contacts`)
    });
  }
  /** `GET /accounts/customers/{id}/contacts/{contactId}` — a single emergency contact. */
  async getContact(id, contactId) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/contacts/${contactId}`)
    });
  }
  /**
   * `POST /accounts/customers/{id}/contacts` — add an emergency contact to a customer.
   *
   * The route stamps `customerId` from the URL onto the model, so the body need not carry it.
   */
  async createContact(id, contact) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/contacts`),
      body: contact
    });
  }
  /**
   * `PUT /accounts/customers/{id}/contacts/{contactId}` — replace an emergency contact.
   *
   * The route stamps both `id` and `customerId` from the URL onto the model.
   */
  async updateContact(id, contactId, contact) {
    return await this.httpPUT({
      url: this.appendUrl(`/${id}/contacts/${contactId}`),
      body: contact
    });
  }
  /**
   * `DELETE /accounts/customers/{id}/contacts/{contactId}` — remove an emergency contact.
   * Answers 204 with no body.
   */
  async deleteContact(id, contactId) {
    await this.httpDELETE({ url: this.appendUrl(`/${id}/contacts/${contactId}`) });
  }
  /**
   * `GET /accounts/customers/{id}/linked-accounts` — the customer's parent or children. An unlinked
   * customer answers an empty model, not a 404.
   */
  async getLinkedAccounts(id) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/linked-accounts`)
    });
  }
  /**
   * `POST /accounts/customers/{id}/linked-accounts` — link `childId` as a child of this customer,
   * and get the parent's links back.
   */
  async linkCustomer(id, childId) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/linked-accounts`),
      body: { childId }
    });
  }
  /**
   * `DELETE /accounts/customers/{id}/linked-accounts/{linkedId}` — remove the link from either side:
   * `linkedId` is this customer's child or its parent. Answers 204 with no body.
   */
  async unlinkCustomer(id, linkedId) {
    await this.httpDELETE({ url: this.appendUrl(`/${id}/linked-accounts/${linkedId}`) });
  }
}
class DealersClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "accounts/dealers");
  }
  /**
   * `POST /accounts/dealers` — create a dealer together with its physical and shipping addresses,
   * and get the saved trio back.
   */
  async createDealer(request) {
    return await this.create(request);
  }
  /** `GET /accounts/dealers/{id}` — fetch a single dealer by account id. */
  async getById(id) {
    return await this.fetch(id);
  }
  /**
   * `PUT /accounts/dealers/{id}` — replace a dealer and, when supplied, its addresses.
   *
   * The API replaces rather than patches, so `request.dealer` must be the whole dealer. Read it
   * with {@link getById} first and spread your changes over it. The route stamps `id` from the URL
   * onto `request.dealer`.
   */
  async updateDealer(id, request) {
    return await this.update(id, request);
  }
  /** `DELETE /accounts/dealers/{id}` — soft-delete a dealer. Answers 204 with no body. */
  async deleteDealer(id) {
    await this.delete(id);
  }
  /**
   * `GET /accounts/dealers/me` — the dealer the signed-in user acts as: their own dealer, or the
   * dealer a dealer user belongs to. Resolved from the token, so it can only answer about the caller,
   * and it needs no Dealers policy.
   *
   * `null` rather than a rejection when the user is nobody's dealer (an administrator, say) — the
   * route's 404 is an ordinary answer here, not a failure.
   */
  async getMine() {
    try {
      return await this.httpGET({ url: this.appendUrl("/me") });
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) return null;
      throw error;
    }
  }
  /**
   * `POST /accounts/dealers/query` — run an MQL query over dealers and get back a page of results
   * (`values` plus `totalCount`/`skip`/`take`).
   *
   * Sent as `text/plain`: the route binds a `PlainTextBody`, and the SDK's serializer only
   * JSON-encodes when the content type is `application/json`. Named `queryMql` because `query` is
   * reserved on `BaseEndpoint`.
   */
  async queryMql(metadata) {
    const query = metadata ? MqlRequestBuilder.create(metadata).build("DealerModel") : "FETCH DealerModel;";
    return await this.httpPOST({
      url: this.appendUrl("/query"),
      body: query,
      options: { contentType: "text/plain" }
    });
  }
  /** `GET /accounts/dealers/{id}/users` — every user belonging to a dealer. */
  async getUsers(id) {
    return await this.httpGET({ url: this.appendUrl(`/${id}/users`) });
  }
  /** `GET /accounts/dealers/{id}/users/{userId}` — a single dealer user. */
  async getUser(id, userId) {
    return await this.httpGET({
      url: this.appendUrl(`/${id}/users/${userId}`)
    });
  }
  /**
   * `POST /accounts/dealers/{id}/users` — add a user to a dealer.
   *
   * The route stamps the owning dealer onto the model as `parentId`, so the body need not carry it.
   */
  async createUser(id, user) {
    return await this.httpPOST({
      url: this.appendUrl(`/${id}/users`),
      body: user
    });
  }
  /**
   * `PUT /accounts/dealers/{id}/users/{userId}` — replace a dealer user.
   *
   * The route stamps both `id` and `parentId` from the URL onto the model.
   */
  async updateUser(id, userId, user) {
    return await this.httpPUT({
      url: this.appendUrl(`/${id}/users/${userId}`),
      body: user
    });
  }
  /**
   * `DELETE /accounts/dealers/{id}/users/{userId}` — remove a dealer user. Answers 204 with no
   * body.
   */
  async deleteUser(id, userId) {
    await this.httpDELETE({ url: this.appendUrl(`/${id}/users/${userId}`) });
  }
}
class AccountNotesClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "accounts/notes");
  }
  /** `GET /accounts/notes/types` — the selectable note kinds, from the lookup table. */
  async getTypes() {
    return await this.httpGET({ url: this.appendUrl("/types") });
  }
  /** `GET /accounts/notes?accountId=` — every note on an account. */
  async getByAccount(accountId) {
    return await this.get({ accountId: String(accountId) });
  }
  /** `GET /accounts/notes/{noteId}` — a single note. `noteId` is a GUID. */
  async getById(noteId) {
    return await this.fetch(noteId);
  }
  /** `POST /accounts/notes` — add a note. The body carries the account it belongs to. */
  async createNote(note) {
    return await this.create(note);
  }
  /**
   * `PUT /accounts/notes/{noteId}` — replace a note.
   *
   * The route stamps `id` from the URL onto the model and ignores the body's `accountId`: the
   * service holds a note to the account it was created against.
   */
  async updateNote(noteId, note) {
    return await this.update(noteId, note);
  }
  /** `DELETE /accounts/notes/{noteId}` — remove a note. Answers 204 with no body. */
  async deleteNote(noteId) {
    await this.delete(noteId);
  }
  /**
   * `POST /accounts/notes/query?accountId=` — run an MQL query over one account's notes.
   *
   * The route scopes the query to `accountId` server-side, so the MQL itself needs no account
   * filter. Sent as `text/plain`, matching the route's `PlainTextBody` binding.
   */
  async queryMql(accountId, metadata) {
    const query = metadata ? MqlRequestBuilder.create(metadata).build("NoteModel") : "FETCH NoteModel;";
    return await this.httpPOST({
      url: this.appendUrl("/query"),
      query: { accountId: String(accountId) },
      body: query,
      options: { contentType: "text/plain" }
    });
  }
}
class AccountAddressesClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "accounts/addresses");
  }
  /** `GET /accounts/addresses?accountId=&type=` — an account's addresses, optionally by kind. */
  async getByAccount(accountId, type) {
    return await this.get({
      accountId: String(accountId),
      ...type === void 0 ? {} : { type: String(type) }
    });
  }
}
class AccountPaymentProfilesClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "accounts/payment-profiles");
  }
  /** `GET /accounts/payment-profiles?accountId=` — every payment profile on an account. */
  async getByAccount(accountId) {
    return await this.get({ accountId: String(accountId) });
  }
  /** `GET /accounts/payment-profiles/{profileId}` — a single payment profile. */
  async getById(profileId) {
    return await this.fetch(profileId);
  }
  /**
   * `POST /accounts/payment-profiles` — store a payment method, with its billing address, and get
   * the saved pair back.
   *
   * Nothing stamps the account any more, so `request.profile.accountId` must name it.
   */
  async createProfile(request) {
    return await this.create(request);
  }
  /**
   * `PUT /accounts/payment-profiles/{profileId}` — update the editable fields of a payment profile
   * and its billing address.
   *
   * A narrow patch, not a replace: card and bank numbers cannot be changed this way. The route
   * stamps `id` from the URL onto the request.
   */
  async updateProfile(profileId, request) {
    return await this.update(profileId, request);
  }
  /** `DELETE /accounts/payment-profiles/{profileId}` — remove a profile. Answers 204, no body. */
  async deleteProfile(profileId) {
    await this.delete(profileId);
  }
  /**
   * `POST /accounts/payment-profiles/{profileId}/default?accountId=` — make this the account's
   * default payment profile. Answers 204 with no body.
   *
   * Its own route rather than a field on the update payload: the default is recorded on the
   * account's subscription, not on the profile, so a PUT carrying it would look like it were
   * writing the profile. The account stays explicit because the service proves the profile belongs
   * to it before writing — the two ids are otherwise unrelated numbers.
   */
  async setDefault(accountId, profileId) {
    await this.httpPOST({
      url: this.appendUrl(`/${profileId}/default`),
      query: { accountId: String(accountId) }
    });
  }
}
class CustomerPaymentPlansClient extends RestClient {
  constructor(baseUrl) {
    super(baseUrl, "collections/payment-plans");
  }
  /**
   * `PUT /collections/payment-plans/payment-profile` — point every open plan on the customer
   * (not Completed or Canceled) at the profile. Answers with the plans that moved; empty when none
   * needed to. A profile that is not the customer's or cannot be charged is a 400.
   */
  async moveOpenPlansToProfile(request) {
    return await this.httpPUT({
      url: this.appendUrl("/payment-profile"),
      body: request
    });
  }
}
const AccountConfig = EnvironmentConfigurations.hydrate({
  development: {
    host: "https://localhost:9002",
    api: {
      host: "https://localhost:7104",
      apiKey: "AccountsAPI"
    },
    extended: {
      portal2: "https://portal2.local.net",
      features: {
        customers: true,
        dealers: true
      }
    }
  },
  staging: {
    host: "https://lf-artemis-staging.azurewebsites.net",
    api: {
      host: "https://lf-artemis-api-staging.azurewebsites.net",
      apiKey: "AccountsAPI"
    },
    extended: {
      portal2: "lf-portal-v2-staging.azurewebsites.net",
      features: {
        customers: true,
        dealers: true
      }
    }
  },
  production: {
    host: [
      "https://artemis.lifebeacon.com",
      "https://app.alert1.com",
      "https://app.lifebeacon.com"
    ],
    api: {
      host: "https://artemis.lfapi.net",
      apiKey: "AccountsAPI"
    },
    extended: {
      portal2: "https://portal-v2.lifebeacon.com",
      features: {
        customers: true,
        dealers: true
      }
    }
  }
});
AccountConfig.extend({
  dependencies: {
    googleMapsKey: "AIzaSyBk67alAd9TWqehZxRSYXQzv2S_71olvdo",
    googleLibraries: ["places"]
  }
});
const reactive = new ReactiveCore("account");
const toDealerPayload = (data, id) => {
  const { homeAddress, physicalAddress, shippingAddress, ...dealer } = data ?? {};
  return {
    dealer: id === void 0 ? dealer : { ...dealer, id: Number(id) },
    physicalAddress: physicalAddress ?? homeAddress ?? null,
    shippingAddress: shippingAddress ?? null
  };
};
const unwrapDealer = (response) => response?.dealer ?? response;
class DealersServiceClient {
  #dealers;
  constructor(baseUrl) {
    this.#dealers = new DealersClient(baseUrl);
  }
  getDealers(metadata) {
    return this.#dealers.queryMql(metadata);
  }
  /**
   * Every dealer, for a lookup. Its own action, apart from the grid's `getDealers`: the grid's
   * calls replace one another (latest wins), and its filtered pages must not replace this list.
   */
  getAllDealers() {
    return this.#dealers.queryMql();
  }
  getDealer(id) {
    return this.#dealers.getById(Number(id));
  }
  createDealer(dealer) {
    return this.#dealers.createDealer(toDealerPayload(dealer)).then(unwrapDealer);
  }
  updateDealer(id, dealer) {
    return this.#dealers.updateDealer(Number(id), toDealerPayload(dealer, id)).then(unwrapDealer);
  }
  deleteDealer(id) {
    return this.#dealers.deleteDealer(Number(id));
  }
  /**
   * The API exposes no query route for dealer users, only the plain list, so the request-broker
   * `metadata` cannot be pushed to the server. The list is shaped like an MQL result to keep the
   * reactor and the request-broker count working; sorting still happens locally through
   * `db.withRequestBroker`, but filtering and paging are not applied server-side.
   */
  getDealerUsers(id) {
    return this.#dealers.getUsers(Number(id)).then((values) => ({
      values: values ?? [],
      totalCount: values?.length ?? 0
    }));
  }
  getDealerUser(id, userId) {
    return this.#dealers.getUser(Number(id), userId);
  }
  createDealerUser(id, user) {
    return this.#dealers.createUser(Number(id), user);
  }
  updateDealerUser(id, userId, user) {
    return this.#dealers.updateUser(Number(id), userId, user);
  }
  deleteDealerUser(id, userId) {
    return this.#dealers.deleteUser(Number(id), userId);
  }
}
const DealersService = reactive.service(
  "dealers",
  DealersServiceClient
)((Service) => new Service(AccountConfig.current.api.host));
DealersService.getDealers.latest();
DealersService.reactor("dealersReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case DealersService.getDealers: {
        const { values, totalCount } = payload;
        RequestMetadata.setCount(source.action, totalCount);
        return replaceRows(db, db.dealers_page, values);
      }
      // The lookup's list has its own store too, so a filtered grid page cannot replace it.
      case DealersService.getAllDealers:
        return replaceRows(db, db.dealers_lookup, payload.values);
      case DealersService.getDealer:
        return db.dealers.put(payload);
      case DealersService.createDealer:
        appRuntime.services.alerts.success(`Dealer added successfully`);
        return Promise.all([
          db.dealers.add(payload),
          db.dealers_page.put(payload),
          db.dealers_lookup.count().then((count) => count && db.dealers_lookup.put(payload))
        ]);
      case DealersService.updateDealer:
        appRuntime.services.alerts.success(`Dealer updated successfully`);
        return Promise.all([
          db.dealers.put(payload),
          db.dealers_page.update(payload.id, payload),
          db.dealers_lookup.update(payload.id, payload)
        ]);
      case DealersService.deleteDealer:
        appRuntime.services.alerts.success(`Dealer deleted successfully`);
        return Promise.all([
          db.dealers.delete(args[0]),
          db.dealers_page.delete(args[0]),
          db.dealers_lookup.delete(args[0])
        ]);
      case DealersService.getDealerUsers: {
        const { values, totalCount } = payload;
        RequestMetadata.setCount(`${source.action}-${args[0]}`, totalCount);
        return replaceRows(db, db.dealer_users, values);
      }
      case DealersService.getDealerUser:
        return db.dealer_users.put(payload);
      case DealersService.createDealerUser:
        appRuntime.services.alerts.success(`Dealer user added successfully`);
        return db.dealer_users.add(payload);
      case DealersService.updateDealerUser:
        appRuntime.services.alerts.success(`Dealer user updated successfully`);
        return db.dealer_users.put(payload);
      case DealersService.deleteDealerUser:
        appRuntime.services.alerts.success(`Dealer user deleted successfully`);
        return db.dealer_users.delete(args[1]);
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const normalizeDealerData = (data) => {
  const emptyToNull = (v) => v === "" ? null : v;
  if (typeof data.parentId === "object" && data.parentId !== null) {
    data.parentId = data.parentId.id;
  }
  data.companyName = emptyToNull(data.companyName);
  data.mobilePhone = emptyToNull(data.mobilePhone);
  data.workPhone = emptyToNull(data.workPhone);
  data.workPhoneExt = emptyToNull(data.workPhoneExt);
  data.homePhone = emptyToNull(data.homePhone);
  data.timeZone = emptyToNull(data.timeZone);
  data.prefix = emptyToNull(data.prefix);
  data.suffix = emptyToNull(data.suffix);
  data.taxNumber = emptyToNull(data.taxNumber);
};
DealersService.createDealer.interceptor("pre", async (args) => {
  normalizeDealerData(args[0]);
});
DealersService.updateDealer.interceptor("pre", async (args) => {
  normalizeDealerData(args[1]);
});
class AccountNotesServiceClient {
  #notes;
  constructor(baseUrl) {
    this.#notes = new AccountNotesClient(baseUrl);
  }
  getNoteTypes() {
    return this.#notes.getTypes();
  }
  /**
   * The account is passed rather than read back out of the filters: the request-broker applies a
   * static filter in an effect, so a read that ran before it landed would ask the route for `NaN`.
   * The filter still scopes the query itself — this only names the route's account.
   */
  getAccountNotes(metadata, actionKey, accountId) {
    const id = Number(accountId ?? metadata?.filters?.get("accountId")?.value);
    return this.#notes.queryMql(id, metadata);
  }
  getAccountNote(id) {
    return this.#notes.getById(id);
  }
  createAccountNote(note) {
    return this.#notes.createNote(note);
  }
  updateAccountNote(id, note) {
    return this.#notes.updateNote(id, note);
  }
  deleteAccountNote(id) {
    return this.#notes.deleteNote(id);
  }
}
const AccountNotesService = reactive.service(
  "accountNotes",
  AccountNotesServiceClient
)((Service) => new Service(AccountConfig.current.api.host));
AccountNotesService.getAccountNotes.latest(([, actionKey]) => actionKey);
AccountNotesService.reactor("accountNotesReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case AccountNotesService.getAccountNotes: {
        const { values, totalCount } = payload;
        const [metadata, actionKey, routeAccountId] = args;
        const accountId = Number(routeAccountId ?? metadata.filters.get("accountId")?.value);
        RequestMetadata.setCount(actionKey || `${source.action}-${accountId}`, totalCount);
        return replaceRows(db, db.account_notes, values, {
          where: (notes) => notes.where("accountId").equals(accountId)
        });
      }
      case AccountNotesService.getAccountNote:
        return db.account_notes.put(payload);
      case AccountNotesService.createAccountNote:
        appRuntime.services.alerts.success(`Account note added successfully`);
        return db.account_notes.add(payload);
      case AccountNotesService.updateAccountNote:
        appRuntime.services.alerts.success(`Account note updated successfully`);
        return db.account_notes.put(payload);
      case AccountNotesService.deleteAccountNote:
        appRuntime.services.alerts.success(`Account note deleted successfully`);
        return db.account_notes.delete(args[0]);
    }
  },
  error: (response, services) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const isMissingCustomer = (error) => error?.statusCode === 404 || error?.statusCode === 403;
class CustomersServiceClient {
  #customers;
  #addresses;
  constructor(baseUrl) {
    this.#customers = new CustomersClient(baseUrl);
    this.#addresses = new AccountAddressesClient(baseUrl);
  }
  queryCustomers(metadata) {
    return this.#customers.queryMql(metadata);
  }
  getCustomer(id) {
    return this.#customers.getById(Number(id));
  }
  /**
   * `getCustomer` for a caller that only wants a customer if there is one: an account that is not a
   * customer (404) or not visible to the user (403) resolves to null instead of failing, so no
   * error alert shows. The contact pickers use it with any account id they are handed.
   */
  findCustomer(id) {
    return this.#customers.getById(Number(id)).catch((error) => {
      if (isMissingCustomer(error)) return null;
      throw error;
    });
  }
  getCustomerAddresses(id, type) {
    return this.#addresses.getByAccount(Number(id), type ?? void 0);
  }
  /**
   * The API replaces rather than patches, so the current customer is read first and the caller's
   * changes spread over it. Read through the raw client, not this service's own action, so it does
   * not dispatch a `getCustomer` every consumer of that action would then react to.
   */
  updateCustomer(id, changes, { physicalAddress, shippingAddress } = {}) {
    return this.#customers.getById(Number(id)).then(
      (current) => this.#customers.updateCustomer(Number(id), {
        customer: { ...current, ...changes },
        physicalAddress,
        shippingAddress
      })
    );
  }
  deleteCustomer(id) {
    return this.#customers.deleteCustomer(Number(id));
  }
}
const CustomersService = reactive.service(
  "customers",
  CustomersServiceClient
)((Service) => new Service(AccountConfig.current.api.host));
CustomersService.queryCustomers.latest();
CustomersService.reactor("customersReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case CustomersService.queryCustomers: {
        const { values, totalCount } = payload;
        RequestMetadata.setCount(source.action, totalCount);
        return replaceRows(db, db.customers_page, values);
      }
      case CustomersService.getCustomer:
        return db.customers.put(payload);
      case CustomersService.findCustomer:
        return payload ? db.customers.put(payload) : void 0;
      case CustomersService.getCustomerAddresses: {
        const [id, type] = args;
        const accountId = Number(id);
        return replaceRows(db, db.addresses, payload, {
          where: (addresses) => type == null ? addresses.where("accountId").equals(accountId) : addresses.where("[accountId+type]").equals([accountId, Number(type)])
        });
      }
      case CustomersService.updateCustomer: {
        const changes = { ...args[1], ...payload.customer };
        const writes = [
          db.customers.update(args[0], changes),
          // Only changes a row the grid's page holds; adds none.
          db.customers_page.update(args[0], changes)
        ];
        if (payload.physicalAddress) writes.push(db.addresses.put(payload.physicalAddress));
        if (payload.shippingAddress) writes.push(db.addresses.put(payload.shippingAddress));
        return Promise.all(writes);
      }
      case CustomersService.deleteCustomer:
        return Promise.all([db.customers.delete(args[0]), db.customers_page.delete(args[0])]);
    }
  },
  error: (response) => {
    if (response.source === CustomersService.getCustomer && isMissingCustomer(response.payload)) {
      return;
    }
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
class EmergencyContactsServiceClient {
  #customers;
  constructor(baseUrl) {
    this.#customers = new CustomersClient(baseUrl);
  }
  getContacts(customerId) {
    return this.#customers.getContacts(Number(customerId));
  }
  createContact(contact) {
    return this.#customers.createContact(Number(contact.customerId), contact);
  }
  updateContact(id, contact) {
    return this.#customers.updateContact(Number(contact.customerId), id, contact);
  }
  deleteContact(id, customerId) {
    return this.#customers.deleteContact(Number(customerId), id);
  }
}
const EmergencyContactsService = reactive.service(
  "emergencyContacts",
  EmergencyContactsServiceClient
)((Service) => new Service(AccountConfig.current.api.host));
EmergencyContactsService.reactor("emergencyContactsReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case EmergencyContactsService.getContacts: {
        const customerId = Number(args[0]);
        return replaceRows(db, db.emergency_contacts, payload, {
          where: (contacts) => contacts.where("customerId").equals(customerId)
        });
      }
      case EmergencyContactsService.createContact:
        appRuntime.services.alerts.success("Contact added successfully");
        return db.emergency_contacts.put(payload);
      case EmergencyContactsService.updateContact:
        appRuntime.services.alerts.success("Contact updated successfully");
        return db.emergency_contacts.put(payload);
      case EmergencyContactsService.deleteContact:
        appRuntime.services.alerts.success("Contact deleted successfully");
        return db.emergency_contacts.delete(args[0]);
    }
  },
  error: (response) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const UPDATABLE = [
  "name",
  "expirationMonth",
  "expirationYear",
  "firstName",
  "lastName",
  "phoneNumber",
  "email",
  "isFrozen"
];
const assign = async (profiles, plans, accountId, profileId, { subscription, paymentPlans }) => {
  const result = { subscription, movedPlans: 0, errors: [] };
  if (subscription) {
    try {
      await profiles.setDefault(Number(accountId), profileId);
    } catch (error) {
      result.subscription = false;
      result.errors.push(`The subscription was not assigned: ${error?.message ?? "error"}`);
    }
  }
  if (paymentPlans) {
    try {
      const moved = await plans.moveOpenPlansToProfile({
        customerId: Number(accountId),
        paymentProfileId: profileId
      });
      result.movedPlans = moved?.length ?? 0;
    } catch (error) {
      result.errors.push(`The payment plans were not moved: ${error?.message ?? "error"}`);
    }
  }
  return result;
};
class PaymentProfilesServiceClient {
  #profiles;
  #plans;
  constructor(baseUrl) {
    this.#profiles = new AccountPaymentProfilesClient(baseUrl);
    this.#plans = new CustomerPaymentPlansClient(baseUrl);
  }
  /** Splits the form's assignment boxes off the profile fields. */
  #assignments({ assignToSubscription, assignToPaymentPlans, ...values }) {
    return [values, { subscription: !!assignToSubscription, paymentPlans: !!assignToPaymentPlans }];
  }
  async #saveAndAssign(accountId, save, assignments) {
    const payload = await save();
    if (!assignments.subscription && !assignments.paymentPlans) return payload;
    const assigned = await assign(
      this.#profiles,
      this.#plans,
      accountId,
      payload.profile.id,
      assignments
    );
    return { ...payload, assigned };
  }
  getProfiles(accountId) {
    return this.#profiles.getByAccount(Number(accountId));
  }
  /**
   * `values` is the form output: the profile fields plus a `billingAddress` object, and the
   * `assignToSubscription` / `assignToPaymentPlans` boxes, which are acted on once the profile is
   * saved (see `assign`).
   */
  createProfile(accountId, values) {
    const [{ billingAddress, ...profile }, assignments] = this.#assignments(values);
    return this.#saveAndAssign(
      accountId,
      () => this.#profiles.createProfile({
        profile: { ...profile, accountId: Number(accountId) },
        billingAddress: billingAddress ?? {}
      }),
      assignments
    );
  }
  /**
   * `current` is the stored profile (with its `billingAddress` attached); `values` the form output.
   * The PUT replaces the address, so the stored one is merged under the edited fields.
   */
  updateProfile(accountId, current, values) {
    const [{ billingAddress, ...changes }, assignments] = this.#assignments(values);
    return this.#saveAndAssign(
      accountId,
      () => this.#profiles.updateProfile(current.id, {
        ...objectPick(changes, UPDATABLE),
        id: current.id,
        billingAddress: { ...current.billingAddress, ...billingAddress }
      }),
      assignments
    );
  }
  deleteProfile(accountId, profileId) {
    return this.#profiles.deleteProfile(profileId);
  }
  setDefaultProfile(accountId, profileId) {
    return this.#profiles.setDefault(Number(accountId), profileId);
  }
}
const PaymentProfilesService = reactive.service(
  "paymentProfiles",
  PaymentProfilesServiceClient
)((Service) => new Service(AccountConfig.current.api.host));
const storeEnvelope = (db, payload) => Promise.all([
  db.payment_profiles.put(payload.profile),
  ...payload.billingAddress?.id ? [db.addresses.put(payload.billingAddress)] : []
]);
const reportAssignments = (services, accountId, assigned) => {
  if (!assigned) return;
  const { alerts: alerts2 } = appRuntime.services;
  if (assigned.subscription) alerts2.success("Subscription now renews against this profile");
  if (assigned.movedPlans > 0)
    alerts2.success(
      `${assigned.movedPlans} payment ${assigned.movedPlans === 1 ? "plan" : "plans"} moved to this profile`
    );
  assigned.errors.forEach((message) => alerts2.error(message));
  if (assigned.subscription || assigned.movedPlans > 0)
    services.paymentProfiles.getProfiles(Number(accountId));
};
PaymentProfilesService.reactor("paymentProfilesReactor", {
  success: (response, services, db) => {
    const { source, args, payload } = response;
    switch (source) {
      case PaymentProfilesService.getProfiles: {
        const accountId = Number(args[0]);
        return replaceRows(db, db.payment_profiles, payload, {
          where: (profiles) => profiles.where("accountId").equals(accountId)
        });
      }
      case PaymentProfilesService.createProfile:
        appRuntime.services.alerts.success("Payment profile added");
        reportAssignments(services, args[0], payload.assigned);
        return storeEnvelope(db, payload);
      case PaymentProfilesService.updateProfile:
        appRuntime.services.alerts.success("Payment profile updated");
        reportAssignments(services, args[0], payload.assigned);
        return storeEnvelope(db, payload);
      case PaymentProfilesService.deleteProfile:
        appRuntime.services.alerts.success("Payment profile deleted");
        return db.payment_profiles.delete(args[1]);
    }
  },
  error: (response) => {
    appRuntime.services.alerts.error(response.payload?.message ?? "Error");
  }
});
const storeFormat = (store) => Object.entries(store).reduce((result, [key, value]) => {
  result[key] = value.join(",");
  return result;
}, {});
const ReactiveConfig = {
  db: {
    name: "livefree.accounts.db",
    provider: new DexieDatabaseProvider([dexieRequestBrokerAddon]),
    init: (db) => {
      db.version(7).stores(storeFormat(_store));
      db.version(8).stores({ device_activity: null });
      db.version(9).stores({ addresses: "&id, accountId, [accountId+type]" });
      db.version(10).stores({
        ledger: null,
        ledger_entries: "++id, customerId",
        ledger_summary: "&customerId"
      });
      db.version(11).stores({ rmr_roll_forward_report: null });
      db.version(12).stores({ customer_devices: "&id, customerId" });
      db.version(13).stores({ emergency_contacts: "&id, customerId" });
      db.version(14).stores({
        profiles: null,
        payment_profiles: "&id, accountId"
      });
      db.version(15).stores({ ledger_entries: null, ledger_summary: null });
      db.version(16).stores({ customer_devices: null });
      db.version(17).stores({ customers_page: "&id, dealerId", dealers_page: "&id" });
      db.version(18).stores({ dealers_lookup: "&id" });
    }
  }
};
const _store = {
  dealers: ["&id"],
  dealer_users: ["&id", "parentId"],
  account_notes: ["&id", "accountId"],
  rmr_roll_forward_report: ["&dealerId"],
  customers: ["&id", "dealerId"],
  ledger: ["&id", "customerId"],
  payment_profiles: ["&id", "customerId"],
  profiles: ["&id", "referenceId"]
};
const render = reactive.configure(ReactiveConfig).render;
const Shell = ({ children }) => /* @__PURE__ */ jsx("div", { className: "p-4 h-full flex flex-col", children });
let Bootstrapped;
const Layout = ({ children }) => {
  Bootstrapped ??= App.bootstrap(Shell, { runtime: appRuntime });
  return /* @__PURE__ */ jsx(Bootstrapped, { children });
};
const BaseLayout = render((props) => /* @__PURE__ */ jsx(Layout, { ...props }));
const activeDealerTab = (pathname) => pathname.split("/").reverse().find((segment) => ["details", "addresses", "users", "notes"].includes(segment)) ?? "details";
function DealerDetailsNavigationTabs({ active, onSelect }) {
  return /* @__PURE__ */ jsx(
    Tabs,
    {
      variant: "secondary",
      className: "bg-[var(--surface)]",
      selectedKey: active,
      onSelectionChange: (key) => onSelect(String(key)),
      children: /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsxs(
        Tabs.List,
        {
          "aria-label": "Dealer sections",
          className: "px-5 **:data-[slot=tabs-tab]:h-10 **:data-[slot=tabs-tab]:w-auto",
          children: [
            /* @__PURE__ */ jsxs(Tabs.Tab, { id: "details", children: [
              "Details",
              /* @__PURE__ */ jsx(Tabs.Indicator, {})
            ] }, "details"),
            /* @__PURE__ */ jsxs(Tabs.Tab, { id: "addresses", children: [
              "Addresses",
              /* @__PURE__ */ jsx(Tabs.Indicator, {})
            ] }, "addresses"),
            /* @__PURE__ */ jsx(RequirePolicy, { resource: "DealerUsers", operation: ["View"], children: /* @__PURE__ */ jsxs(Tabs.Tab, { id: "users", children: [
              "Users",
              /* @__PURE__ */ jsx(Tabs.Indicator, {})
            ] }, "users") }),
            /* @__PURE__ */ jsxs(Tabs.Tab, { id: "notes", children: [
              "Notes",
              /* @__PURE__ */ jsx(Tabs.Indicator, {})
            ] }, "notes")
          ]
        }
      ) })
    }
  );
}
function ContactChip({ icon, label }) {
  if (!label) return null;
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5 text-sm text-[var(--muted)]", children: [
    /* @__PURE__ */ jsx("i", { className: `${icon} text-xs text-[var(--fg-3)]` }),
    label
  ] });
}
const AVATAR_PALETTE = ["#0868D8", "#1F9D55", "#1B7FC4", "#5F6B79", "#E08600", "#A82420"];
const colorFor = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = h * 31 + name.charCodeAt(i) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};
const initialsFor = (name) => (name || "?").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
function AccountAvatar({ name = "?", size = 34 }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: "inline-flex flex-none items-center justify-center rounded-full font-semibold text-white",
      style: {
        width: size,
        height: size,
        background: colorFor(name),
        fontSize: Math.round(size * 0.38)
      },
      children: initialsFor(name)
    }
  );
}
const AccountName = ({ name, id }) => /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
  /* @__PURE__ */ jsx("div", { className: "flex flex-wrap items-center gap-1.5", children: /* @__PURE__ */ jsx("p", { className: "text-[13px] font-medium text-[var(--foreground)]", children: name }) }),
  /* @__PURE__ */ jsxs("div", { className: "flex gap-2 items-center", children: [
    /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-[11px] text-[var(--fg-3)] font-mono truncate", children: id }),
    /* @__PURE__ */ jsx(CopyToClipboard, { text: id })
  ] })
] });
const AddressDisplay = ({ address }) => {
  if (!address) return null;
  const cityLine = address.city || address.state || address.postalCode ? `${address.city ?? ""}${address.city ? ", " : ""}${address.state ?? ""} ${address.postalCode ?? ""}`.trim() : null;
  const lines = [
    address.address1,
    address.address2,
    address.address3,
    cityLine,
    address.country
  ].filter((line) => line && line.trim() !== "");
  return /* @__PURE__ */ jsx("ul", { className: "list-none mx-2", children: lines.map((line, index) => /* @__PURE__ */ jsx("li", { children: line }, index)) });
};
const isValidAddress = (form, importedAddress) => {
  const { isValid } = form;
  if (isValid) return true;
  if (!importedAddress) return isValid;
  const { address1, city, state, postalCode, country } = importedAddress;
  return !!address1 && address1 !== "" && !!city && city !== "" && !!state && state !== "" && !!postalCode && postalCode !== "" && !!country && country !== "";
};
const AddressForm = React.memo(
  ({
    address,
    name,
    addressTypeLabel = "Address",
    isLoading = false,
    readOnly = false,
    isRequired = false,
    onChange
  }) => {
    const [_address, setAddress] = useState(address);
    const [form, setForm] = useState({
      data: address,
      isValid: false,
      errors: []
    });
    const [selectedTab, setSelectedTab] = useState(
      !readOnly ? /* @__PURE__ */ new Set(["edit_address"]) : /* @__PURE__ */ new Set()
    );
    const handleChange = useCallback(
      (form2) => {
        setForm(form2);
        onChange(form2);
      },
      [onChange]
    );
    useEffect(() => {
      if (address) {
        setAddress(address);
      }
    }, [address]);
    useEffect(() => {
      if (_address)
        setForm((prev) => {
          const result = {
            ...prev,
            data: _address,
            isValid: isValidAddress(prev, _address)
          };
          onChange && onChange(result);
          return result;
        });
    }, [_address, onChange]);
    return /* @__PURE__ */ jsxs("div", { style: { marginBottom: form ? 0 : 120 }, children: [
      /* @__PURE__ */ jsx("div", { className: "w-full mb-4", children: /* @__PURE__ */ jsx(
        GoogleAutocomplete,
        {
          startContent: /* @__PURE__ */ jsx("i", { className: "text-lg text-gray-400 ri-map-pin-line" }),
          placeholder: "Enter address",
          onSelect: (addr) => {
            setAddress({
              address1: addr?.address1,
              address2: addr?.address2,
              address3: addr?.address3,
              city: addr?.city,
              state: addr?.state,
              postalCode: addr?.postalCode,
              country: addr?.country
            });
          },
          variant: "faded"
        }
      ) }),
      /* @__PURE__ */ jsx("div", { className: "w-full mb-2", children: isLoading ? /* @__PURE__ */ jsx(LoadingStatus, { isLoading }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: "w-full mb-4", children: /* @__PURE__ */ jsx(AddressDisplay, { address: form?.data }) }),
        !readOnly && /* @__PURE__ */ jsx(Accordion, { expandedKeys: selectedTab, onExpandedChange: setSelectedTab, children: /* @__PURE__ */ jsxs(Accordion.Item, { id: "edit_address", "aria-label": "Modify Address", children: [
          /* @__PURE__ */ jsx(Accordion.Heading, { children: /* @__PURE__ */ jsxs(Accordion.Trigger, { children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center w-full", children: [
              /* @__PURE__ */ jsx("i", { className: "ri-pencil-fill mr-2" }),
              /* @__PURE__ */ jsx("span", { className: "mr-2", children: "Modify Address" }),
              /* @__PURE__ */ jsx("div", { className: "ml-auto mr-3", children: /* @__PURE__ */ jsx(AddressStatusChip, { isValid: form.isValid, isRequired }) })
            ] }),
            /* @__PURE__ */ jsx(Accordion.Indicator, {})
          ] }) }),
          /* @__PURE__ */ jsx(Accordion.Panel, { children: /* @__PURE__ */ jsx(Accordion.Body, { children: /* @__PURE__ */ jsx(
            ModifyAddressForm,
            {
              addressTypeLabel,
              name,
              address: _address || {},
              isReadOnly: readOnly,
              onChange: handleChange
            }
          ) }) })
        ] }) })
      ] }) })
    ] });
  },
  (prevProps, nextProps) => {
    return deepEquals(prevProps?.address, nextProps?.address) && prevProps?.readOnly === nextProps?.readOnly && prevProps?.isLoading === nextProps?.isLoading;
  }
);
const AddressStatusChip = ({ isRequired, isValid }) => {
  const color = isValid ? "success" : isRequired ? "danger" : "default";
  return /* @__PURE__ */ jsxs(Chip, { color, variant: "tertiary", className: "float-right", children: [
    /* @__PURE__ */ jsx("i", { className: isValid ? "ri-check-line" : "ri-close-line" }),
    isValid ? "Address Validated" : "Address Incomplete"
  ] });
};
const AccountNoteType = {
  Complaint: 1,
  TechSupport: 2,
  Billing: 3,
  Activation: 5,
  Information: 6,
  General: 7,
  Shipping: 8,
  PurchaseReason: 9,
  SavedAccount: 10,
  Concierge: 11,
  RemotePatientMonitoring: 12
};
const AccountNoteTypeDisplay = {
  [AccountNoteType.Complaint]: "Complaint",
  [AccountNoteType.TechSupport]: "Tech Support",
  [AccountNoteType.Billing]: "Billing",
  [AccountNoteType.Activation]: "Activation",
  [AccountNoteType.Information]: "Information",
  [AccountNoteType.General]: "General",
  [AccountNoteType.Shipping]: "Shipping",
  [AccountNoteType.PurchaseReason]: "Purchase Reason",
  [AccountNoteType.SavedAccount]: "Saved Account",
  [AccountNoteType.Concierge]: "Concierge",
  [AccountNoteType.RemotePatientMonitoring]: "Remote Patient Monitoring"
};
const AccountNoteTypeOptions = Object.entries(AccountNoteTypeDisplay).map(
  ([value, label]) => ({
    value,
    label
  })
);
const AccountType = {
  Dealer: 1,
  Customer: 2,
  EmergencyContact: 3,
  DealerUser: 4,
  BillingParty: 5
};
const AccountTypeDisplay = {
  [AccountType.Dealer]: "Dealer",
  [AccountType.Customer]: "Customer",
  [AccountType.EmergencyContact]: "Emergency Contact",
  [AccountType.DealerUser]: "Dealer User",
  [AccountType.BillingParty]: "Billing Party"
};
const AddressType = {
  Physical: 1,
  Shipping: 2,
  Billing: 3,
  Home: 1
};
const AddressTypeDisplay = {
  [AddressType.Physical]: "Physical",
  [AddressType.Shipping]: "Shipping",
  [AddressType.Billing]: "Billing"
};
const RelationshipTypeOptions = [
  { id: 1, name: "Unknown" },
  { id: 2, name: "Aunt" },
  { id: 3, name: "Brother-In-Law" },
  { id: 4, name: "Building Manager" },
  { id: 5, name: "Brother" },
  { id: 6, name: "Clergy" },
  { id: 7, name: "Cousin" },
  { id: 8, name: "Caretaker" },
  { id: 9, name: "Daughter" },
  { id: 10, name: "Daughter-In-Law" },
  { id: 11, name: "Doctor" },
  { id: 12, name: "Employee" },
  { id: 13, name: "Ex-Husband" },
  { id: 14, name: "Ex-Wife" },
  { id: 15, name: "Father" },
  { id: 16, name: "Front Desk" },
  { id: 17, name: "Friend" },
  { id: 18, name: "Grandson" },
  { id: 19, name: "Granddaughter" },
  { id: 20, name: "Husband" },
  { id: 21, name: "Hospice" },
  { id: 22, name: "Lawyer" },
  { id: 23, name: "Mother" },
  { id: 24, name: "Cleaning Service" },
  { id: 25, name: "Manager" },
  { id: 26, name: "Neighbor" },
  { id: 27, name: "Niece" },
  { id: 28, name: "Nephew" },
  { id: 29, name: "Owner" },
  { id: 30, name: "Parents" },
  { id: 31, name: "Power of Attorney" },
  { id: 32, name: "Partner or Significant Other" },
  { id: 33, name: "Runner" },
  { id: 34, name: "Son" },
  { id: 35, name: "Service Tech" },
  { id: 36, name: "Son-In-Law" },
  { id: 37, name: "Sister-In-Law" },
  { id: 38, name: "Sister" },
  { id: 39, name: "Step-Daughter" },
  { id: 40, name: "Step-Son" },
  { id: 41, name: "Uncle" },
  { id: 42, name: "Wife" }
];
const PaymentProfileType = {
  CreditCard: 1,
  Ach: 2,
  ManualInvoice: 8
};
const PaymentProfileTypeDisplay = {
  [PaymentProfileType.CreditCard]: "Credit card",
  [PaymentProfileType.Ach]: "ACH",
  [PaymentProfileType.ManualInvoice]: "Manual invoice"
};
const PaymentProfileTypeIcon = {
  [PaymentProfileType.CreditCard]: "ri-bank-card-line",
  [PaymentProfileType.Ach]: "ri-building-2-line",
  [PaymentProfileType.ManualInvoice]: "ri-file-text-line"
};
const PaymentProfileTypeOptions = Object.entries(PaymentProfileTypeDisplay).map(
  ([value, label]) => ({
    value: Number(value),
    label,
    icon: PaymentProfileTypeIcon[value]
  })
);
const DefaultPaymentProfileType = PaymentProfileType.CreditCard;
const compareAddresses = (home, shipping) => {
  if (!home && !shipping) return true;
  const homeAddress = { ...home };
  const shippingAddress = { ...shipping };
  if (!home || !shipping) return false;
  delete homeAddress.id;
  delete homeAddress.type;
  delete shippingAddress.id;
  delete shippingAddress.type;
  return deepEquals(homeAddress, shippingAddress);
};
const contactFormReducer = (state, action) => {
  switch (action.type) {
    case "updateHomeAddress": {
      const shippingAddress = state.shippingSameAsHome ? { ...action.value } : { ...state.shippingAddress };
      return {
        ...state,
        homeAddress: { ...action.value, type: AddressType.Home },
        shippingAddress: {
          ...shippingAddress,
          type: AddressType.Shipping
        }
      };
    }
    case "updateShippingAddress":
      return {
        ...state,
        shippingAddress: { ...action.value, type: AddressType.Shipping }
      };
    case "updateShippingSameAsHome":
      return {
        ...state,
        shippingSameAsHome: action.value,
        shippingAddress: action.value ? state.homeAddress : state.shippingAddress
      };
  }
};
const ContactForm = ({ contact = {}, isLoading = false, sameAsHome = false, onChange }) => {
  const [form, dispatch] = useReducer(contactFormReducer, {
    homeAddress: contact?.homeAddress || { isValid: false, errors: [] },
    shippingAddress: contact?.shippingAddress || { isValid: false, errors: [] },
    shippingSameAsHome: sameAsHome || compareAddresses(contact?.homeAddress, contact?.shippingAddress)
  });
  const handleChange = useCallback(
    (form2) => {
      onChange(form2);
    },
    [onChange]
  );
  useEffect(() => {
    handleChange(form);
  }, [form]);
  useEffect(() => {
    if (form.shippingSameAsHome) {
      dispatch({
        type: "updateShippingAddress",
        value: { ...form.homeAddress, type: AddressType.Shipping }
      });
    }
  }, [form.homeAddress, form.shippingSameAsHome]);
  return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs("div", { className: "flex flex-nowrap flex-row gap-2", children: [
    /* @__PURE__ */ jsx("div", { className: "w-full lg:w-1/2", children: /* @__PURE__ */ jsxs(Card, { children: [
      /* @__PURE__ */ jsx(Card.Header, { children: "Primary Address" }),
      /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(
        AddressForm,
        {
          name: "homeAddress",
          addressTypeLabel: "Home Address",
          isLoading,
          address: contact?.homeAddress,
          onChange: ({ data, isValid, errors }) => dispatch({
            type: "updateHomeAddress",
            value: { ...data, isValid, errors }
          })
        }
      ) })
    ] }) }),
    /* @__PURE__ */ jsx("div", { className: "w-full lg:w-1/2", children: /* @__PURE__ */ jsxs(Card, { children: [
      /* @__PURE__ */ jsxs(Card.Header, { className: "flex justify-between items-center", children: [
        /* @__PURE__ */ jsx("span", { children: "Shipping Address" }),
        /* @__PURE__ */ jsxs(
          Switch,
          {
            size: "sm",
            isSelected: form.shippingSameAsHome,
            onChange: (checked) => dispatch({
              type: "updateShippingSameAsHome",
              value: checked
            }),
            children: [
              /* @__PURE__ */ jsx(Switch.Control, { children: /* @__PURE__ */ jsx(Switch.Thumb, {}) }),
              /* @__PURE__ */ jsx(Switch.Content, { children: /* @__PURE__ */ jsx(Label, { children: "Same as Home" }) })
            ]
          }
        )
      ] }),
      /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(
        AddressForm,
        {
          name: "shippingAddress",
          addressTypeLabel: "Shipping Address",
          isLoading,
          readOnly: form.shippingSameAsHome,
          address: form.shippingSameAsHome ? form?.homeAddress : contact?.shippingAddress,
          onChange: ({ data, isValid, errors }) => dispatch({
            type: "updateShippingAddress",
            value: { ...data, isValid, errors }
          })
        }
      ) })
    ] }) })
  ] }) });
};
const formatPhoneWithExt = (phone, ext) => phone && ext ? `${formatPhone(phone)} ext. ${ext}` : formatPhone(phone);
const InactiveChip = ({ isActive, children = "Inactive" }) => isActive === false ? /* @__PURE__ */ jsx(Chip, { size: "sm", color: "warning", variant: "soft", children }) : null;
function LoadingStatus({ noDelay, noText, text = "This May Take A Moment" }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    let id;
    if (!noDelay) {
      id = setTimeout(() => {
        setSlow(true);
      }, 5e3);
    }
    return () => !noDelay && clearTimeout(id);
  }, [noDelay]);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col justify-center items-center py-4 gap-3 text-muted", children: [
    /* @__PURE__ */ jsx(Spinner, { color: "current", size: "lg" }),
    !noText && (!noDelay ? slow && text : text && text)
  ] });
}
const ModifyAddressForm = ({
  address = {},
  name,
  addressTypeLabel,
  isReadOnly = false,
  onChange
}) => {
  return /* @__PURE__ */ jsxs(Uniformly, { className: "flex flex-col gap-2", name, onChange, children: [
    /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
      TextField,
      {
        name: "address1",
        label: "Address 1",
        value: address.address1,
        isReadOnly,
        validation: (x) => x.string().max(100).required(`[${addressTypeLabel}] Address 1 is required`)
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { important: true, children: /* @__PURE__ */ jsx(
      TextField,
      {
        name: "address2",
        label: "Address 2",
        value: address.address2,
        isReadOnly,
        validation: (x) => x.string().max(100).notRequired()
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { important: true, children: /* @__PURE__ */ jsx(
      TextField,
      {
        name: "address3",
        label: "Address 3",
        value: address.address3,
        isReadOnly,
        validation: (x) => x.string().max(100).notRequired()
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
      TextField,
      {
        name: "city",
        label: "City",
        value: address.city,
        isReadOnly,
        validation: (x) => x.string().max(100).required(`[${addressTypeLabel}] City is required`)
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
      TextField,
      {
        name: "state",
        label: "State",
        value: address.state,
        isReadOnly,
        validation: (x) => x.string().max(100).required(`[${addressTypeLabel}] State is required`)
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
      TextField,
      {
        name: "postalCode",
        label: "Postal Code",
        value: address.postalCode,
        isReadOnly,
        validation: (x) => x.string().max(100).required(`[${addressTypeLabel}] Postal Code is required`)
      }
    ) }),
    /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
      TextField,
      {
        name: "country",
        label: "Country",
        value: address.country,
        isReadOnly,
        validation: (x) => x.string().max(100).required(`[${addressTypeLabel}] Country is required`)
      }
    ) })
  ] });
};
const DealersListLoader = reactive.component(
  ({ services, data, monitors, db, children }) => {
    const { dealers: dealers2 } = data;
    return children({
      dealers: dealers2,
      monitors: {
        getDealers: monitors.getDealers.executing
      },
      action: {
        getDealers: services.dealers.getDealers
      },
      skipInitial: () => db.dealers_page.toCollection().count()
    });
  },
  {
    name: "DealersListLoader",
    sync: (services, { withRequestBroker }) => [
      {
        execute: () => !withRequestBroker && services.dealers.getDealers(),
        bypass: (db) => db.dealers_page.toCollection().count(),
        deps: []
      }
    ],
    queries: ({ withRequestBroker }, services) => [
      {
        name: "dealers",
        execute: (db) => withRequestBroker ? db.withRequestBroker(services.dealers.getDealers.action, () => db.dealers_page, {
          sorting: true
        }) : db.dealers_page.toArray(),
        deps: []
      }
    ],
    monitors: (services) => [services.dealers.getDealers]
  }
);
const RoutePaths = () => {
  const baseUrl = appRuntime.baseUrl;
  const rootPaths = {
    dealers: baseUrl + "/dealers",
    customers: baseUrl + "/customers",
    notes: baseUrl + "/notes"
  };
  return {
    customers: {
      list: () => rootPaths.customers,
      details: (id) => `${rootPaths.customers}/${id}/overview`,
      tab: (id, tab) => `${rootPaths.customers}/${id}/${tab}`,
      overview: (id) => `${rootPaths.customers}/${id}/overview`,
      contactInfo: (id) => `${rootPaths.customers}/${id}/contact-info`,
      contacts: (id) => `${rootPaths.customers}/${id}/contacts`,
      paymentProfiles: (id) => `${rootPaths.customers}/${id}/payment-profiles`,
      deviceActivity: (id) => `${rootPaths.customers}/${id}/device-activity`,
      ledger: (id) => `${rootPaths.customers}/${id}/ledger`,
      paymentPlans: (id) => `${rootPaths.customers}/${id}/payment-plans`,
      linkedAccounts: (id) => `${rootPaths.customers}/${id}/linked-accounts`,
      notifications: (id) => `${rootPaths.customers}/${id}/notifications`
    },
    dealers: {
      list: () => rootPaths.dealers,
      create: () => `${rootPaths.dealers}/create`,
      tab: (id, tab) => `${rootPaths.dealers}/${id}/${tab}`,
      details: (id) => `${rootPaths.dealers}/${id}/details`,
      addresses: (id) => `${rootPaths.dealers}/${id}/addresses`,
      notes: (id) => `${rootPaths.dealers}/${id}/notes`,
      users: (id) => `${rootPaths.dealers}/${id}/users`,
      createUser: (id) => `${rootPaths.dealers}/${id}/users/create`,
      updateUser: (id, userId) => `${rootPaths.dealers}/${id}/users/${userId}`,
      subDealers: (id) => `${rootPaths.dealers}/${id}/subDealers`
    },
    notes: {
      list: () => rootPaths.notes,
      create: () => `${rootPaths.notes}/create`
    }
  };
};
const WithParams = ({ element: Component }) => {
  const params = useParams();
  return /* @__PURE__ */ jsx(Component, { ...params });
};
const getDealerLabel = ({ firstName, lastName, companyName } = {}) => {
  const personName = [firstName, lastName].filter(Boolean).join(" ").trim();
  return personName || companyName || "";
};
const DealerContext = createContext(null);
const DealerProvider = reactive.component(
  ({ data, children }) => {
    const { dealer } = data;
    useRecentView({
      title: dealer?.id ? getDealerLabel(dealer) : void 0,
      route: dealer?.id ? RoutePaths().dealers.details(dealer.id) : void 0
    });
    if (!dealer?.id) {
      return /* @__PURE__ */ jsx(Spinner, {});
    }
    return dealer.id ? /* @__PURE__ */ jsx(DealerContext.Provider, { value: dealer, children }) : null;
  },
  {
    name: "DealerProvider",
    sync: (services, { dealerId }) => [
      {
        execute: () => services.dealers.getDealer(dealerId),
        deps: [dealerId]
      }
    ],
    queries: ({ dealerId }) => [
      {
        name: "dealer",
        defaultValue: {},
        execute: (db) => db.dealers.get(dealerId),
        deps: [dealerId]
      }
    ]
  }
);
const useDealer = () => useContext(DealerContext);
const CustomerContext = createContext(null);
const NOT_FOUND_STATUS_CODES = [403, 404];
const isCustomerId = (id) => Number.isInteger(id) && id > 0;
const CustomerLoadFailed = ({ onRetry }) => /* @__PURE__ */ jsxs(EmptyState, { className: "py-10", children: [
  /* @__PURE__ */ jsxs(EmptyState.Header, { children: [
    /* @__PURE__ */ jsx(EmptyState.Media, { variant: "icon", children: /* @__PURE__ */ jsx("i", { className: "ri-error-warning-line" }) }),
    /* @__PURE__ */ jsx(EmptyState.Title, { children: "Could not load the customer" }),
    /* @__PURE__ */ jsx(EmptyState.Description, { children: "The request failed. Try again." })
  ] }),
  /* @__PURE__ */ jsx(EmptyState.Content, { children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "primary", onPress: onRetry, children: "Retry" }) })
] });
const useCustomerLoadFailure = (getCustomer, customerId) => {
  const [failure, setFailure] = useState(null);
  const handler = useMemo(
    () => ({
      error: ({ payload, args }) => {
        if (Number(args?.[0]) === customerId) {
          setFailure({ customerId, statusCode: payload?.statusCode });
        }
      },
      success: ({ args }) => {
        if (Number(args?.[0]) === customerId) setFailure(null);
      }
    }),
    [customerId]
  );
  useReactor(getCustomer, handler);
  const retry = () => {
    setFailure(null);
    getCustomer(customerId);
  };
  return [failure?.customerId === customerId ? failure : null, retry];
};
const CustomerProvider = reactive.component(
  ({ customerId, services, data, children }) => {
    const { customer } = data;
    const navigate = useNavigate();
    const id = Number(customerId);
    const [failure, retry] = useCustomerLoadFailure(services.customers.getCustomer, id);
    useRecentView({
      title: customer?.fullName,
      route: customer?.id ? RoutePaths().customers.overview(customer.id) : void 0
    });
    if (!isCustomerId(id) || NOT_FOUND_STATUS_CODES.includes(failure?.statusCode)) {
      return /* @__PURE__ */ jsx(NotFound, { resourceType: "customer", onBack: () => navigate(RoutePaths().customers.list()) });
    }
    if (failure) return /* @__PURE__ */ jsx(CustomerLoadFailed, { onRetry: retry });
    if (customer?.id) {
      return /* @__PURE__ */ jsx(CustomerContext.Provider, { value: customer, children });
    }
    return /* @__PURE__ */ jsx(Loading, {});
  },
  {
    name: "CustomerProvider",
    sync: (services, { customerId }) => [
      {
        execute: () => {
          const id = Number(customerId);
          if (isCustomerId(id)) services.customers.getCustomer(id);
        },
        deps: [customerId]
      }
    ],
    queries: ({ customerId }) => [
      {
        name: "customer",
        defaultValue: {},
        execute: (db) => db.customers.get(Number(customerId)),
        deps: [customerId]
      }
    ]
  }
);
const useCustomer = () => useContext(CustomerContext);
const withDealer = (Wrapped) => {
  const _Wrapped = (props) => {
    const dealer = useContext(DealerContext);
    if (!dealer && !dealer.id) {
      return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(Alert, { status: "danger", children: [
        /* @__PURE__ */ jsx(Alert.Indicator, {}),
        /* @__PURE__ */ jsxs(Alert.Content, { children: [
          /* @__PURE__ */ jsx(Alert.Title, { children: "Error" }),
          /* @__PURE__ */ jsxs(Alert.Description, { children: [
            "A component using ",
            /* @__PURE__ */ jsx("strong", { children: "withDealer" }),
            " must be wrapped with",
            /* @__PURE__ */ jsx("strong", { children: "DealerProvider" }),
            "."
          ] })
        ] })
      ] }) });
    }
    return /* @__PURE__ */ jsx(Wrapped, { ...props, dealer });
  };
  return _Wrapped;
};
const instrumentText = (profile) => {
  switch (profile.profileType) {
    case PaymentProfileType.CreditCard:
      return profile.cardNumberMasked;
    case PaymentProfileType.Ach:
      return profile.bankName;
    default:
      return null;
  }
};
const detailText = (profile) => [
  PaymentProfileTypeDisplay[profile.profileType],
  instrumentText(profile),
  profile.createdOnUtc && `Added ${parseAndFormatDate(profile.createdOnUtc)}`
].filter(Boolean).join(" · ");
const profileText = (profile) => [
  profile.name,
  profile.assignedToSubscription && "assigned to subscription",
  profile.isFrozen && "frozen",
  detailText(profile)
].filter(Boolean).join(", ");
const ProfileName = ({ profile }) => /* @__PURE__ */ jsxs("span", { className: "flex min-w-0 items-center gap-1.5", children: [
  /* @__PURE__ */ jsx("span", { className: "truncate", children: profile.name }),
  profile.assignedToSubscription && /* @__PURE__ */ jsx(
    "i",
    {
      className: "ri-checkbox-circle-fill text-success shrink-0",
      title: "Assigned to subscription",
      "aria-hidden": "true"
    }
  ),
  profile.isFrozen && /* @__PURE__ */ jsx("i", { className: "ri-snowflake-line shrink-0 text-blue-500", title: "Frozen", "aria-hidden": "true" })
] });
const PaymentProfilesSelectField$1 = reactive.component(
  ({ accountId, services, onProfilesLoaded, ...rest }) => {
    const [profiles, setProfiles] = useState([]);
    useSuccessReactor(services.paymentProfiles.getProfiles, (payload) => {
      const loaded = payload ?? [];
      setProfiles(loaded);
      onProfilesLoaded?.(loaded);
    });
    return /* @__PURE__ */ jsx(
      SelectField,
      {
        ...rest,
        type: "number",
        items: profiles,
        renderValue: (profile) => /* @__PURE__ */ jsx(ProfileName, { profile }),
        children: (profile) => /* @__PURE__ */ jsx(ListBox.Item, { id: profile.id, textValue: profileText(profile), children: /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col", children: [
          /* @__PURE__ */ jsx(Label, { children: /* @__PURE__ */ jsx(ProfileName, { profile }) }),
          /* @__PURE__ */ jsx(Description, { children: detailText(profile) })
        ] }) }, profile.id)
      }
    );
  },
  {
    name: "PaymentProfilesSelectField",
    sync: (services, { accountId }) => [
      {
        // No account (or one read as NaN) has no profiles to ask for; the API would answer 400.
        execute: () => accountId != null && Number.isFinite(Number(accountId)) && services.paymentProfiles.getProfiles(accountId),
        deps: [accountId]
      }
    ]
  }
);
const MAX_LENGTH = 10;
const WorkPhoneExtField = ({ value, ...props }) => /* @__PURE__ */ jsx(
  TextField,
  {
    name: "workPhoneExt",
    "aria-label": "Work phone extension",
    placeholder: "Ext.",
    inputMode: "numeric",
    maxLength: MAX_LENGTH,
    value: value ?? "",
    validation: (v) => v.string().matches(/^\d*$/, "Extension must be digits only").test(
      "needs-work-phone",
      "Extension needs a work phone",
      (ext, { options }) => !ext || !!options.context?.workPhone
    ),
    ...props
  }
);
const AccountNotesContext = createContext({
  accountId: null,
  notes: [],
  isLoading: false,
  isSaving: false,
  createNote: () => {
  },
  createAction: null,
  updateNote: () => {
  },
  togglePin: () => {
  },
  deleteNote: () => {
  }
});
const useAccountNotes = () => useContext(AccountNotesContext);
const AccountNotesProvider = ({
  accountId,
  notes,
  isLoading = false,
  isSaving = false,
  actions,
  children
}) => {
  const list = notes ?? [];
  const value = useMemo(
    () => ({
      accountId,
      notes: list,
      isLoading,
      isSaving,
      /** Takes a new note. `type` falls back to General for a caller that offers no choice. */
      createNote: (note) => actions.createAccountNote({
        type: AccountNoteType.General,
        pinned: false,
        ...note,
        accountId
      }),
      /**
       * The reactive create action itself, for a caller that needs the created note back (its id)
       * — results arrive through a reactor, not from the call.
       */
      createAction: actions.createAccountNote,
      /** Takes the whole note back, edited — the route replaces what it is given. */
      updateNote: (note) => actions.updateAccountNote(note.id, { ...note, accountId }),
      togglePin: (note) => actions.updateAccountNote(note.id, { ...note, accountId, pinned: !note.pinned }),
      /** Takes the note, or just its id. */
      deleteNote: (note) => actions.deleteAccountNote(note?.id ?? note)
    }),
    [accountId, list, isLoading, isSaving, actions]
  );
  return /* @__PURE__ */ jsx(AccountNotesContext.Provider, { value, children });
};
const notesKey = (accountId, actionKey) => actionKey ?? `accountNotes-${accountId}`;
const accountNoteFilters = (accountId) => new Filters([
  new FilterConstant({ name: "accountId", title: "Account #", defaultValue: accountId }),
  new FilterSelect({
    name: "type",
    title: "Type",
    loader: () => Promise.resolve(AccountNoteTypeOptions)
  }),
  new FilterInput({
    name: "note",
    title: "Note",
    options: { searchType: "contains", isClearable: true }
  }),
  new FilterDateRange({ name: "createdOnUtc", title: "Created On" })
]);
const AccountNotesController = reactive.component(
  ({ services, data, monitors, accountId, actionKey, filters: filters2, pageSize = 100, children }) => {
    const id = Number(accountId);
    const key = notesKey(id, actionKey);
    const handleExecute = useCallback((action, metadata) => action(metadata, key, id), [key, id]);
    const brokerFilters = useMemo(() => filters2 ?? accountNoteFilters(id), [filters2, id]);
    const actions = useMemo(
      () => ({
        key,
        getAccountNotes: services.accountNotes.getAccountNotes,
        updateAccountNote: services.accountNotes.updateAccountNote,
        createAccountNote: services.accountNotes.createAccountNote,
        deleteAccountNote: services.accountNotes.deleteAccountNote
      }),
      [services, key]
    );
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: actions.key,
        action: actions.getAccountNotes,
        filters: brokerFilters,
        sorting: new Sorting([{ title: "Created On", value: "createdOnUtc" }], "createdOnUtc", "desc"),
        paging: new Paging(pageSize),
        isExecuting: monitors.getAccountNotes.executing,
        onExecute: handleExecute,
        deps: [id],
        children: /* @__PURE__ */ jsx(
          AccountNotesProvider,
          {
            accountId: id,
            notes: data.notes,
            isLoading: monitors.getAccountNotes.executing,
            isSaving: monitors.createAccountNote.executing || monitors.updateAccountNote.executing || monitors.deleteAccountNote.executing,
            actions,
            children
          }
        )
      }
    );
  },
  {
    name: "AccountNotesController",
    queries: ({ accountId, actionKey }) => {
      const id = Number(accountId);
      return [
        {
          name: "notes",
          execute: (db) => db.withRequestBroker(
            notesKey(id, actionKey),
            () => db.account_notes.where({ accountId: id }),
            { sorting: true }
          ),
          deps: [id, actionKey]
        }
      ];
    },
    monitors: (services) => [
      services.accountNotes.getAccountNotes,
      services.accountNotes.createAccountNote,
      services.accountNotes.updateAccountNote,
      services.accountNotes.deleteAccountNote
    ]
  }
);
function NoteActions({ pinned, onEdit, onTogglePin, onDelete }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-none items-center gap-1.5", children: [
    onEdit && /* @__PURE__ */ jsx(IconButton, { size: "sm", icon: "ri-edit-line", label: "Edit note", onClick: onEdit }),
    /* @__PURE__ */ jsx(
      IconButton,
      {
        size: "sm",
        icon: pinned ? "ri-pushpin-fill" : "ri-pushpin-line",
        label: pinned ? "Unpin note" : "Pin note",
        onClick: onTogglePin,
        className: pinned ? "!border-primary/40 !bg-primary/10 !text-primary" : "hover:!bg-primary/10 hover:!text-primary"
      }
    ),
    /* @__PURE__ */ jsx(DeleteButton, { isIconOnly: true, size: "sm", label: "Delete note", onPress: onDelete })
  ] });
}
function NoteRow({ note, compact, onEdit, onTogglePin, onDelete }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `group flex items-start border-b border-[var(--border-subtle)] last:border-b-0 hover:bg-[var(--surface)] ${compact ? "gap-2 px-4 py-3" : "gap-3 px-5 py-3.5"}`,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
          /* @__PURE__ */ jsx(
            "p",
            {
              className: `m-0 mb-1.5 leading-relaxed text-[var(--foreground)] ${compact ? "text-[13px]" : "text-[13.5px]"}`,
              children: note.note
            }
          ),
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: `flex flex-wrap items-center gap-1.5 text-[var(--fg-3)] ${compact ? "text-[11.5px]" : "text-xs"}`,
              children: [
                AccountNoteTypeDisplay[note.type] && /* @__PURE__ */ jsx(Pill, { tone: "info", children: AccountNoteTypeDisplay[note.type] }),
                /* @__PURE__ */ jsx("span", { className: "font-medium text-[var(--muted)]", children: note.createdBy }),
                /* @__PURE__ */ jsx("span", { children: "·" }),
                /* @__PURE__ */ jsx("span", { children: parseAndFormatDateTime(note.createdOnUtc) }),
                note.followUpOnUtc && /* @__PURE__ */ jsxs(Pill, { tone: note.isCompleted ? "success" : "warning", children: [
                  /* @__PURE__ */ jsx("i", { className: note.isCompleted ? "ri-check-line" : "ri-alarm-line" }),
                  note.isCompleted ? "Followed up" : parseAndFormatDateTime(note.followUpOnUtc)
                ] })
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx("div", { className: "opacity-0 transition-opacity group-hover:opacity-100", children: /* @__PURE__ */ jsx(
          NoteActions,
          {
            pinned: note.pinned,
            onEdit,
            onTogglePin,
            onDelete
          }
        ) })
      ]
    }
  );
}
const AccountNoteTypeLookup = ({ selectionMode, value, ...rest }) => {
  return /* @__PURE__ */ jsx(
    AutocompleteField,
    {
      ...rest,
      value,
      items: AccountNoteTypeOptions,
      emptyContent: "No options",
      selectionMode: selectionMode || "single",
      extractValue: (item) => item.value.toString(),
      extractDisplayValue: (item) => item.label,
      transformIn: (val) => val != null ? val.toString() : null,
      transformOut: (val) => val != null ? Number(val) : null,
      children: (type) => /* @__PURE__ */ jsx(ListBox.Item, { id: type.value, textValue: type.label, children: type.label }, type.value)
    }
  );
};
const DealersContext = createContext([]);
reactive.component(
  ({ data, children }) => /* @__PURE__ */ jsx(DealersContext.Provider, { value: data.dealers ?? [], children }),
  {
    name: "DealersProvider",
    sync: (services) => [
      {
        execute: () => services.dealers.getAllDealers(),
        bypass: (db) => db.dealers_lookup.toCollection().count(),
        deps: []
      }
    ],
    queries: () => [
      {
        name: "dealers",
        defaultValue: [],
        execute: (db) => db.dealers_lookup.toArray(),
        deps: []
      }
    ],
    monitors: (services) => [services.dealers.getAllDealers]
  }
);
const TIMEZONES = [
  { value: "America/New_York", label: "(UTC−5/−4) Eastern" },
  { value: "America/Chicago", label: "(UTC−6/−5) Central" },
  { value: "America/Denver", label: "(UTC−7/−6) Mountain" },
  { value: "America/Los_Angeles", label: "(UTC−8/−7) Pacific" },
  { value: "America/Phoenix", label: "(UTC−7) Arizona — no DST" },
  { value: "America/Anchorage", label: "(UTC−9/−8) Alaska" },
  { value: "Pacific/Honolulu", label: "(UTC−10) Hawaii" }
];
const TimezonesLookup = ({ selectionMode, value, ...rest }) => {
  const current = Array.isArray(value) ? null : value;
  const items = current != null && !TIMEZONES.some((t) => t.value === String(current)) ? [...TIMEZONES, { value: String(current), label: String(current) }] : TIMEZONES;
  return /* @__PURE__ */ jsx(
    AutocompleteField,
    {
      ...rest,
      value,
      items,
      extractValue: (item) => item.value.toString(),
      extractDisplayValue: (item) => item.label,
      transformIn: (val) => val != null ? val.toString() : null,
      transformOut: (val) => val?.toString() ?? null,
      emptyContent: "No options",
      selectionMode: selectionMode || "single",
      children: (timezone) => /* @__PURE__ */ jsx(ListBox.Item, { id: timezone.value, textValue: timezone.label, children: timezone.label }, timezone.value)
    }
  );
};
const blankToNull = (value) => value === "" || value === void 0 ? null : value;
const SOURCE_REMINDER = "reminders.source-reminder";
const NOTES_REMINDER_SOURCE = "account-notes";
const reminderTextFor = (noteText) => {
  const text = String(noteText ?? "").replace(/\s+/g, " ").trim();
  return `Follow up: ${text.length > 120 ? `${text.slice(0, 117)}…` : text}`;
};
const toNote = (values) => ({
  ...values,
  followUpOnUtc: blankToNull(values.followUpOnUtc),
  followedUpBy: blankToNull(values.followedUpBy)
});
function NoteComposer({
  note,
  onSave,
  onCancel,
  isSaving = false,
  compact = false,
  inline = false
}) {
  const [form, setForm] = useState(null);
  const Reminder = useComponent(SOURCE_REMINDER);
  const isEditing = !!note?.id;
  const hasText = !!String(form?.data?.note ?? note?.note ?? "").trim();
  const canSave = hasText && form?.isValid !== false;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: compact ? "flex-none border-b border-[var(--border)] bg-[var(--background)] p-3.5" : inline ? "m-2 rounded-md border border-primary bg-[var(--surface)] p-4" : "mb-4 rounded-md border border-primary bg-[var(--surface)] p-4",
      children: [
        /* @__PURE__ */ jsxs(Uniformly, { onChange: setForm, children: [
          /* @__PURE__ */ jsx(
            AccountNoteTypeLookup,
            {
              name: "type",
              label: "Type",
              isRequired: true,
              value: note?.type ?? AccountNoteType.General,
              validation: (v) => v.string().required("Type is required")
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "mt-2.5", children: /* @__PURE__ */ jsx(
            TextAreaField,
            {
              name: "note",
              label: isEditing ? "Edit note" : "Add a note",
              isRequired: true,
              value: note?.note ?? "",
              validation: (v) => v.string().trim().max(2048, "Notes cannot exceed 2048 characters").required("A note is required"),
              autoFocus: true
            }
          ) }),
          /* @__PURE__ */ jsx("div", { className: "mt-2.5", children: /* @__PURE__ */ jsx(CheckboxField, { name: "pinned", value: note?.pinned ?? false, children: "Pinned" }) }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: `mt-2.5 grid gap-2.5 ${compact ? "grid-cols-1" : "grid-cols-[minmax(0,1fr)_auto] items-end"}`,
              children: [
                /* @__PURE__ */ jsx(
                  DateTimeField,
                  {
                    name: "followUpOnUtc",
                    label: "Follow up on",
                    value: note?.followUpOnUtc ?? null,
                    granularity: "minute",
                    hourCycle: 12
                  }
                ),
                /* @__PURE__ */ jsx("div", { className: compact ? void 0 : "flex h-9 items-center", children: /* @__PURE__ */ jsx(
                  Reminder,
                  {
                    source: NOTES_REMINDER_SOURCE,
                    sourceKey: note?.id,
                    text: reminderTextFor(form?.data?.note ?? note?.note),
                    url: window.location.href,
                    defaultRunOnUtc: blankToNull(form?.data?.followUpOnUtc ?? note?.followUpOnUtc),
                    allowTeamsChannel: true
                  }
                ) })
              ]
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "mt-2.5", children: /* @__PURE__ */ jsx(CheckboxField, { name: "isCompleted", value: note?.isCompleted ?? false, children: "Follow-up completed" }) }),
          /* @__PURE__ */ jsx("div", { className: "mt-2.5", children: /* @__PURE__ */ jsx(
            TextField,
            {
              name: "followedUpBy",
              label: "Followed up by",
              value: note?.followedUpBy ?? "",
              validation: (v) => v.string().max(150, "Cannot exceed 150 characters")
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx("div", { className: compact ? "mt-2.5" : "mt-3", children: /* @__PURE__ */ jsx(
          ResourceActions,
          {
            saveLabel: isEditing ? "Save note" : "Add note",
            onSave: () => canSave && onSave(toNote(form?.data ?? {})),
            onCancel,
            isLoading: isSaving,
            isSaveEnabled: canSave,
            errors: form?.errors
          }
        ) })
      ]
    }
  );
}
const NO_ACTION = { reactor: () => ({ connect: () => {
}, disconnect: () => {
} }) };
const useNoteEditor = () => {
  const { notes, createNote, updateNote, createAction } = useAccountNotes();
  const [isComposing, setIsComposing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isInline, setIsInline] = useState(false);
  const [created, setCreated] = useState(null);
  const awaitingCreate = useRef(false);
  const action = createAction ?? NO_ACTION;
  useSuccessReactor(action, (payload) => {
    if (!awaitingCreate.current || payload?.id == null) return;
    awaitingCreate.current = false;
    setCreated(payload);
    setEditingId(payload.id);
  });
  useErrorReactor(action, () => {
    awaitingCreate.current = false;
  });
  const editing = editingId === null ? null : notes.find((note) => note.id === editingId) ?? (created?.id === editingId ? created : null);
  const open = (note) => {
    setEditingId(note?.id ?? null);
    setIsInline(note?.id != null);
    setCreated(null);
    setIsComposing(true);
  };
  const close = () => {
    awaitingCreate.current = false;
    setIsComposing(false);
    setIsInline(false);
    setEditingId(null);
    setCreated(null);
  };
  const save = (values) => {
    if (editing) {
      updateNote({ ...editing, ...values });
      close();
      return;
    }
    awaitingCreate.current = true;
    createNote(values);
  };
  const isShowing = isComposing && (!isInline || editing !== null);
  return {
    isComposing: isShowing,
    isComposingNew: isShowing && !isInline,
    inlineId: isShowing && isInline ? editingId : null,
    editing,
    editingId,
    open,
    close,
    save
  };
};
const SectionLabel$1 = ({ icon, children }) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5 px-4 pb-1.5 pt-3 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--fg-3)]", children: [
  /* @__PURE__ */ jsx("i", { className: icon }),
  children
] });
function AccountNotesPanel() {
  const { notes, isSaving, deleteNote, togglePin } = useAccountNotes();
  const { isComposing, isComposingNew, inlineId, editing, editingId, open, close, save } = useNoteEditor();
  const pinned = notes.filter((note) => note.pinned);
  const unpinned = notes.filter((note) => !note.pinned);
  const rows = (list) => list.map(
    (note) => note.id === inlineId ? /* @__PURE__ */ jsx(
      NoteComposer,
      {
        compact: true,
        inline: true,
        note: editing,
        isSaving,
        onSave: save,
        onCancel: close
      },
      note.id
    ) : /* @__PURE__ */ jsx(
      NoteRow,
      {
        compact: true,
        note,
        onEdit: () => open(note),
        onTogglePin: () => togglePin(note),
        onDelete: () => deleteNote(note)
      },
      note.id
    )
  );
  return (
    // The card owns the height: only Main scrolls, so the footer's action stays pinned to the
    // bottom of the drawer however long the list grows. `gap-0 p-0` strips the card's own spacing —
    // this panel runs edge to edge, with its rules drawn by the header and footer borders.
    /* @__PURE__ */ jsxs(FlatCard, { className: "gap-0 p-0", children: [
      /* @__PURE__ */ jsxs(FlatCard.Header, { className: "h-12 flex-none flex-row items-center gap-2 border-b border-[var(--border)] pl-3.5 pr-12", children: [
        /* @__PURE__ */ jsx("i", { className: "ri-sticky-note-line text-[var(--muted)]" }),
        /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold text-[var(--foreground)]", children: "Notes" }),
        /* @__PURE__ */ jsx("span", { className: "rounded-full bg-[var(--border-subtle)] px-1.5 text-[11px] font-semibold text-[var(--muted)]", children: notes.length })
      ] }),
      isComposingNew && /* @__PURE__ */ jsx(
        NoteComposer,
        {
          compact: true,
          note: editing,
          isSaving,
          onSave: save,
          onCancel: close
        },
        editingId ?? "new"
      ),
      /* @__PURE__ */ jsxs(FlatCard.Main, { children: [
        pinned.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(SectionLabel$1, { icon: "ri-pushpin-fill", children: "Pinned" }),
          rows(pinned)
        ] }),
        unpinned.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(SectionLabel$1, { icon: "ri-file-list-3-line", children: "All notes" }),
          rows(unpinned)
        ] }),
        notes.length === 0 && /* @__PURE__ */ jsx("div", { className: "px-5 py-10 text-center text-[13px] text-[var(--fg-3)]", children: "No notes yet." })
      ] }),
      /* @__PURE__ */ jsx(FlatCard.Footer, { className: "flex-none border-t border-[var(--border)] p-3", children: /* @__PURE__ */ jsx(
        Button,
        {
          fullWidth: true,
          variant: "primary",
          startContent: /* @__PURE__ */ jsx("i", { className: "ri-add-line" }),
          isDisabled: isComposing,
          onPress: () => open(null),
          children: "Add note"
        }
      ) })
    ] })
  );
}
const AccountNotesDrawer = withDrawerController(AccountNotesPanel);
const SectionLabel = ({ icon, children, count }) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5 border-b border-[var(--border-subtle)] bg-[var(--background)] px-5 py-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--fg-3)]", children: [
  /* @__PURE__ */ jsx("i", { className: icon }),
  children,
  /* @__PURE__ */ jsx("span", { className: "rounded-full bg-[var(--border-subtle)] px-1.5 text-[10px] text-[var(--muted)]", children: count })
] });
function AccountNotesList() {
  const { notes, isSaving, deleteNote, togglePin } = useAccountNotes();
  const { isComposing, isComposingNew, inlineId, editing, editingId, open, close, save } = useNoteEditor();
  const pinned = notes.filter((note) => note.pinned);
  const unpinned = notes.filter((note) => !note.pinned);
  const rows = (list) => list.map(
    (note) => note.id === inlineId ? /* @__PURE__ */ jsx(
      NoteComposer,
      {
        inline: true,
        note: editing,
        isSaving,
        onSave: save,
        onCancel: close
      },
      note.id
    ) : /* @__PURE__ */ jsx(
      NoteRow,
      {
        note,
        onEdit: () => open(note),
        onTogglePin: () => togglePin(note),
        onDelete: () => deleteNote(note)
      },
      note.id
    )
  );
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
    /* @__PURE__ */ jsx(
      HorizontalControl,
      {
        chipScope: "global",
        hideSort: true,
        createLabel: "Add note",
        createEnabled: !isComposing,
        onCreate: () => open(null)
      }
    ),
    isComposingNew && /* @__PURE__ */ jsx(
      NoteComposer,
      {
        note: editing,
        isSaving,
        onSave: save,
        onCancel: close
      },
      editingId ?? "new"
    ),
    /* @__PURE__ */ jsx("div", { className: "overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]", children: notes.length === 0 ? /* @__PURE__ */ jsx("div", { className: "px-5 py-12 text-center text-[13px] text-[var(--fg-3)]", children: "No notes on this account." }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      pinned.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(SectionLabel, { icon: "ri-pushpin-fill", count: pinned.length, children: "Pinned" }),
        rows(pinned)
      ] }),
      unpinned.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(SectionLabel, { icon: "ri-file-list-3-line", count: unpinned.length, children: "All notes" }),
        rows(unpinned)
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(HorizontalPager, {})
  ] });
}
const PinnedNotesWidget = () => {
  const { notes } = useAccountNotes();
  const pinnedNotes = notes.filter((n) => n.pinned);
  if (pinnedNotes.length === 0) return null;
  return /* @__PURE__ */ jsxs(Widget, { className: "w-full", children: [
    /* @__PURE__ */ jsxs(Widget.Header, { children: [
      /* @__PURE__ */ jsx(Widget.Title, { children: "Pinned notes" }),
      /* @__PURE__ */ jsxs(Widget.Description, { children: [
        pinnedNotes.length,
        " pinned"
      ] })
    ] }),
    /* @__PURE__ */ jsx(Widget.Content, { className: "p-0", children: pinnedNotes.map((note, i) => /* @__PURE__ */ jsxs(
      "div",
      {
        className: `flex gap-2.5 px-5 py-3.5 ${i < pinnedNotes.length - 1 ? "border-b border-[var(--border-subtle)]" : ""}`,
        children: [
          /* @__PURE__ */ jsx("i", { className: "ri-pushpin-fill mt-0.5 flex-none text-xs text-[var(--accent)]" }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
            /* @__PURE__ */ jsx("p", { className: "m-0 mb-1.5 text-[13.5px] leading-relaxed text-[var(--foreground)]", children: note.note }),
            /* @__PURE__ */ jsxs("p", { className: "text-[11.5px] text-[var(--fg-3)]", children: [
              /* @__PURE__ */ jsx("span", { className: "font-medium text-[var(--muted)]", children: note.createdBy }),
              " ·",
              " ",
              parseAndFormatDateTime(note.createdOnUtc)
            ] })
          ] })
        ]
      },
      note.id
    )) })
  ] });
};
function DealerHeader({ onOpenNotes }) {
  const dealer = useDealer();
  const { notes } = useAccountNotes();
  const pinnedCount = notes.filter((note) => note.pinned).length;
  const person = getDealerLabel(dealer);
  const title = dealer?.companyName || person;
  return /* @__PURE__ */ jsx("div", { className: "border-b border-[var(--border-default-c)] bg-[var(--surface)] px-7 py-4", children: /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-4", children: [
    /* @__PURE__ */ jsx(AccountAvatar, { name: title, size: 44 }),
    /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
      /* @__PURE__ */ jsxs("div", { className: "mb-2 flex flex-wrap items-center gap-2.5", children: [
        /* @__PURE__ */ jsx("h1", { className: "m-0 text-xl font-bold leading-tight tracking-tight text-[var(--foreground)]", children: title || "—" }),
        dealer?.companyName && person && /* @__PURE__ */ jsx(Pill, { children: person }),
        /* @__PURE__ */ jsx(InactiveChip, { isActive: dealer?.isActive }),
        /* @__PURE__ */ jsx("span", { className: "font-mono text-xs text-[var(--fg-3)]", children: dealer?.id })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-x-5 gap-y-1", children: [
        /* @__PURE__ */ jsx(ContactChip, { icon: "ri-organization-chart", label: dealer?.parentName }),
        /* @__PURE__ */ jsx(ContactChip, { icon: "ri-mail-line", label: dealer?.email }),
        /* @__PURE__ */ jsx(
          ContactChip,
          {
            icon: "ri-briefcase-line",
            label: dealer?.workPhone && `Work · ${formatPhoneWithExt(dealer.workPhone, dealer.workPhoneExt)}`
          }
        ),
        /* @__PURE__ */ jsx(
          ContactChip,
          {
            icon: "ri-smartphone-line",
            label: dealer?.mobilePhone && `Mobile · ${formatPhone(dealer.mobilePhone)}`
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-none items-center gap-2", children: /* @__PURE__ */ jsxs(
      Button,
      {
        isIconOnly: true,
        className: "relative",
        variant: "secondary",
        "aria-label": pinnedCount > 0 ? `Notes (${pinnedCount} pinned)` : "Notes",
        title: "Notes",
        onPress: onOpenNotes,
        children: [
          /* @__PURE__ */ jsx("i", { className: "ri-sticky-note-line" }),
          pinnedCount > 0 && /* @__PURE__ */ jsx("span", { className: "absolute -top-1 -right-1 inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-bold text-white", children: pinnedCount })
        ]
      }
    ) })
  ] }) });
}
const columns$3 = [
  { propertyName: "id", name: "#", isSortable: true },
  { propertyName: "parentName", name: "Parent", isSortable: true },
  { propertyName: "companyName", name: "Company Name", isSortable: true },
  { propertyName: "firstName", name: "First Name", isSortable: true },
  { propertyName: "lastName", name: "Last Name", isSortable: true },
  { propertyName: "homePhone", name: "Primary Phone", isSortable: true },
  { propertyName: "timeZone", name: "Time Zone", isSortable: true }
];
const DealersTable = ({ dealers: dealers2 = [], onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    switch (columnKey) {
      case "id":
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(Chip, { variant: "soft", color: "accent", size: "sm", children: row.id }),
          /* @__PURE__ */ jsx(InactiveChip, { isActive: row.isActive })
        ] });
      case "parentName":
        return /* @__PURE__ */ jsx("div", { className: "flex justify-stretch gap-2", children: row.parentId ? `${row.parentId} - ${row.parentName}` : "" });
      default:
        return row[columnKey];
    }
  }, []);
  return /* @__PURE__ */ jsx(
    RequestBrokerTable,
    {
      columns: columns$3,
      items: dealers2,
      renderColumn: (column) => /* @__PURE__ */ jsx(
        Table.Column,
        {
          id: column.propertyName,
          isRowHeader: column.propertyName === "id",
          allowsSorting: column?.isSortable,
          children: column.name
        },
        column.propertyName
      ),
      renderRow: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.id, onAction: () => onSelect(row), children: columns$3.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(row, column.propertyName) }, column.propertyName)) }, row.id)
    }
  );
};
const DEFAULT_ACCOUNT_TYPES = [AccountType$1.Customer, AccountType$1.Dealer];
const SEARCH_POLICY = {
  apiKey: "AccountsAPI",
  resource: "Accounts",
  operation: "Search"
};
const CHIP_COLOR = {
  [AccountType$1.Customer]: "accent",
  [AccountType$1.Dealer]: "success"
};
const AccountResultRow = ({ result }) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
  /* @__PURE__ */ jsx(Chip, { size: "sm", variant: "soft", color: CHIP_COLOR[result.type] ?? "default", children: /* @__PURE__ */ jsx(Chip.Label, { children: AccountTypeDisplay[result.type] ?? result.typeName }) }),
  /* @__PURE__ */ jsx("span", { className: "min-w-0 flex-1 truncate font-medium", children: result.name }),
  /* @__PURE__ */ jsx(InactiveChip, { isActive: result.isActive }),
  /* @__PURE__ */ jsxs("span", { className: "shrink-0 text-sm text-muted tabular-nums", children: [
    "#",
    result.id
  ] })
] });
const apiHost = () => {
  const host = AccountConfig.current?.api?.host;
  if (!host) throw new Error("No accounts API host is configured for this environment.");
  return host;
};
let accounts = null;
let customers = null;
let dealers = null;
const getAccountsClient = () => accounts ??= new AccountsClient(apiHost());
const getCustomersClient = () => customers ??= new CustomersClient(apiHost());
const getDealersClient = () => dealers ??= new DealersClient(apiHost());
const defaultRenderItem = (result) => /* @__PURE__ */ jsx(AccountResultRow, { result });
const AccountSearchComboBox = ({
  accountTypes = DEFAULT_ACCOUNT_TYPES,
  initialItems = null,
  isLoading = false,
  onSelect,
  children,
  ...rest
}) => {
  const [results, setResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const items = results ?? initialItems;
  const latestRequest = useRef(0);
  const handleSearch = useCallback(
    async (term) => {
      const request = ++latestRequest.current;
      setIsSearching(true);
      try {
        const found = await getAccountsClient().search({ query: term, types: accountTypes });
        if (request !== latestRequest.current) return;
        setResults(found);
      } catch (error) {
        if (request !== latestRequest.current) return;
        setResults([]);
        alerts.error(error instanceof Error ? error.message : "Account search failed");
      } finally {
        if (request === latestRequest.current) setIsSearching(false);
      }
    },
    [accountTypes]
  );
  const handleSelect = useCallback(
    (value, result) => onSelect?.(value === null || value === void 0 ? null : Number(value), result ?? null),
    [onSelect]
  );
  return /* @__PURE__ */ jsx(RequirePolicy, { ...SEARCH_POLICY, children: /* @__PURE__ */ jsx(
    SearchComboBox,
    {
      placeholder: "Search accounts...",
      emptyMessage: "No accounts matched that search.",
      minLength: 4,
      delay: 600,
      ...rest,
      items,
      isSearching: isSearching || isLoading,
      onSearch: handleSearch,
      onSelect: handleSelect,
      getItemValue: (result) => result.id,
      getItemText: (result) => result.name ?? "",
      children: children ?? defaultRenderItem
    }
  ) });
};
FormField(
  ({
    name,
    value,
    onChange,
    showError,
    errors,
    // Uniformly's other injected props are pulled out so they are not forwarded to the combo box.
    isDirty,
    isValid,
    showValid,
    onlyDirty,
    ...rest
  }) => /* @__PURE__ */ jsx(
    AccountSearchComboBox,
    {
      name,
      defaultValue: typeof value === "string" || typeof value === "number" ? value : void 0,
      isInvalid: showError,
      errorMessage: errors?.[0],
      onSelect: (accountId) => onChange(accountId),
      ...rest
    }
  )
);
const defaultRenderResult = (result) => /* @__PURE__ */ jsx(AccountResultRow, { result });
const AccountSearchPanel$1 = ({
  accountTypes = DEFAULT_ACCOUNT_TYPES,
  children,
  ...rest
}) => {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const latestRequest = useRef(0);
  const handleSelect = useCallback(
    (result) => {
      const paths = RoutePaths();
      navigate(
        result.type === AccountType$1.Dealer ? paths.dealers.details(result.id) : paths.customers.details(result.id)
      );
    },
    [navigate]
  );
  const selectResult = rest.onResultSelect ?? handleSelect;
  const handleSearch = useCallback(
    async (term) => {
      const request = ++latestRequest.current;
      setIsSearching(true);
      try {
        const found = await getAccountsClient().search({ query: term, types: accountTypes });
        if (request !== latestRequest.current) return;
        if (found.length === 1) {
          setResults(null);
          selectResult(found[0], 0);
          return;
        }
        setResults(found);
      } catch (error) {
        if (request !== latestRequest.current) return;
        setResults([]);
        alerts.error(error instanceof Error ? error.message : "Account search failed");
      } finally {
        if (request === latestRequest.current) setIsSearching(false);
      }
    },
    [accountTypes, selectResult]
  );
  const runtime = useRuntimeProvider();
  if (!runtime.config?.features.customers) {
    console.warn("WARNING: Account search disabled by feature flag");
    return;
  }
  return /* @__PURE__ */ jsx(RequirePolicy, { ...SEARCH_POLICY, children: /* @__PURE__ */ jsx(
    SearchPanel,
    {
      placeholder: "Account Search",
      modalTitle: "Search Results",
      modalSize: "lg",
      emptyMessage: "No customers or dealers matched that search.",
      onResultSelect: handleSelect,
      ...rest,
      onSearch: handleSearch,
      isSearching,
      results,
      onClearResults: () => setResults(null),
      children: children ?? defaultRenderResult
    }
  ) });
};
const toAccountId = (value) => {
  if (value === null || value === void 0 || value === "") return null;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};
const useAccountLookup = (accountId, load, {
  isEnabled = true,
  errorMessage: errorMessage2 = "Could not load the selected account."
} = {}) => {
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const latestLoad = useRef(load);
  const latestRequest = useRef(0);
  const loadedId = useRef(null);
  useEffect(() => {
    latestLoad.current = load;
  });
  useEffect(() => {
    const id = toAccountId(accountId);
    if (id === null) {
      latestRequest.current += 1;
      loadedId.current = null;
      setAccount(null);
      setIsLoading(false);
      return;
    }
    if (!isEnabled) return;
    if (loadedId.current === id) return;
    const request = ++latestRequest.current;
    loadedId.current = id;
    setIsLoading(true);
    latestLoad.current(id).then((result) => {
      if (request === latestRequest.current) setAccount(result);
    }).catch((error) => {
      if (request !== latestRequest.current) return;
      loadedId.current = null;
      alerts.error(error instanceof Error ? error.message : errorMessage2);
    }).finally(() => {
      if (request === latestRequest.current) setIsLoading(false);
    });
  }, [accountId, isEnabled, errorMessage2]);
  return { account, isLoading };
};
const CUSTOMER_TYPES = [AccountType$1.Customer];
const toResult$1 = (customer) => ({
  id: customer.id,
  type: AccountType$1.Customer,
  name: customer.fullName || [customer.firstName, customer.lastName].filter(Boolean).join(" ")
});
const loadCustomer = async (id) => toResult$1(await getCustomersClient().getById(id));
const CustomerSearchComboBox$1 = ({
  defaultValue = null,
  isLoading = false,
  onSelect,
  ...rest
}) => {
  const [hasSelected, setHasSelected] = useState(false);
  const { account: customer, isLoading: isReading } = useAccountLookup(defaultValue, loadCustomer, {
    isEnabled: !hasSelected,
    errorMessage: "Could not load the selected customer."
  });
  const handleSelect = useCallback(
    (accountId, result) => {
      setHasSelected(true);
      onSelect?.(accountId, result);
    },
    [onSelect]
  );
  return /* @__PURE__ */ jsx(
    AccountSearchComboBox,
    {
      placeholder: "Search customers...",
      emptyMessage: "No customers matched that search.",
      ...rest,
      accountTypes: CUSTOMER_TYPES,
      defaultValue: defaultValue ?? void 0,
      initialItems: customer ? [customer] : null,
      isLoading: isLoading || isReading,
      onSelect: handleSelect
    }
  );
};
const CustomerSearchField$1 = FormField(
  ({
    name,
    value,
    onChange,
    showError,
    errors,
    // Uniformly's other injected props are pulled out so they are not forwarded to the combo box.
    isDirty,
    isValid,
    showValid,
    onlyDirty,
    ...rest
  }) => /* @__PURE__ */ jsx(
    CustomerSearchComboBox$1,
    {
      name,
      defaultValue: typeof value === "string" || typeof value === "number" ? value : void 0,
      isInvalid: showError,
      errorMessage: errors?.[0],
      onSelect: (accountId) => onChange(accountId),
      ...rest
    }
  )
);
const CURRENT_DEALER = "accounts.current-dealer";
const CurrentDealer = defineUserContext(
  CURRENT_DEALER,
  () => getDealersClient().getMine()
);
const DEALER_TYPES = [AccountType$1.Dealer];
const toResult = (dealer) => ({
  id: dealer.id,
  type: AccountType$1.Dealer,
  name: dealer.companyName || [dealer.firstName, dealer.lastName].filter(Boolean).join(" "),
  isActive: dealer.isActive
});
const hasValue = (value) => value !== null && value !== void 0 && value !== "";
const DealerSearchComboBox$1 = ({
  defaultValue = null,
  defaultToCurrentDealer = true,
  isLoading = false,
  onSelect,
  ...rest
}) => {
  const [hasSelected, setHasSelected] = useState(false);
  const currentDealer = useUserContext(CurrentDealer);
  const fallback = defaultToCurrentDealer && !hasValue(defaultValue) && !hasSelected ? currentDealer : null;
  const loadDealer = useCallback(
    async (id) => toResult(id === currentDealer?.id ? currentDealer : await getDealersClient().getById(id)),
    [currentDealer]
  );
  const { account: dealer, isLoading: isReading } = useAccountLookup(defaultValue, loadDealer, {
    isEnabled: !hasSelected,
    errorMessage: "Could not load the selected dealer."
  });
  const reportedFallback = useRef(false);
  useEffect(() => {
    if (!fallback || reportedFallback.current) return;
    reportedFallback.current = true;
    onSelect?.(fallback.id, toResult(fallback));
  }, [fallback, onSelect]);
  const handleSelect = useCallback(
    (accountId, result) => {
      setHasSelected(true);
      onSelect?.(accountId, result);
    },
    [onSelect]
  );
  const initial = dealer ?? (fallback ? toResult(fallback) : null);
  return /* @__PURE__ */ jsx(
    AccountSearchComboBox,
    {
      placeholder: "Search dealers...",
      emptyMessage: "No dealers matched that search.",
      ...rest,
      accountTypes: DEALER_TYPES,
      defaultValue: hasValue(defaultValue) ? defaultValue ?? void 0 : fallback?.id,
      initialItems: initial ? [initial] : null,
      isLoading: isLoading || isReading,
      onSelect: handleSelect
    }
  );
};
const DealerSearchFieldInner = FormField(
  ({
    name,
    value,
    onChange,
    showError,
    errors,
    // Uniformly's other injected props are pulled out so they are not forwarded to the combo box.
    isDirty,
    isValid,
    showValid,
    onlyDirty,
    ...rest
  }) => /* @__PURE__ */ jsx(
    DealerSearchComboBox$1,
    {
      name,
      defaultValue: typeof value === "string" || typeof value === "number" ? value : void 0,
      isInvalid: showError,
      errorMessage: errors?.[0],
      onSelect: (accountId) => onChange(accountId),
      ...rest
    }
  )
);
const DealerSearchField$1 = ({
  defaultToCurrentDealer = true,
  ...props
}) => {
  const currentDealer = useUserContext(CurrentDealer);
  const fallback = defaultToCurrentDealer && !props.isDisabled && !props.value ? currentDealer?.id : void 0;
  return /* @__PURE__ */ jsx(
    DealerSearchFieldInner,
    {
      ...props,
      defaultToCurrentDealer,
      value: fallback ?? props.value
    }
  );
};
const DealerForm = ({ dealer = {}, onChange }) => {
  return /* @__PURE__ */ jsx(Uniformly, { className: "flex flex-col gap-2", onChange, children: /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsxs(Card.Content, { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsx(
      RequirePolicy,
      {
        resource: "Dealers",
        operation: ["Create", "Update"],
        context: "all",
        fallback: /* @__PURE__ */ jsx(TextField, { type: "hidden", name: "parentId", value: dealer.parentId }),
        children: /* @__PURE__ */ jsx(
          DealerSearchField$1,
          {
            name: "parentId",
            label: "Parent",
            size: "lg",
            value: dealer.parentId,
            defaultToCurrentDealer: !dealer.id
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "companyName",
        label: "Company Name",
        value: dealer.companyName,
        validation: (x) => x.string().max(250).required("Company Name is required"),
        isRequired: true
      }
    ),
    /* @__PURE__ */ jsx(TextField, { name: "prefix", label: "Prefix", value: dealer.prefix }),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "firstName",
        label: "First Name",
        value: dealer.firstName,
        validation: (x) => x.string().max(250).required("First Name is required"),
        isRequired: true
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        name: "lastName",
        label: "Last Name",
        value: dealer.lastName,
        validation: (x) => x.string().max(250).required("Last Name is required"),
        isRequired: true
      }
    ),
    /* @__PURE__ */ jsx(TextField, { name: "suffix", label: "Suffix", value: dealer.suffix }),
    /* @__PURE__ */ jsx(
      EmailField,
      {
        name: "email",
        label: "Email",
        value: dealer.email,
        isRequired: true,
        requiredMessage: "Email is required",
        validationMessage: "Invalid email"
      }
    ),
    /* @__PURE__ */ jsx(
      PhoneField,
      {
        name: "homePhone",
        label: "Primary Phone",
        value: dealer.homePhone,
        validation: (x) => x.string().required("Primary Phone is required"),
        isRequired: true
      }
    ),
    /* @__PURE__ */ jsx(PhoneField, { name: "mobilePhone", label: "Mobile Phone", value: dealer.mobilePhone }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2", children: [
      /* @__PURE__ */ jsx("div", { className: "min-w-0 flex-1", children: /* @__PURE__ */ jsx(PhoneField, { name: "workPhone", label: "Work Phone", value: dealer.workPhone }) }),
      /* @__PURE__ */ jsx(WorkPhoneExtField, { label: "Ext.", value: dealer.workPhoneExt, className: "w-24" })
    ] }),
    /* @__PURE__ */ jsx(TimezonesLookup, { name: "timezone", label: "Timezone", value: dealer.timezone }),
    /* @__PURE__ */ jsx(TextField, { name: "taxNumber", label: "Tax ID", value: dealer.taxNumber })
  ] }) }) });
};
const DealerFormContainer = ({ onChange, onTabNext }) => {
  const [dealer, setDealer] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [errors, setErrors] = useState([]);
  const handleChange = (form) => {
    setDealer(form.data);
    setIsValid(form.isValid);
    setErrors(form.errors);
    onChange({
      dealer: {
        ...form.data,
        isValid: form.isValid,
        errors: form.errors
      },
      errors: {
        dealer: form.errors
      }
    });
  };
  return /* @__PURE__ */ jsxs(Card, { className: "h-full", children: [
    /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(DealerForm, { dealer, onChange: handleChange }) }),
    /* @__PURE__ */ jsx(Card.Footer, { className: "flex justify-end", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsx(ErrorListChip, { errors }),
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "primary", isDisabled: !isValid, onPress: onTabNext, children: "Contact info" })
    ] }) })
  ] });
};
const DealerContactContainer = ({
  onChange,
  onTabPrev,
  onSave,
  isSaving,
  isValidSave,
  errors
}) => {
  const handleChange = (contact) => {
    onChange({
      homeAddress: contact.homeAddress,
      shippingAddress: contact.shippingAddress,
      errors: {
        homeAddress: contact.homeAddress?.errors || [],
        shippingAddress: contact.shippingAddress?.errors || []
      }
    });
  };
  return /* @__PURE__ */ jsxs(Card, { className: "h-full", children: [
    /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(ContactForm, { sameAsHome: true, onChange: handleChange }) }),
    /* @__PURE__ */ jsxs(Card.Footer, { className: "flex justify-between", children: [
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "primary", onPress: onTabPrev, children: "Dealer" }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx(ErrorListChip, { errors }),
        /* @__PURE__ */ jsx(
          ConfirmableButton,
          {
            size: "sm",
            label: "Save",
            color: "primary",
            confirmColor: "primary",
            iconClass: "ri-save-line",
            isDisabled: !isValidSave,
            isLoading: isSaving,
            onPress: onSave
          }
        )
      ] })
    ] })
  ] });
};
const DealersListController = ({ onSelect }) => {
  const handleExecute = useCallback((action, metadata) => {
    action(metadata);
  }, []);
  return /* @__PURE__ */ jsx(DealersListLoader, { withRequestBroker: true, children: ({ dealers: dealers2, monitors, action, skipInitial }) => /* @__PURE__ */ jsx(
    RequestBroker,
    {
      actionKey: "getDealers",
      action: action.getDealers,
      filters: DealerFilterDefinitions,
      paging: new Paging(50),
      isExecuting: monitors.getDealers,
      skipInitial,
      onExecute: handleExecute,
      children: /* @__PURE__ */ jsx(DealersTable, { dealers: dealers2, onSelect: (item) => onSelect && onSelect(item) })
    }
  ) });
};
const DealerFilterDefinitions = new Filters([
  new FilterInput({
    name: "id",
    title: "Dealer #",
    options: {
      searchType: "equals",
      isVisible: false,
      isLocked: true
    }
  }),
  new FilterInput({
    name: "firstName",
    title: "First Name"
  }),
  new FilterInput({
    name: "lastName",
    title: "Last Name"
  }),
  new FilterInput({
    name: "homePhone",
    title: "Primary Phone"
  }),
  new FilterInput({
    name: "companyName",
    title: "Company Name"
  }),
  new FilterInput({
    name: "email",
    title: "Email"
  }),
  new FilterDateRange({
    name: "createdOnUtc",
    title: "Created On"
  })
]);
const ERROR_LABELS = {
  dealer: "Dealer"
};
const formatErrors = (errorsObj) => Object.entries(errorsObj).flatMap(
  ([key, list]) => list.map((error) => ERROR_LABELS[key] ? `[${ERROR_LABELS[key]}] ${error}` : error)
);
const dealerCreateReducer = (state, action) => {
  switch (action.type) {
    case "setDealer":
      return {
        ...state,
        dealer: action.payload
      };
    case "setHomeAddress":
      return {
        ...state,
        homeAddress: action.payload
      };
    case "setShippingAddress":
      return {
        ...state,
        shippingAddress: action.payload
      };
    case "setErrors": {
      const nextErrors = {
        ...state.errors,
        ...action.payload
      };
      return {
        ...state,
        errors: nextErrors,
        formattedErrors: formatErrors(nextErrors)
      };
    }
    default:
      return state;
  }
};
const initialState = {
  dealer: {
    isValid: false,
    errors: []
  },
  homeAddress: {
    isValid: false,
    errors: []
  },
  shippingAddress: {
    isValid: false,
    errors: []
  },
  errors: {
    dealer: [],
    contact: [],
    homeAddress: [],
    shippingAddress: []
  },
  formattedErrors: []
};
const DealersListView = () => {
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Accounts" }),
      /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Dealers" })
    ] }) }),
    /* @__PURE__ */ jsx(
      DealersListController,
      {
        onSelect: (dealer) => navigate(RoutePaths().dealers.details(dealer.id))
      }
    )
  ] });
};
const DealerCreateView = reactive.component(
  ({ services, monitors }) => {
    const navigate = useNavigate();
    const [tabSelected, setTabSelected] = useState("dealer");
    const [dealerData, dispatch] = useReducer(dealerCreateReducer, initialState);
    const isValidSave = dealerData.dealer?.isValid && dealerData.homeAddress?.isValid;
    const handleDealer = useCallback(({ dealer, errors }) => {
      dispatch({ type: "setDealer", payload: dealer });
      if (errors) {
        dispatch({
          type: "setErrors",
          payload: errors
        });
      }
    }, []);
    const handleContact = useCallback(({ homeAddress, shippingAddress, errors }) => {
      dispatch({ type: "setHomeAddress", payload: homeAddress });
      dispatch({ type: "setShippingAddress", payload: shippingAddress });
      if (errors) {
        dispatch({
          type: "setErrors",
          payload: errors
        });
      }
    }, []);
    const handleSave = () => {
      services.dealers.createDealer({
        ...dealerData.dealer,
        homeAddress: dealerData.homeAddress?.isValid ? dealerData.homeAddress : void 0,
        shippingAddress: dealerData.shippingAddress?.isValid ? dealerData.shippingAddress : void 0
      });
    };
    useSuccessReactor(services.dealers.createDealer, (payload) => {
      navigate(RoutePaths().dealers.details(payload.id));
    });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between mb-4", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
        /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Accounts" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { onPress: () => navigate(RoutePaths().dealers.list()), children: "Dealers" }),
        /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "New" })
      ] }) }),
      /* @__PURE__ */ jsxs(
        Tabs,
        {
          orientation: "vertical",
          selectedKey: tabSelected,
          onSelectionChange: setTabSelected,
          className: "h-full",
          children: [
            /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsxs(Tabs.List, { "aria-label": "Dealer creation steps", children: [
              /* @__PURE__ */ jsxs(Tabs.Tab, { id: "dealer", children: [
                "Dealer",
                /* @__PURE__ */ jsx(Tabs.Indicator, {})
              ] }),
              /* @__PURE__ */ jsxs(Tabs.Tab, { id: "contact", children: [
                "Contact info",
                /* @__PURE__ */ jsx(Tabs.Indicator, {})
              ] })
            ] }) }),
            /* @__PURE__ */ jsx(Tabs.Panel, { id: "dealer", className: "w-full px-4 pe-0!", children: /* @__PURE__ */ jsx(
              DealerFormContainer,
              {
                onChange: handleDealer,
                onTabNext: () => setTabSelected("contact")
              }
            ) }),
            /* @__PURE__ */ jsx(Tabs.Panel, { id: "contact", className: "w-full px-4 pe-0!", children: /* @__PURE__ */ jsx(
              DealerContactContainer,
              {
                onChange: handleContact,
                isValidSave,
                onTabPrev: () => setTabSelected("dealer"),
                onSave: handleSave,
                isSaving: monitors.createDealer.executing,
                errors: dealerData.formattedErrors
              }
            ) })
          ]
        }
      )
    ] });
  },
  {
    name: "DealerCreateView",
    monitors: (services) => [services.dealers.createDealer]
  }
);
const DealerLayout = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dealer = useDealer();
  const [notesDrawer, NotesDrawer] = useDrawerController(AccountNotesDrawer, {
    size: "sm",
    // Required: withDrawerController defaults isOpen to true and the hook forwards it as given,
    // so leaving it out opens the drawer on first paint.
    isOpen: false
  });
  return /* @__PURE__ */ jsxs("div", { className: "relative -m-4 flex min-h-full flex-col", children: [
    /* @__PURE__ */ jsx(
      Breadcrumb,
      {
        className: "pb-3",
        items: [
          { label: "Accounts" },
          { label: "Dealers", onPress: () => navigate(RoutePaths().dealers.list()) },
          { label: getDealerLabel(dealer) }
        ]
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "sticky top-0 z-10 bg-[var(--surface)]", children: [
      /* @__PURE__ */ jsx(DealerHeader, { onOpenNotes: () => notesDrawer.open() }),
      /* @__PURE__ */ jsx(
        DealerDetailsNavigationTabs,
        {
          active: activeDealerTab(pathname),
          onSelect: (tab) => navigate(RoutePaths().dealers.tab(dealer?.id, tab))
        }
      )
    ] }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-1 flex-col p-4", children: /* @__PURE__ */ jsx(Outlet, {}) }),
    /* @__PURE__ */ jsx(NotesDrawer, { classNames: { body: "p-0" } })
  ] });
};
const DealerDetailsView = ({ dealerId }) => /* @__PURE__ */ jsx(DealerProvider, { dealerId: Number(dealerId), children: /* @__PURE__ */ jsx(AccountNotesController, { accountId: Number(dealerId), children: /* @__PURE__ */ jsx(DealerLayout, {}) }) });
const isValidForm = ({ isValid, data }) => !!isValid && !!data?.parentId;
const DealerDetailsSection = withDealer(
  reactive.component(
    ({ services, monitors, dealerId, dealer }) => {
      const navigate = useNavigate();
      const [form, setForm] = useState({});
      const { user, config } = useRuntimeProvider();
      const apiKey = config?.api?.apiKey ?? "";
      useSuccessReactor(services.dealers.updateDealer, () => {
      });
      useSuccessReactor(services.dealers.deleteDealer, () => {
        navigate(RoutePaths().dealers.list());
      });
      const handleSave = () => {
        services.dealers.updateDealer(dealerId, {
          ...dealer,
          homeAddress: null,
          shippingAddress: null,
          ...form.data
        });
      };
      const handleDelete = () => {
        services.dealers.deleteDealer(dealerId);
      };
      return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
        dealer?.id && /* @__PURE__ */ jsx(DealerForm, { dealer, onChange: setForm }),
        /* @__PURE__ */ jsx(
          ResourceActions,
          {
            errors: form.errors,
            onDelete: handleDelete,
            onSave: handleSave,
            isDeleteEnabled: user.hasPolicy(apiKey, "Dealers", "delete"),
            isSaveEnabled: isValidForm(form) && user.hasPolicy(apiKey, "Dealers", ["Create", "Update"]),
            isLoading: monitors.updateDealer.executing || monitors.deleteDealer.executing
          }
        )
      ] });
    },
    {
      name: "DealerDetailsSection",
      monitors: (services) => [services.dealers.updateDealer, services.dealers.deleteDealer]
    }
  )
);
const DealerUpdateView = ({ dealerId }) => {
  dealerId = Number(dealerId);
  return /* @__PURE__ */ jsx("div", { className: "flex flex-1 flex-col gap-2", children: /* @__PURE__ */ jsx(DealerDetailsSection, { dealerId }) });
};
const ADDRESS_FIELDS = ["address1", "address2", "city", "state", "postalCode", "country"];
const sameAddress = (a, b) => objectEquals(
  objectPick(a ?? {}, ADDRESS_FIELDS),
  objectPick(b ?? {}, ADDRESS_FIELDS),
  (x, y) => lowerCase(trim(x)) === lowerCase(trim(y))
);
const withStoredId = (value, stored) => value ? { ...value, id: stored?.id ?? 0 } : null;
const accountAddressesQuery = (accountId) => ({
  name: "addresses",
  defaultValue: { physical: {}, shipping: null },
  execute: (db) => accountId ? db.addresses.where("accountId").equals(accountId).toArray() : [],
  deps: [accountId],
  transform: (addresses) => ({
    physical: addresses?.find((x) => x.type === AddressType.Physical) ?? {},
    shipping: addresses?.find((x) => x.type === AddressType.Shipping) ?? null
  })
});
const addressesPayload = ({ address, shippingAddress }, { physical, shipping }, sameAsHome) => ({
  physicalAddress: withStoredId(address, physical),
  shippingAddress: withStoredId(sameAsHome ? shipping && address : shippingAddress, shipping)
});
const useSameAsHome = ({ physical, shipping }) => {
  const [choice, setChoice] = useState(null);
  const stored = !shipping?.id || sameAddress(physical, shipping);
  const sameAsHome = choice ?? stored;
  return { sameAsHome, setSameAsHome: setChoice, isChanged: sameAsHome !== stored };
};
const AccountAddressesFields = ({
  address,
  shippingAddress,
  sameAsHome,
  onSameAsHomeChange,
  homeDescription
}) => /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsxs(Fieldset, { children: [
    /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Home Address" }),
    /* @__PURE__ */ jsx(Description, { children: homeDescription }),
    /* @__PURE__ */ jsx(AddressForm$1, { name: "address", value: address, validation: validators.address })
  ] }),
  /* @__PURE__ */ jsxs(Fieldset, { children: [
    /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Shipping Address" }),
    /* @__PURE__ */ jsx(Description, { children: "Where devices and accessories are shipped" }),
    /* @__PURE__ */ jsx(Checkbox, { isSelected: sameAsHome, onChange: onSameAsHomeChange, children: /* @__PURE__ */ jsxs(Checkbox.Content, { children: [
      /* @__PURE__ */ jsx(Checkbox.Control, { children: /* @__PURE__ */ jsx(Checkbox.Indicator, {}) }),
      /* @__PURE__ */ jsx(Label, { children: "Same as home address" })
    ] }) }),
    !sameAsHome && /* @__PURE__ */ jsx(AddressForm$1, { name: "shippingAddress", value: shippingAddress ?? {} })
  ] })
] });
const DealerAddressesController = withDealer(
  reactive.component(
    ({ data, services, monitors, dealerId, dealer }) => {
      const navigate = useNavigate();
      const { physical, shipping } = data.addresses;
      const [form, setForm] = useState({});
      const { sameAsHome, setSameAsHome, isChanged } = useSameAsHome(data.addresses);
      useSuccessReactor(services.dealers.updateDealer, () => {
        navigate(RoutePaths().dealers.list());
      });
      const handleSave = () => {
        if (!(form.isDirty || isChanged) || !form.isValid) return;
        services.dealers.updateDealer(dealerId, {
          ...dealer,
          ...addressesPayload(form.data, data.addresses, sameAsHome)
        });
      };
      const handleCancel = () => {
        navigate(RoutePaths().dealers.list());
      };
      return /* @__PURE__ */ jsxs(FlatCard, { children: [
        /* @__PURE__ */ jsx(FlatCard.Main, { children: /* @__PURE__ */ jsx("div", { className: "px-7 py-6 pb-20", children: /* @__PURE__ */ jsx(Card, { className: "border border-[var(--border)] bg-[var(--surface)]", children: /* @__PURE__ */ jsx(CardContent, { className: "p-6", children: /* @__PURE__ */ jsx(Uniformly, { onChange: setForm, children: /* @__PURE__ */ jsx(
          AccountAddressesFields,
          {
            address: physical,
            shippingAddress: shipping,
            sameAsHome,
            onSameAsHomeChange: setSameAsHome,
            homeDescription: "The dealer's primary address"
          }
        ) }, `${dealerId}-${shipping?.id ?? 0}`) }) }) }) }),
        /* @__PURE__ */ jsx(FlatCard.Footer, { children: /* @__PURE__ */ jsx(
          ResourceActions,
          {
            onCancel: handleCancel,
            onSave: handleSave,
            isSaveEnabled: form.isValid,
            isLoading: monitors.updateDealer.executing,
            errors: form.errors
          }
        ) })
      ] });
    },
    {
      name: "DealerAddressesController",
      // Addresses are an account resource: a dealer's are read the same way as a customer's.
      sync: (services, { dealerId }) => [
        {
          execute: () => dealerId && services.customers.getCustomerAddresses(dealerId),
          deps: [dealerId]
        }
      ],
      queries: ({ dealerId }) => [accountAddressesQuery(dealerId)],
      monitors: (services) => [services.dealers.updateDealer]
    }
  )
);
const DealerAddressesView = ({ dealerId }) => {
  dealerId = Number(dealerId);
  return /* @__PURE__ */ jsx("div", { className: "flex flex-1 flex-col gap-2", children: /* @__PURE__ */ jsx(DealerAddressesController, { dealerId }) });
};
const SubDealersController = reactive.component(
  ({ onSelect, dealerId, data }) => {
    const { dealer } = data;
    return /* @__PURE__ */ jsx(SubDealersTable, { onSelect, subDealers: dealer?.children ?? [] });
  },
  {
    name: "SubDealersController",
    queries: ({ dealerId }) => [
      {
        name: "dealer",
        execute: (db) => db.dealers.get(dealerId),
        deps: [dealerId]
      }
    ]
  }
);
const SubDealersListView = ({ dealerId }) => {
  dealerId = Number(dealerId);
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-1 flex-col gap-2", children: [
    /* @__PURE__ */ jsx(
      SubDealersController,
      {
        onSelect: (dealer) => navigate(RoutePaths().dealers.details(dealer.id)),
        dealerId
      }
    ),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const columns$2 = [
  { propertyName: "id", name: "#" },
  { propertyName: "firstName", name: "First Name" },
  { propertyName: "lastName", name: "Last Name" },
  { propertyName: "homePhone", name: "Primary Phone" },
  { propertyName: "timezone", name: "Time Zone" },
  { propertyName: "treeLevel", name: "Tree Level" }
];
const SubDealersTable = ({ subDealers = [], onSelect }) => {
  const renderCell = useCallback((row, columnKey) => {
    switch (columnKey) {
      case "id":
        return /* @__PURE__ */ jsx(Chip, { variant: "soft", color: "accent", size: "sm", children: row.id });
      default:
        return row[columnKey];
    }
  }, []);
  return /* @__PURE__ */ jsx(Table, { children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Sub Dealers Table", children: [
    /* @__PURE__ */ jsx(Table.Header, { columns: columns$2, children: (column) => /* @__PURE__ */ jsx(Table.Column, { isRowHeader: true, id: column.propertyName, children: column.name }, column.propertyName) }),
    /* @__PURE__ */ jsx(Table.Body, { items: subDealers, children: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.id, onAction: () => onSelect(row), children: columns$2.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(row, String(column.propertyName)) }, column.propertyName)) }, row.id) })
  ] }) }) });
};
const DealerNotesView = ({ dealerId }) => /* @__PURE__ */ jsx(AccountNotesController, { accountId: Number(dealerId), pageSize: 25, children: /* @__PURE__ */ jsx(AccountNotesList, {}) });
const DealerUsersListController = reactive.component(
  ({ services, db, data, monitors, dealerId, onSelected }) => {
    const { users } = data;
    const handleExecute = useCallback(
      (action, metadata) => {
        action(dealerId, metadata);
      },
      [dealerId]
    );
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: `${services.dealers.getDealerUsers.action}-${dealerId}`,
        action: services.dealers.getDealerUsers,
        filters: DealerUsersFiltersDefinition,
        staticFilters: {
          deps: [dealerId],
          handler: (filters2) => {
            filters2.setValue("parentId", dealerId);
          }
        },
        defaultSorting: { propertyName: "id", sortType: true },
        isExecuting: monitors.getDealerUsers.executing,
        skipInitial: () => db.dealer_users.where({ parentId: dealerId }).count(),
        onExecute: handleExecute,
        deps: [dealerId],
        children: /* @__PURE__ */ jsx(
          DealerUsersTable,
          {
            users,
            onSelect: (item) => onSelected && onSelected(item),
            onDelete: (item) => services.dealers.deleteDealerUser(dealerId, item.id)
          }
        )
      }
    );
  },
  {
    name: "DealerUsersListController",
    queries: ({ dealerId }, services) => [
      {
        name: "users",
        execute: (db) => db.withRequestBroker(
          `${services.dealers.getDealerUsers.action}-${dealerId}`,
          () => db.dealer_users.where({ parentId: dealerId }).toArray(),
          {
            sorting: true
          }
        ),
        deps: [dealerId]
      }
    ],
    monitors: (services) => [services.dealers.getDealerUsers]
  }
);
const DealerUserListView = ({ dealerId }) => {
  dealerId = Number(dealerId);
  const navigate = useNavigate();
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-1 flex-col gap-2", children: [
    /* @__PURE__ */ jsx("div", { className: "flex justify-end mb-2", children: /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "primary",
        size: "sm",
        onPress: () => navigate(RoutePaths().dealers.createUser(dealerId)),
        children: [
          /* @__PURE__ */ jsx("i", { className: "ri-add-line" }),
          "New User"
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(
      DealerUsersListController,
      {
        dealerId,
        onSelected: (user) => navigate(RoutePaths().dealers.updateUser(dealerId, Number(user.id)))
      }
    ),
    /* @__PURE__ */ jsx(Outlet, {})
  ] });
};
const DealerUserCreateModal = reactive.component(
  ({ services, monitors, dealerId }) => {
    const navigate = useNavigate();
    const [form, setForm] = useState({});
    useSuccessReactor(services.dealers.createDealerUser, () => {
      navigate(RoutePaths().dealers.users(dealerId));
    });
    const handleClose = () => {
      navigate(RoutePaths().dealers.users(dealerId));
    };
    const handleSave = () => {
      services.dealers.createDealerUser(dealerId, {
        ...form.data,
        parentId: dealerId
      });
    };
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsx(DealerUserForm, { onChange: setForm }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-row justify-between gap-2", children: [
        /* @__PURE__ */ jsx(Button, { size: "sm", onPress: handleClose, variant: "tertiary", children: "Cancel" }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col sm:flex-row items-stretch sm:items-center gap-2", children: [
          /* @__PURE__ */ jsx(ErrorListChip, { errors: [...form?.errors || []] }),
          /* @__PURE__ */ jsx(
            SaveButton,
            {
              size: "sm",
              confirmColor: "primary",
              isDisabled: !form?.isValid,
              onPress: handleSave,
              isLoading: monitors.createDealerUser.executing
            }
          )
        ] })
      ] })
    ] });
  },
  {
    name: "DealerUserCreateModal",
    monitors: (services) => [services.dealers.createDealerUser]
  }
);
const DealerUserCreateModalWrapped = withModalController(({ dealerId }) => /* @__PURE__ */ jsx(DealerUserCreateModal, { dealerId }));
const DealerUserCreateView = ({ dealerId }) => {
  dealerId = Number(dealerId);
  return /* @__PURE__ */ jsx(
    DealerUserCreateModalWrapped,
    {
      isOpen: true,
      isDismissable: false,
      title: "New User",
      dealerId
    }
  );
};
const DealerUserUpdateModal = reactive.component(
  ({ services, data, monitors, dealerId, userId }) => {
    const navigate = useNavigate();
    const [form, setForm] = useState({});
    const { user } = data;
    useSuccessReactor(services.dealers.updateDealerUser, () => {
      navigate(RoutePaths().dealers.users(dealerId));
    });
    useSuccessReactor(services.dealers.deleteDealerUser, () => {
      navigate(RoutePaths().dealers.users(dealerId));
    });
    const handleClose = () => {
      navigate(RoutePaths().dealers.users(dealerId));
    };
    const handleDelete = () => {
      services.dealers.deleteDealerUser(dealerId, userId);
    };
    const handleSave = () => {
      services.dealers.updateDealerUser(dealerId, userId, {
        ...form.data,
        parentId: dealerId,
        userId
      });
    };
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      user?.id && /* @__PURE__ */ jsx(DealerUserForm, { user, onChange: setForm }),
      /* @__PURE__ */ jsx("div", { className: "flex flex-row justify-between gap-2", children: /* @__PURE__ */ jsx(
        ResourceActions,
        {
          onCancel: handleClose,
          onDelete: handleDelete,
          onSave: handleSave,
          isLoading: monitors.updateDealerUser.executing || monitors.deleteDealerUser.executing,
          isSaveEnabled: form?.isValid,
          errors: form?.errors ?? []
        }
      ) })
    ] });
  },
  {
    name: "DealerUserUpdateModal",
    sync: (services, { dealerId, userId }) => [
      {
        execute: () => services.dealers.getDealerUser(dealerId, userId),
        deps: [dealerId, userId]
      }
    ],
    queries: ({ userId }) => [
      {
        name: "user",
        defaultValue: {},
        execute: (db) => db.dealer_users.get(userId),
        deps: [userId]
      }
    ],
    monitors: (services) => [
      services.dealers.getDealerUser,
      services.dealers.updateDealerUser,
      services.dealers.deleteDealerUser
    ]
  }
);
const DealerUserUpdateModalWrapped = withModalController(({ dealerId, userId }) => /* @__PURE__ */ jsx(DealerUserUpdateModal, { dealerId, userId }));
const DealerUserUpdateView = ({ dealerId, userId }) => {
  dealerId = Number(dealerId);
  userId = Number(userId);
  return /* @__PURE__ */ jsx(
    DealerUserUpdateModalWrapped,
    {
      isOpen: true,
      isDismissable: false,
      title: "Update User",
      dealerId,
      userId
    }
  );
};
const columns$1 = [
  { propertyName: "id", name: "#", isSortable: true },
  { propertyName: "firstName", name: "First Name", isSortable: true },
  { propertyName: "lastName", name: "Last Name", isSortable: true },
  { propertyName: "homePhone", name: "Primary Phone", isSortable: true },
  { propertyName: "timezone", name: "Time Zone" }
];
const DealerUsersTable = ({ users = [], onSelect, onDelete }) => {
  const renderCell = useCallback(
    (row, columnKey) => {
      switch (columnKey) {
        case "id":
          return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx(Chip, { variant: "soft", color: "accent", size: "sm", children: row.id }),
            /* @__PURE__ */ jsx(InactiveChip, { isActive: row.isActive })
          ] });
        default:
          return row[columnKey];
      }
    },
    [onDelete]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerTable,
    {
      columns: columns$1,
      items: users,
      chipScope: "global",
      renderColumn: (column) => /* @__PURE__ */ jsx(
        Table.Column,
        {
          id: column.propertyName,
          isRowHeader: column.propertyName === "id",
          allowsSorting: column?.isSortable,
          children: column.name
        },
        column.propertyName
      ),
      renderRow: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.id, onAction: () => onSelect(row), children: columns$1.map((column) => /* @__PURE__ */ jsx(Table.Cell, { children: renderCell(row, column.propertyName) }, column.propertyName)) }, row.id)
    }
  );
};
const DealerUserForm = ({ user = {}, onChange }) => {
  return /* @__PURE__ */ jsxs(
    Uniformly,
    {
      className: "flex flex-col gap-2",
      onChange: (form) => onChange({
        ...form,
        data: {
          ...form.data,
          homePhone: form.data.homePhone === "" ? null : form.rawData.homePhone,
          timezone: form.data.timezone === "" ? null : form.data.timezone
        }
      }),
      children: [
        /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
          TextField,
          {
            name: "firstName",
            label: "First Name",
            value: user.firstName,
            validation: (x) => x.string().max(50).required("First Name is required")
          }
        ) }),
        /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
          TextField,
          {
            name: "lastName",
            label: "Last Name",
            value: user.lastName,
            validation: (x) => x.string().max(50).required("Last Name is required")
          }
        ) }),
        /* @__PURE__ */ jsx(FieldStatus, { important: true, children: /* @__PURE__ */ jsx(
          TextField,
          {
            name: "middleName",
            label: "Middle Name",
            value: user.middleName,
            validation: (x) => x.string().max(50).notRequired()
          }
        ) }),
        /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
          EmailField,
          {
            name: "email",
            label: "Email",
            value: user.email,
            required: true,
            requiredMessage: "Email is required",
            validationMessage: "Invalid email"
          }
        ) }),
        /* @__PURE__ */ jsx(FieldStatus, { required: true, children: /* @__PURE__ */ jsx(
          PhoneField,
          {
            name: "homePhone",
            label: "Primary Phone",
            placeholder: `Enter Primary Phone: ${masks.phone.placeholder}`,
            value: user.homePhone,
            mask: { mask: masks.phone.mask },
            validation: (x) => x.string().required("Primary Phone is required")
          }
        ) }),
        /* @__PURE__ */ jsx(FieldStatus, { important: true, children: /* @__PURE__ */ jsx(TimezonesLookup, { name: "timezone", label: "Timezone", value: user.timezone }) })
      ]
    }
  );
};
const DealerUsersFiltersDefinition = new Filters([
  new FilterInput({
    name: "parentId",
    title: "Parent",
    options: {
      searchType: "equals",
      scope: "custom",
      isVisible: false,
      isApplied: true
    }
  }),
  new FilterInput({
    name: "firstName",
    title: "First Name"
  }),
  new FilterInput({
    name: "lastName",
    title: "Last Name"
  }),
  new FilterInput({
    name: "homePhone",
    title: "Primary Phone"
  }),
  new FilterInput({
    name: "email",
    title: "Email"
  })
]);
const DEALER_RESOURCE = "Dealers";
const DealersRouter = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "/",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: DEALER_RESOURCE, operation: "view", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(DealersListView, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "create",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: DEALER_RESOURCE, operation: "create", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(DealerCreateView, {}) })
    }
  ),
  /* @__PURE__ */ jsxs(Route, { path: ":dealerId", element: /* @__PURE__ */ jsx(WithParams, { element: DealerDetailsView }), children: [
    /* @__PURE__ */ jsx(Route, { path: "details", element: /* @__PURE__ */ jsx(WithParams, { element: DealerUpdateView }) }),
    /* @__PURE__ */ jsx(Route, { path: "addresses", element: /* @__PURE__ */ jsx(WithParams, { element: DealerAddressesView }) }),
    /* @__PURE__ */ jsxs(
      Route,
      {
        path: "users",
        element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "DealerUsers", operation: "View", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(WithParams, { element: /* @__PURE__ */ jsx(DealerUserListView, {}) }) }),
        children: [
          /* @__PURE__ */ jsx(Route, { path: "create", element: /* @__PURE__ */ jsx(WithParams, { element: DealerUserCreateView }) }),
          /* @__PURE__ */ jsx(Route, { path: ":userId", element: /* @__PURE__ */ jsx(WithParams, { element: DealerUserUpdateView }) })
        ]
      }
    ),
    /* @__PURE__ */ jsx(Route, { path: "subDealers", element: /* @__PURE__ */ jsx(WithParams, { element: SubDealersListView }) }),
    /* @__PURE__ */ jsx(Route, { path: "notes", element: /* @__PURE__ */ jsx(WithParams, { element: DealerNotesView }) })
  ] })
] });
const CD_TABS = [
  {
    id: "overview",
    label: "Overview",
    resource: "Customers",
    operation: "view",
    apiKey: "AccountsAPI"
  },
  {
    id: "contact-info",
    label: "Contact info",
    resource: "Customers",
    operation: "view",
    apiKey: "AccountsAPI"
  },
  {
    id: "payment-profiles",
    label: "Payment profiles",
    resource: "PaymentProfiles",
    operation: "view",
    apiKey: "AccountsAPI"
  },
  { id: "ledger", label: "Ledger", resource: "Ledgers", operation: "view", apiKey: "BillingAPI" },
  {
    id: "payment-plans",
    label: "Payment plans",
    resource: "PaymentPlans",
    operation: "view",
    apiKey: "CollectionsAPI"
  },
  { id: "notes", label: "Notes", resource: "Customers", operation: "view", apiKey: "AccountsAPI" },
  {
    id: "notifications",
    label: "Messages",
    resource: "Notifications",
    operation: "view",
    apiKey: "NotificationsAPI"
  },
  {
    id: "contacts",
    label: "Contacts",
    resource: "EmergencyContacts",
    operation: "view",
    apiKey: "AccountsAPI"
  },
  { id: "device-activity", label: "Device activity" },
  {
    id: "linked-accounts",
    label: "Linked accounts",
    resource: "LinkedAccounts",
    operation: "view",
    apiKey: "AccountsAPI"
  }
];
function CustomerDetailsNavigationTabs({ active, onSelect }) {
  const { user } = useRuntimeProvider();
  const tabs = CD_TABS.filter(
    (x) => x.apiKey ? user.hasPolicy(x.apiKey, x.resource, x.operation) : true
  );
  return /* @__PURE__ */ jsx(Tabs, { variant: "secondary", selectedKey: active, onSelectionChange: (key) => onSelect(String(key)), children: /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsx(
    Tabs.List,
    {
      "aria-label": "Customer sections",
      className: "px-5 **:data-[slot=tabs-tab]:h-10 **:data-[slot=tabs-tab]:w-auto",
      children: tabs.map((tab) => /* @__PURE__ */ jsxs(Tabs.Tab, { id: tab.id, children: [
        tab.label,
        /* @__PURE__ */ jsx(Tabs.Indicator, {})
      ] }, tab.id))
    }
  ) }) });
}
const SUB_STATUS_TONE = {
  "Active": "success",
  "On Hold": "warn",
  "Pending Cancel": "warn",
  "Canceled": "danger",
  "None": "neutral"
};
const subscriptionTone = (status) => SUB_STATUS_TONE[status] ?? "neutral";
function CustomerHeader({ onOpenNotes }) {
  const customer = useCustomer();
  const { notes } = useAccountNotes();
  const pinnedCount = notes.filter((n) => n.pinned).length;
  return /* @__PURE__ */ jsx("div", { className: "bg-[var(--surface)] border-b border-[var(--border-default-c)] px-7 py-4", children: /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-4", children: [
    /* @__PURE__ */ jsx(AccountAvatar, { name: customer?.fullName, size: 44 }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2.5 mb-2 flex-wrap", children: [
        /* @__PURE__ */ jsx("h1", { className: "m-0 text-xl font-bold tracking-tight text-[var(--foreground)] leading-tight", children: customer?.fullName ?? "—" }),
        /* @__PURE__ */ jsx(Pill, { tone: subscriptionTone(customer?.subscriptionStatus), children: customer?.subscriptionStatus ?? "None" }),
        /* @__PURE__ */ jsx(InactiveChip, { isActive: customer?.isActive }),
        customer?.id != null && /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5 text-xs text-[var(--fg-3)] font-mono", children: [
          customer.id,
          /* @__PURE__ */ jsx(CopyToClipboard, { text: String(customer.id) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-x-5 gap-y-1", children: [
        customer?.dealerName && /* @__PURE__ */ jsx(ContactChip, { icon: "ri-store-2-line", label: customer.dealerName }),
        /* @__PURE__ */ jsx(InactiveChip, { isActive: customer?.dealerIsActive, children: "Dealer inactive" }),
        customer?.email && /* @__PURE__ */ jsx(ContactChip, { icon: "ri-mail-line", label: customer.email }),
        customer?.homePhone && /* @__PURE__ */ jsx(
          ContactChip,
          {
            icon: "ri-phone-line",
            label: "Home · " + formatPhone(customer.homePhone)
          }
        ),
        customer?.mobilePhone && /* @__PURE__ */ jsx(
          ContactChip,
          {
            icon: "ri-smartphone-line",
            label: "Mobile · " + formatPhone(customer.mobilePhone)
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "flex gap-2 items-center flex-none", children: /* @__PURE__ */ jsxs(
      Button,
      {
        isIconOnly: true,
        className: "relative",
        variant: "secondary",
        "aria-label": pinnedCount > 0 ? `Notes (${pinnedCount} pinned)` : "Notes",
        title: "Notes",
        onPress: onOpenNotes,
        children: [
          /* @__PURE__ */ jsx("i", { className: "ri-sticky-note-line" }),
          pinnedCount > 0 && // Over the button's own corner rather than beside a label.
          /* @__PURE__ */ jsx("span", { className: "absolute -top-1 -right-1 inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-bold text-white", children: pinnedCount })
        ]
      }
    ) })
  ] }) });
}
const CustomerLayout = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const customer = useCustomer();
  const [notesDrawer, NotesDrawer] = useDrawerController(AccountNotesDrawer, {
    size: "sm",
    // Required: withDrawerController defaults isOpen to true and the hook forwards it as given,
    // so leaving it out opens the drawer on first paint.
    isOpen: false
  });
  const activeTab = CD_TABS.find((t) => pathname.includes(`/${t.id}`))?.id ?? "overview";
  return (
    // A fixed-height shell: the header block stays put and the tab below is the scroll region, so
    // a tab that wants to fill the remaining height and scroll internally (the device activity
    // grid) has a definite height to fill, while a long tab (the overview) scrolls on its own.
    /* @__PURE__ */ jsxs("div", { className: "relative -m-4 flex h-full min-h-0 flex-col", children: [
      /* @__PURE__ */ jsx(
        Breadcrumb,
        {
          className: "pb-3",
          items: [
            { label: "Accounts" },
            { label: "Customers", onPress: () => navigate(RoutePaths().customers.list()) },
            { label: customer?.fullName ?? "" }
          ]
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "z-10 shrink-0 bg-[var(--surface)]", children: [
        /* @__PURE__ */ jsx(CustomerHeader, { onOpenNotes: () => notesDrawer.open() }),
        /* @__PURE__ */ jsx(
          CustomerDetailsNavigationTabs,
          {
            active: activeTab,
            onSelect: (tab) => navigate(RoutePaths().customers.tab(customer?.id, tab))
          }
        )
      ] }),
      /* @__PURE__ */ jsx("div", { className: "flex min-h-0 flex-1 flex-col overflow-y-auto", children: /* @__PURE__ */ jsx(Outlet, {}) }),
      /* @__PURE__ */ jsx(NotesDrawer, { classNames: { body: "p-0" } })
    ] })
  );
};
const BaseCustomerLayout = ({ customerId }) => /* @__PURE__ */ jsx(CustomerProvider, { customerId, children: /* @__PURE__ */ jsx(AccountNotesController, { accountId: customerId, children: /* @__PURE__ */ jsx(CustomerLayout, {}) }) });
const detailPath = (accountId, accountType) => {
  const paths = RoutePaths();
  switch (accountType) {
    case AccountType$1.Customer:
    case "customer":
      return paths.customers.details(accountId);
    case AccountType$1.Dealer:
    case "dealer":
      return paths.dealers.details(accountId);
    default:
      return null;
  }
};
const AccountLink$1 = ({ accountId, accountType, children, className }) => {
  const label = children ?? accountId;
  const to = Number.isFinite(accountId) && accountId > 0 ? detailPath(accountId, accountType) : null;
  if (!to) return /* @__PURE__ */ jsx("span", { className, children: label });
  return /* @__PURE__ */ jsxs(Link, { to, className: `inline-flex items-center gap-0.5 hover:underline ${className ?? ""}`.trim(), children: [
    label,
    /* @__PURE__ */ jsx(
      "i",
      {
        className: "ri-external-link-line relative -top-[0.2em] text-[0.9em] leading-none",
        "aria-hidden": "true"
      }
    )
  ] });
};
const EMPTY = { parent: null, children: [] };
const errorMessage = (error, fallback) => error instanceof Error && error.message ? error.message : fallback;
const useLinkedAccounts = (accountId, { isQuiet = false } = {}) => {
  const [links, setLinks] = useState(EMPTY);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const latestRequest = useRef(0);
  const load = useCallback(async () => {
    const request = ++latestRequest.current;
    setLinks(EMPTY);
    if (!accountId) return;
    setIsLoading(true);
    try {
      const result = await getCustomersClient().getLinkedAccounts(accountId);
      if (request === latestRequest.current) setLinks({ ...EMPTY, ...result });
    } catch (error) {
      if (request === latestRequest.current && !isQuiet)
        alerts.error(errorMessage(error, "Could not load the linked accounts."));
    } finally {
      if (request === latestRequest.current) setIsLoading(false);
    }
  }, [accountId, isQuiet]);
  useEffect(() => {
    void load();
  }, [load]);
  const save = useCallback(
    async (action, success, failure) => {
      setIsSaving(true);
      try {
        await action();
        alerts.success(success);
        await load();
        return true;
      } catch (error) {
        alerts.error(errorMessage(error, failure));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [load]
  );
  const link = useCallback(
    (childId) => save(
      () => getCustomersClient().linkCustomer(Number(accountId), childId),
      "Customer linked",
      "Could not link the customer."
    ),
    [accountId, save]
  );
  const unlink = useCallback(
    (linkedId) => save(
      () => getCustomersClient().unlinkCustomer(Number(accountId), linkedId),
      "Customer unlinked",
      "Could not unlink the customer."
    ),
    [accountId, save]
  );
  return { links, isLoading, isSaving, link, unlink };
};
const customerName = (customer) => customer.fullName || [customer.firstName, customer.lastName].filter(Boolean).join(" ");
const linkClass = "font-semibold text-[var(--accent)] hover:underline";
const LinkedAccountNotice$1 = ({ accountId, className }) => {
  const { links } = useLinkedAccounts(accountId, { isQuiet: true });
  const { parent, children } = links;
  if (!accountId || !parent && children.length === 0) return null;
  return /* @__PURE__ */ jsxs(Alert, { status: "accent", className, children: [
    /* @__PURE__ */ jsx(Alert.Indicator, {}),
    /* @__PURE__ */ jsxs(Alert.Content, { children: [
      /* @__PURE__ */ jsx(Alert.Title, { children: "Linked account" }),
      /* @__PURE__ */ jsx(Alert.Description, { children: parent ? /* @__PURE__ */ jsxs(Fragment, { children: [
        "This is a child account of",
        " ",
        /* @__PURE__ */ jsxs(AccountLink$1, { accountId: parent.id, accountType: "customer", className: linkClass, children: [
          customerName(parent),
          " (#",
          parent.id,
          ")"
        ] }),
        "."
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        "This is a parent account with ",
        children.length,
        " linked",
        " ",
        children.length === 1 ? "customer" : "customers",
        ".",
        " ",
        /* @__PURE__ */ jsx(Link, { to: RoutePaths().customers.linkedAccounts(accountId), className: linkClass, children: "View linked accounts" })
      ] }) })
    ] })
  ] });
};
const columns = [
  { key: "account", label: "Account" },
  { key: "name", label: "Name" },
  { key: "status", label: "Status" },
  { key: "actions", label: "", align: "end" }
];
const describe = (parent, children) => {
  if (parent) return "This customer is a child account. Unlinking makes it a customer of its own.";
  if (children.length === 0) return "No customers are linked to this account.";
  return `${children.length} linked ${children.length === 1 ? "customer" : "customers"}.`;
};
const LinkedAccountsPanel = ({ links, isLoading, isSaving, onLink, onUnlink }) => {
  const { parent, children } = links;
  const rows = parent ? [parent] : children;
  const renderCell = (row, key) => {
    switch (key) {
      case "account":
        return /* @__PURE__ */ jsx(AccountLink$1, { accountId: row.id, accountType: "customer" });
      case "name":
        return parent ? `${customerName(row)} (parent)` : customerName(row);
      case "status": {
        const status = row.subscriptionStatus ?? "None";
        return /* @__PURE__ */ jsx(Pill, { tone: subscriptionTone(status), children: status });
      }
      case "actions":
        return /* @__PURE__ */ jsx(
          ConfirmableButton,
          {
            size: "sm",
            variant: "danger",
            iconClass: "ri-link-unlink",
            label: "Unlink",
            isDisabled: isSaving,
            onPress: () => onUnlink(row.id)
          }
        );
      default:
        return null;
    }
  };
  return /* @__PURE__ */ jsx("div", { className: "px-7 py-6", children: /* @__PURE__ */ jsxs(Fieldset, { children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-start justify-between gap-4", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Linked accounts" }),
        /* @__PURE__ */ jsx(Description, { children: isLoading ? "Loading…" : describe(parent, children) })
      ] }),
      /* @__PURE__ */ jsx(RequirePolicy, { resource: "LinkedAccounts", operation: "update", children: !parent && /* @__PURE__ */ jsxs(Button, { size: "sm", variant: "secondary", isDisabled: isLoading, onPress: onLink, children: [
        /* @__PURE__ */ jsx(RiLinkM, { size: "16" }),
        /* @__PURE__ */ jsx("span", { className: "relative top-px", children: "Link customer" })
      ] }) })
    ] }),
    rows.length > 0 && /* @__PURE__ */ jsx(Table, { className: "w-full text-[13px]", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Linked accounts", children: [
      /* @__PURE__ */ jsx(Table.Header, { columns, children: (column) => /* @__PURE__ */ jsx(
        Table.Column,
        {
          id: column.key,
          align: column.align,
          isRowHeader: column.key === "account",
          children: column.label
        },
        column.key
      ) }),
      /* @__PURE__ */ jsx(Table.Body, { items: rows, children: (row) => /* @__PURE__ */ jsx(Table.Row, { id: row.id, children: columns.map((column) => /* @__PURE__ */ jsx(Table.Cell, { align: column.align, children: renderCell(row, column.key) }, `${row.id}-${column.key}`)) }, row.id) })
    ] }) }) })
  ] }) });
};
const LinkCustomerForm = ({ parentId, isSaving, onLink, onSuccess, onClose }) => {
  const [childId, setChildId] = useState(null);
  const canLink = !!childId && childId !== Number(parentId) && !isSaving;
  const link = async () => {
    if (await onLink(childId)) onSuccess?.();
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
    /* @__PURE__ */ jsx(CustomerSearchComboBox$1, { label: "Customer", onSelect: (id) => setChildId(id) }),
    /* @__PURE__ */ jsx(Description, { children: "The customer becomes a child of this account. A customer that already has a parent, or has children of its own, cannot be linked." }),
    /* @__PURE__ */ jsxs(DrawerActions, { children: [
      /* @__PURE__ */ jsx(Button, { variant: "secondary", onPress: onClose, children: "Cancel" }),
      /* @__PURE__ */ jsx(
        SaveButton,
        {
          label: "Link customer",
          iconClass: "ri-link",
          isDisabled: !canLink,
          isLoading: isSaving,
          onPress: link
        }
      )
    ] })
  ] });
};
const LinkCustomerDrawer = withDrawerController(LinkCustomerForm);
const LinkedAccountsController = () => {
  const customer = useCustomer();
  const { links, isLoading, isSaving, link, unlink } = useLinkedAccounts(customer?.id);
  const [linkDrawer, LinkDrawer] = useDrawerController(LinkCustomerDrawer, {
    size: "md",
    // Required: withDrawerController defaults isOpen to true.
    isOpen: false,
    headerContent: () => "Link Customer"
  });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      LinkedAccountsPanel,
      {
        links,
        isLoading,
        isSaving,
        onLink: () => linkDrawer.open(),
        onUnlink: unlink
      }
    ),
    /* @__PURE__ */ jsx(LinkDrawer, { parentId: customer?.id, isSaving, onLink: link })
  ] });
};
const CustomerOverviewPanel = ({ customer }) => {
  const SubscriptionWidget = useComponent("subscriptions.subscription-widget");
  const LedgerSummaryWidget = useComponent("billing.ledger-summary-widget");
  const CustomerDevices = useComponent("devices.customer-devices");
  return /* @__PURE__ */ jsxs("div", { className: "px-7 py-6 flex flex-col gap-4", children: [
    /* @__PURE__ */ jsx(LinkedAccountNotice$1, { accountId: customer?.id }),
    /* @__PURE__ */ jsx(PinnedNotesWidget, {}),
    /* @__PURE__ */ jsx(LedgerSummaryWidget, { customerId: customer?.id }),
    /* @__PURE__ */ jsx(SubscriptionWidget, { accountId: customer?.id }),
    /* @__PURE__ */ jsx(CustomerDevices, { customerId: customer?.id })
  ] });
};
const CustomerOverviewController = () => {
  const customer = useCustomer();
  return /* @__PURE__ */ jsx(CustomerOverviewPanel, { customer });
};
const CAPABILITIES = [
  { key: "mobilePhoneAlertsEnabled", label: "SMS", icon: "ri-message-2-line" },
  { key: "emailAlertsEnabled", label: "Email", icon: "ri-mail-line" },
  { key: "homePhoneAlertsEnabled", label: "Home", icon: "ri-phone-line" }
];
const contactName = (contact) => contact.fullName || [contact.firstName, contact.lastName].filter(Boolean).join(" ");
const primaryPhone = (contact) => contact.mobilePhone || contact.homePhone || contact.workPhone;
const Meta$1 = ({ label, children }) => /* @__PURE__ */ jsxs("span", { children: [
  /* @__PURE__ */ jsxs("span", { className: "font-medium text-[var(--muted)]", children: [
    label,
    ": "
  ] }),
  children
] });
const ContactCard = ({ contact, priority, onEdit, onDelete }) => /* @__PURE__ */ jsxs(ItemCard, { variant: "outline", className: "bg-[var(--surface)]", children: [
  /* @__PURE__ */ jsx(
    ItemCard.Icon,
    {
      className: priority === 1 ? "bg-[var(--accent)] text-white" : "bg-[var(--border-subtle)] text-[var(--muted)]",
      children: /* @__PURE__ */ jsx("span", { className: "text-sm font-bold", children: priority })
    }
  ),
  /* @__PURE__ */ jsxs(ItemCard.Content, { children: [
    /* @__PURE__ */ jsxs(ItemCard.Title, { className: "flex flex-wrap items-center gap-2", children: [
      contactName(contact),
      /* @__PURE__ */ jsx(InactiveChip, { isActive: contact.isActive }),
      contact.relationshipTypeName && /* @__PURE__ */ jsx(Chip, { size: "sm", variant: "soft", children: contact.relationshipTypeName }),
      CAPABILITIES.filter((c) => contact[c.key]).map((c) => /* @__PURE__ */ jsxs(Chip, { size: "sm", color: "accent", variant: "soft", children: [
        /* @__PURE__ */ jsx("i", { className: `${c.icon} mr-1 text-[11px]` }),
        c.label
      ] }, c.key))
    ] }),
    /* @__PURE__ */ jsxs(ItemCard.Description, { className: "flex flex-wrap gap-x-5 gap-y-1", children: [
      primaryPhone(contact) && /* @__PURE__ */ jsx(Meta$1, { label: "Phone", children: formatPhone(primaryPhone(contact)) }),
      contact.email && /* @__PURE__ */ jsx(Meta$1, { label: "Email", children: contact.email }),
      contact.timeZone && /* @__PURE__ */ jsx(Meta$1, { label: "Time zone", children: contact.timeZone })
    ] })
  ] }),
  /* @__PURE__ */ jsxs(ItemCard.Action, { className: "flex items-center gap-1.5", children: [
    /* @__PURE__ */ jsx(ModifyButton, { isIconOnly: true, size: "sm", "aria-label": "Edit", onPress: onEdit }),
    /* @__PURE__ */ jsx(DeleteButton, { isIconOnly: true, size: "sm", onPress: onDelete })
  ] })
] });
const CustomerContactForm = ({ contact, values, onChange }) => /* @__PURE__ */ jsxs(Uniformly, { onChange, throttle: 0, children: [
  /* @__PURE__ */ jsxs(Fieldset, { children: [
    /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Identity" }),
    /* @__PURE__ */ jsx(Description, { children: "The contact's name, relationship to the subscriber, and their timezone." }),
    /* @__PURE__ */ jsxs(Grid, { cols: 2, className: "gap-3.5", children: [
      /* @__PURE__ */ jsx(
        SelectField,
        {
          name: "relationshipTypeId",
          label: "Relationship",
          value: contact?.relationshipTypeId ?? 1,
          items: RelationshipTypeOptions.map((o) => ({ id: o.id, name: o.name })),
          children: (item) => /* @__PURE__ */ jsx(ListBox.Item, { id: item.id, textValue: item.name, children: item.name }, item.id)
        }
      ),
      /* @__PURE__ */ jsx(TimezonesLookup, { name: "timeZone", label: "Timezone", value: contact?.timeZone ?? null })
    ] }),
    /* @__PURE__ */ jsxs(Grid, { cols: 3, children: [
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "firstName",
          label: "First name",
          isRequired: true,
          value: contact?.firstName ?? "",
          validation: (v) => v.string().max(150, "First name cannot exceed 150 characters").required("First name is required")
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "lastName",
          label: "Last name",
          isRequired: true,
          value: contact?.lastName ?? "",
          validation: (v) => v.string().max(150, "Last name cannot exceed 150 characters").required("Last name is required")
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          name: "middleName",
          label: "Middle name",
          value: contact?.middleName ?? "",
          validation: (v) => v.string().max(150, "Middle name cannot exceed 150 characters")
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsxs(Fieldset, { children: [
    /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Contact methods" }),
    /* @__PURE__ */ jsx(Description, { children: "How the answering service reaches this contact. At least one of mobile or home phone is required." }),
    /* @__PURE__ */ jsxs(Grid, { cols: 3, className: "gap-3.5", children: [
      /* @__PURE__ */ jsx(
        PhoneField,
        {
          name: "mobilePhone",
          label: "Mobile phone",
          isRequired: !values?.homePhone,
          value: contact?.mobilePhone ?? "",
          validation: (x) => x.string().when("$homePhone", {
            is: (home) => !home,
            then: (schema) => schema.required("Mobile phone is required when there is no home phone"),
            otherwise: (schema) => schema.notRequired()
          })
        }
      ),
      /* @__PURE__ */ jsx(
        PhoneField,
        {
          name: "homePhone",
          label: "Home phone",
          isRequired: !values?.mobilePhone,
          value: contact?.homePhone ?? "",
          validation: (x) => x.string().when("$mobilePhone", {
            is: (mobile) => !mobile,
            then: (schema) => schema.required("Home phone is required when there is no mobile phone"),
            otherwise: (schema) => schema.notRequired()
          })
        }
      ),
      /* @__PURE__ */ jsx(PhoneField, { name: "workPhone", label: "Work phone", value: contact?.workPhone ?? "" }),
      /* @__PURE__ */ jsx(Grid.Item, { colSpan: 3, children: /* @__PURE__ */ jsx(
        EmailField,
        {
          name: "email",
          label: "Email address",
          maxLength: 100,
          value: contact?.email ?? ""
        }
      ) })
    ] })
  ] }),
  /* @__PURE__ */ jsxs(Fieldset, { children: [
    /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Alerts" }),
    /* @__PURE__ */ jsx(Description, { children: "Alert opt-ins per channel for this contact." }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2.5", children: [
      /* @__PURE__ */ jsx(
        CheckboxField,
        {
          name: "mobilePhoneAlertsEnabled",
          value: contact?.mobilePhoneAlertsEnabled ?? false,
          children: "SMS alerts"
        }
      ),
      /* @__PURE__ */ jsx(CheckboxField, { name: "emailAlertsEnabled", value: contact?.emailAlertsEnabled ?? false, children: "Email alerts" }),
      /* @__PURE__ */ jsx(
        CheckboxField,
        {
          name: "homePhoneAlertsEnabled",
          value: contact?.homePhoneAlertsEnabled ?? false,
          children: "Home phone alerts"
        }
      )
    ] })
  ] })
] });
const NEW$1 = "new";
const EditPanel$1 = ({ label, children }) => /* @__PURE__ */ jsxs("div", { className: "mt-2 overflow-hidden rounded-[10px] border border-[var(--accent)] bg-[var(--surface)]", children: [
  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2.5 border-b border-[var(--accent)] bg-[var(--pill-info-bg)] px-4 py-2.5", children: [
    /* @__PURE__ */ jsx("span", { className: "flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[var(--accent)] text-white", children: /* @__PURE__ */ jsx("i", { className: "ri-edit-line text-[12px]" }) }),
    /* @__PURE__ */ jsx("span", { className: "text-[13.5px] font-semibold text-[var(--accent)]", children: label })
  ] }),
  /* @__PURE__ */ jsx("div", { className: "px-4 py-4", children })
] });
const CustomerContactsPanel$1 = ({ contacts = [], isSaving, ref, onSave, onDelete }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const open = (target) => {
    setEditing(target);
    setForm(null);
  };
  const close = () => open(null);
  useImperativeHandle(ref, () => ({ open, close }));
  const contact = contacts.find((c) => c.id === editing) ?? null;
  const canSave = !!form?.isValid && !isSaving;
  const save = () => onSave(form.data, contact);
  const editor = /* @__PURE__ */ jsxs(
    EditPanel$1,
    {
      label: editing === NEW$1 ? "New contact" : `Editing ${contactName(contact ?? {}) || "contact"}`,
      children: [
        /* @__PURE__ */ jsx(CustomerContactForm, { contact, values: form?.data, onChange: setForm }, editing),
        /* @__PURE__ */ jsx("div", { className: "mt-3", children: /* @__PURE__ */ jsx(
          ResourceActions,
          {
            saveLabel: editing === NEW$1 ? "Add contact" : "Save contact",
            saveSize: "md",
            cancelSize: "md",
            onSave: save,
            isLoading: isSaving,
            isSaveEnabled: canSave,
            errors: form?.errors ?? [],
            onCancel: close
          }
        ) })
      ]
    }
  );
  return /* @__PURE__ */ jsx("div", { className: "px-7 py-6", children: /* @__PURE__ */ jsxs(Fieldset, { children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-start justify-between gap-4", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Emergency contacts" }),
        /* @__PURE__ */ jsx(Description, { children: `${contacts.length} ${contacts.length === 1 ? "contact" : "contacts"} on file · the answering service calls in priority order.` })
      ] }),
      /* @__PURE__ */ jsxs(Button, { size: "sm", variant: "secondary", className: "text-success", onPress: () => open(NEW$1), children: [
        /* @__PURE__ */ jsx(RiAddLine, { size: "16" }),
        /* @__PURE__ */ jsx("span", { className: "relative top-px", children: "Add Contact" })
      ] })
    ] }),
    contacts.length === 0 && editing !== NEW$1 ? /* @__PURE__ */ jsx("div", { className: "px-5 py-12 text-center text-[13px] text-[var(--fg-3)]", children: "No emergency contacts on file." }) : /* @__PURE__ */ jsxs(
      DisclosureGroup,
      {
        className: "flex flex-col gap-2.5",
        expandedKeys: new Set(editing != null ? [editing] : []),
        children: [
          contacts.map((item, index) => /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx(
              ContactCard,
              {
                contact: item,
                priority: index + 1,
                onEdit: () => open(item.id),
                onDelete: () => onDelete(item.id)
              }
            ),
            /* @__PURE__ */ jsx(Disclosure, { id: item.id, children: /* @__PURE__ */ jsx(Disclosure.Content, { children: /* @__PURE__ */ jsx(Disclosure.Body, { children: editing === item.id && editor }) }) })
          ] }, item.id)),
          /* @__PURE__ */ jsx("div", { className: "mt-2", children: /* @__PURE__ */ jsx(Disclosure, { id: NEW$1, children: /* @__PURE__ */ jsx(Disclosure.Content, { children: /* @__PURE__ */ jsx(Disclosure.Body, { children: editing === NEW$1 && editor }) }) }) })
        ]
      }
    )
  ] }) });
};
const CustomerContactsReactive = reactive.component(
  ({ data, services, monitors, customerId }) => {
    const panel = useRef(null);
    const closeEditor = () => panel.current?.close();
    useSuccessReactor(services.emergencyContacts.createContact, closeEditor);
    useSuccessReactor(services.emergencyContacts.updateContact, closeEditor);
    const save = (values, contact) => contact ? services.emergencyContacts.updateContact(contact.id, {
      ...contact,
      ...values,
      customerId: Number(customerId)
    }) : services.emergencyContacts.createContact({
      ...values,
      customerId: Number(customerId)
    });
    const remove = (id) => services.emergencyContacts.deleteContact(id, customerId);
    return /* @__PURE__ */ jsx(
      CustomerContactsPanel$1,
      {
        ref: panel,
        contacts: data.contacts ?? [],
        isSaving: monitors.createContact.executing || monitors.updateContact.executing,
        onSave: save,
        onDelete: remove
      }
    );
  },
  {
    name: "CustomerContactsController",
    sync: (services, { customerId }) => [
      {
        execute: () => customerId && services.emergencyContacts.getContacts(Number(customerId)),
        deps: [customerId]
      }
    ],
    queries: ({ customerId }) => [
      {
        name: "contacts",
        defaultValue: [],
        execute: (db) => customerId ? db.emergency_contacts.where("customerId").equals(Number(customerId)).toArray() : [],
        deps: [customerId]
      }
    ],
    monitors: (services) => [
      services.emergencyContacts.createContact,
      services.emergencyContacts.updateContact,
      services.emergencyContacts.deleteContact
    ]
  }
);
const CustomerContactsController = () => {
  const customer = useCustomer();
  return /* @__PURE__ */ jsx(CustomerContactsReactive, { customerId: customer?.id });
};
const Meta = ({ label, children }) => /* @__PURE__ */ jsxs("span", { children: [
  /* @__PURE__ */ jsxs("span", { className: "font-medium text-[var(--muted)]", children: [
    label,
    ": "
  ] }),
  children
] });
const PaymentPlanCount = ({ count }) => {
  const label = count === 1 ? "Payment plan" : "Payment plans";
  return /* @__PURE__ */ jsxs(Chip, { size: "sm", variant: "soft", "aria-label": `${count} ${label.toLowerCase()}`, children: [
    /* @__PURE__ */ jsx("span", { className: "mr-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-semibold leading-none text-white", children: count }),
    label
  ] });
};
const borderClass = (profile) => {
  if (profile.isFrozen) return "!border-[var(--pill-warning-fg)]";
  if (profile.assignedToSubscription) return "!border-[var(--pill-success-fg)]";
  return "";
};
const deleteBlockedReason = (profile) => {
  const reasons = [];
  if (profile.assignedToSubscription)
    reasons.push("A subscription uses this profile. Assign another profile to it first.");
  if (profile.paymentPlanCount > 0) {
    const plans = profile.paymentPlanCount === 1 ? "1 open payment plan uses" : `${profile.paymentPlanCount} open payment plans use`;
    reasons.push(`${plans} this profile. Move them to another profile first.`);
  }
  return reasons.length > 0 ? reasons.join(" ") : null;
};
const BlockedDelete = ({ reason }) => /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
  /* @__PURE__ */ jsx(Tooltip.Trigger, { children: /* @__PURE__ */ jsx("span", { tabIndex: 0, "aria-label": `Delete unavailable. ${reason}`, children: /* @__PURE__ */ jsx(DeleteButton, { isIconOnly: true, size: "sm", isDisabled: true }) }) }),
  /* @__PURE__ */ jsx(Tooltip.Content, { children: /* @__PURE__ */ jsx("div", { className: "max-w-64 px-1 py-1 text-xs", children: reason }) })
] });
const ProfileCard = ({ profile, onEdit, onDelete }) => {
  const blockedReason = deleteBlockedReason(profile);
  const type = PaymentProfileTypeOptions.find((o) => o.value === profile.profileType) ?? PaymentProfileTypeOptions[0];
  const billingName = [profile.firstName, profile.lastName].filter(Boolean).join(" ");
  const { billingAddress } = profile;
  return /* @__PURE__ */ jsxs(ItemCard, { variant: "outline", className: `bg-[var(--surface)] ${borderClass(profile)}`, children: [
    /* @__PURE__ */ jsx(ItemCard.Icon, { children: /* @__PURE__ */ jsx("i", { className: `${type.icon} text-lg` }) }),
    /* @__PURE__ */ jsxs(ItemCard.Content, { children: [
      /* @__PURE__ */ jsxs(ItemCard.Title, { className: "flex flex-wrap items-center gap-2", children: [
        profile.name,
        profile.assignedToSubscription && /* @__PURE__ */ jsxs(Chip, { size: "sm", color: "success", variant: "soft", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-fill mr-1 text-[10px]" }),
          "Subscription"
        ] }),
        profile.isFrozen && /* @__PURE__ */ jsxs(Chip, { size: "sm", color: "warning", variant: "soft", children: [
          /* @__PURE__ */ jsx("i", { className: "ri-snowflake-line mr-1 text-[10px]" }),
          "Frozen"
        ] }),
        profile.paymentPlanCount > 0 && /* @__PURE__ */ jsx(PaymentPlanCount, { count: profile.paymentPlanCount }),
        /* @__PURE__ */ jsxs(Chip, { size: "sm", variant: "soft", children: [
          /* @__PURE__ */ jsx("i", { className: `${type.icon} mr-1 text-[11px]` }),
          type.label
        ] }),
        profile.cardNumberMasked && /* @__PURE__ */ jsx("span", { className: "font-mono text-xs text-[var(--fg-3)]", children: profile.cardNumberMasked })
      ] }),
      /* @__PURE__ */ jsxs(ItemCard.Description, { className: "flex flex-wrap gap-x-5 gap-y-1", children: [
        (billingName || profile.phoneNumber) && /* @__PURE__ */ jsx(Meta, { label: "Billing", children: [billingName, profile.phoneNumber && formatPhone(profile.phoneNumber)].filter(Boolean).join(" · ") }),
        billingAddress?.city && /* @__PURE__ */ jsx(Meta, { label: "Address", children: [billingAddress.city, billingAddress.state].filter(Boolean).join(", ") }),
        profile.createdOnUtc && /* @__PURE__ */ jsxs(Meta, { label: "Added", children: [
          parseAndFormatDate(profile.createdOnUtc),
          profile.createdBy ? ` by ${profile.createdBy}` : ""
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxs(ItemCard.Action, { className: "flex items-center gap-1.5", children: [
      /* @__PURE__ */ jsx(RequirePolicy, { resource: "PaymentProfiles", operation: "update", children: /* @__PURE__ */ jsx(ModifyButton, { isIconOnly: true, size: "sm", "aria-label": "Edit", onPress: onEdit }) }),
      /* @__PURE__ */ jsx(RequirePolicy, { resource: "PaymentProfiles", operation: "delete", children: blockedReason ? /* @__PURE__ */ jsx(BlockedDelete, { reason: blockedReason }) : /* @__PURE__ */ jsx(DeleteButton, { isIconOnly: true, size: "sm", onPress: onDelete }) })
    ] })
  ] });
};
const PHONE_FIELDS = [
  { key: "homePhone", type: "Home" },
  { key: "workPhone", type: "Work" },
  { key: "mobilePhone", type: "Mobile" }
];
const toPhoneValue = (phone) => {
  const raw = String(phone ?? "").trim();
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  if (raw.startsWith("+") || digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return `+1${digits}`;
};
const toPhoneOptions = (customer) => PHONE_FIELDS.filter(({ key }) => customer?.[key]?.trim()).map(({ key, type }) => ({
  key,
  type,
  value: toPhoneValue(customer[key]),
  display: formatPhone(customer[key])
}));
const toBillingAddressValue = (address) => ({
  address1: address?.address1 ?? "",
  address2: address?.address2 ?? "",
  city: address?.city ?? "",
  state: address?.state ?? "",
  postalCode: address?.postalCode ?? "",
  country: address?.country || "US"
});
const formatAddressLine = (address) => [
  address.address1,
  address.address2,
  address.city,
  [address.state, address.postalCode].filter(Boolean).join(" ")
].map((part) => part?.trim()).filter(Boolean).join(", ");
const toAddressOptions = (addresses) => {
  const seen = /* @__PURE__ */ new Set();
  return [...addresses ?? []].filter((address) => !address.isDeleted).sort((a, b) => a.type - b.type || a.id - b.id).map((address) => ({
    key: String(address.id),
    type: AddressTypeDisplay[address.type] ?? "Address",
    value: toBillingAddressValue(address),
    display: formatAddressLine(address)
  })).filter((option) => {
    const identity = `${option.type}|${option.display}`;
    if (!option.display || seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
};
const ContactPickerMenu = ({ options, onSelect, label, "aria-label": ariaLabel }) => {
  if (!options?.length) return null;
  const select = (key) => {
    const option = options.find((item) => item.key === String(key));
    if (option) onSelect?.(option.value);
  };
  return /* @__PURE__ */ jsxs(Dropdown, { children: [
    /* @__PURE__ */ jsxs(Button, { size: "sm", variant: "ghost", "aria-label": ariaLabel, className: "h-6 gap-1 px-1.5 text-xs", children: [
      /* @__PURE__ */ jsx("i", { className: "ri-contacts-book-line", "aria-hidden": "true" }),
      label,
      /* @__PURE__ */ jsx("i", { className: "ri-arrow-down-s-line", "aria-hidden": "true" })
    ] }),
    /* @__PURE__ */ jsx(Dropdown.Popover, { placement: "bottom end", children: /* @__PURE__ */ jsx(Dropdown.Menu, { items: options, onAction: select, children: (option) => /* @__PURE__ */ jsx(
      Dropdown.Item,
      {
        id: option.key,
        textValue: `${option.type}: ${option.display}`,
        children: /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col", children: [
          /* @__PURE__ */ jsx(Label, { children: option.type }),
          /* @__PURE__ */ jsx(Description, { children: option.display })
        ] })
      },
      option.key
    ) }) })
  ] });
};
const PhonePickerMenu = ({ phones, onSelect, label = "Use existing" }) => /* @__PURE__ */ jsx(
  ContactPickerMenu,
  {
    options: phones,
    onSelect,
    label,
    "aria-label": `${label} phone`
  }
);
const AddressPickerMenu = ({ addresses, onSelect, label = "Use existing" }) => {
  const options = useMemo(() => toAddressOptions(addresses), [addresses]);
  return /* @__PURE__ */ jsx(
    ContactPickerMenu,
    {
      options,
      onSelect,
      label,
      "aria-label": `${label} address`
    }
  );
};
const EmailFillButton = ({ email, onSelect, children = "Use customer email" }) => {
  if (!email?.trim()) return null;
  return /* @__PURE__ */ jsxs(
    Button,
    {
      size: "sm",
      variant: "ghost",
      className: "h-6 gap-1 px-1.5 text-xs",
      onPress: () => onSelect?.(email),
      children: [
        /* @__PURE__ */ jsx("i", { className: "ri-mail-line", "aria-hidden": "true" }),
        children
      ]
    }
  );
};
const CustomerContactsContext = createContext(null);
const useCustomerContacts = () => useContext(CustomerContactsContext);
const CustomerAddressesSource = reactive.component(
  ({ data, customer, children }) => {
    const contacts = useMemo(
      () => customer ? { customer, addresses: data.addresses } : null,
      [customer, data.addresses]
    );
    return /* @__PURE__ */ jsx(CustomerContactsContext.Provider, { value: contacts, children });
  },
  {
    name: "CustomerAddressesSource",
    sync: (services, { customerId }) => [
      {
        execute: () => customerId && services.customers.getCustomerAddresses(customerId),
        deps: [customerId]
      }
    ],
    queries: ({ customerId }) => [
      {
        name: "addresses",
        defaultValue: [],
        execute: (db) => customerId ? db.addresses.where("accountId").equals(customerId).toArray() : [],
        deps: [customerId]
      }
    ]
  }
);
const CustomerContactSource = reactive.component(
  ({ data, withAddresses = true, children }) => {
    const customer = data.customer?.id ? data.customer : null;
    return /* @__PURE__ */ jsx(
      CustomerAddressesSource,
      {
        customer,
        customerId: withAddresses ? customer?.id : void 0,
        children
      }
    );
  },
  {
    name: "CustomerContactSource",
    sync: (services, { accountId }) => [
      {
        execute: () => accountId && services.customers.findCustomer(Number(accountId)),
        deps: [accountId]
      }
    ],
    queries: ({ accountId }) => [
      {
        name: "customer",
        defaultValue: null,
        execute: (db) => accountId ? db.customers.get(Number(accountId)) : null,
        deps: [accountId]
      }
    ]
  }
);
const CustomerPhonePicker$1 = ({ onSelect, label }) => {
  const contacts = useCustomerContacts();
  const phones = useMemo(() => toPhoneOptions(contacts?.customer), [contacts]);
  return /* @__PURE__ */ jsx(PhonePickerMenu, { phones, onSelect, label });
};
const CustomerEmailButton$1 = ({ onSelect, children }) => {
  const contacts = useCustomerContacts();
  return /* @__PURE__ */ jsx(EmailFillButton, { email: contacts?.customer.email, onSelect, children });
};
const CustomerAddressPicker = ({ onSelect, label }) => {
  const contacts = useCustomerContacts();
  return /* @__PURE__ */ jsx(AddressPickerMenu, { addresses: contacts?.addresses, onSelect, label });
};
const useFieldPicks = (defaults, shown) => {
  const [picks, setPicks] = useState({});
  const pending = useRef(null);
  useEffect(() => {
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    setPicks((current) => ({ ...current, [next.name]: next.value }));
  }, [picks]);
  const valueOf = (name) => name in picks ? picks[name] : defaults[name];
  const pick = (name, value) => {
    const onScreen = shown?.[name];
    const unchanged = deepEquals(valueOf(name), value) && onScreen !== void 0 && !deepEquals(onScreen, value);
    if (unchanged) pending.current = { name, value };
    setPicks((current) => ({ ...current, [name]: unchanged ? onScreen : value }));
  };
  return { valueOf, pick };
};
const ManualInvoiceNotice = FormField(() => /* @__PURE__ */ jsxs(Fieldset, { children: [
  /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Manual invoice" }),
  /* @__PURE__ */ jsx(Description, { children: "Invoices are sent to the billing party's email. No payment is collected automatically." }),
  /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-app)] px-3.5 py-3 text-[13px] leading-relaxed text-[var(--fg-2)]", children: [
    /* @__PURE__ */ jsx("i", { className: "ri-information-line mt-0.5 flex-none text-[var(--lf-info-500)]" }),
    /* @__PURE__ */ jsx("span", { children: "Invoice will be sent to the billing party's email. No card or bank account is required." })
  ] })
] }));
const profileAssignment = ({ profiles = [], profile, hasSubscription }) => ({
  hasSubscription: !!hasSubscription,
  isSubscriptionProfile: !!profile?.assignedToSubscription,
  openPlanCount: profiles.filter((p) => p.id !== profile?.id).reduce((total, p) => total + (p.paymentPlanCount ?? 0), 0)
});
const Note = ({ children }) => /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2 text-[13px] text-[var(--fg-2)]", children: [
  /* @__PURE__ */ jsx("i", { className: "ri-checkbox-circle-fill mt-0.5 flex-none text-success" }),
  /* @__PURE__ */ jsx("span", { children })
] });
const ProfileAssignmentFields = ({ assignment, values }) => {
  if (!assignment) return null;
  const { hasSubscription, isSubscriptionProfile, openPlanCount } = assignment;
  const isFrozen = !!values?.isFrozen;
  const isManualInvoice = Number(values?.profileType) === PaymentProfileType.ManualInvoice;
  const canAssignSubscription = hasSubscription && !isSubscriptionProfile;
  const canAssignPlans = openPlanCount > 0 && !isManualInvoice;
  const plans = `${openPlanCount} open payment ${openPlanCount === 1 ? "plan" : "plans"}`;
  if (!canAssignSubscription && !canAssignPlans && !isSubscriptionProfile) return null;
  return /* @__PURE__ */ jsxs(Fieldset, { children: [
    /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Use this profile for" }),
    (canAssignSubscription || canAssignPlans) && /* @__PURE__ */ jsx(Description, { children: isFrozen ? "A frozen profile cannot be assigned. Clear Frozen to assign it." : "Applied when the profile is saved." }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2.5", children: [
      isSubscriptionProfile && /* @__PURE__ */ jsx(Note, { children: "The subscription renews against this profile." }),
      canAssignSubscription && !isFrozen && /* @__PURE__ */ jsx(CheckboxField, { name: "assignToSubscription", value: false, children: "The subscription — renewals charge this profile" }),
      canAssignPlans && !isFrozen && /* @__PURE__ */ jsx(CheckboxField, { name: "assignToPaymentPlans", value: false, children: `The customer's ${plans} — future installments charge this profile` })
    ] })
  ] });
};
const CustomerPaymentProfileForm = ({
  accountId,
  profile,
  billingAddress,
  editing,
  values,
  assignment,
  onChange
}) => {
  const { valueOf, pick } = useFieldPicks(
    {
      phoneNumber: profile?.phoneNumber ?? "",
      email: profile?.email ?? "",
      billingAddress
    },
    values
  );
  return /* @__PURE__ */ jsx(CustomerContactSource, { accountId, children: /* @__PURE__ */ jsxs(Uniformly, { onChange, throttle: 0, children: [
    /* @__PURE__ */ jsxs(Fieldset, { children: [
      /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Profile info" }),
      /* @__PURE__ */ jsx(Description, { children: "A name and type for this payment method." }),
      /* @__PURE__ */ jsxs(Grid, { cols: 3, className: "gap-3.5", children: [
        /* @__PURE__ */ jsx(Grid.Item, { colSpan: 2, children: /* @__PURE__ */ jsx(
          TextField,
          {
            name: "name",
            label: "Profile name",
            isRequired: true,
            value: profile?.name ?? "",
            placeholder: "e.g. Karen Halloran — Visa",
            validation: (x) => x.string().required("Profile name is required")
          }
        ) }),
        /* @__PURE__ */ jsx("div", { className: "flex h-9 items-center self-end", children: /* @__PURE__ */ jsx(CheckboxField, { name: "isFrozen", value: profile?.isFrozen ?? false, children: "Frozen" }) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs(Fieldset, { children: [
      /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Billing party" }),
      /* @__PURE__ */ jsx(Description, { children: "Who is responsible for this payment method. A phone or an email is required." }),
      /* @__PURE__ */ jsxs(Grid, { cols: 3, className: "gap-3.5", children: [
        /* @__PURE__ */ jsx(
          TextField,
          {
            name: "firstName",
            label: "First name",
            isRequired: true,
            value: profile?.firstName ?? "",
            validation: (x) => x.string().max(150, "First name cannot exceed 150 characters").required("First name is required")
          }
        ),
        /* @__PURE__ */ jsx(
          TextField,
          {
            name: "lastName",
            label: "Last name",
            isRequired: true,
            value: profile?.lastName ?? "",
            validation: (x) => x.string().max(150, "Last name cannot exceed 150 characters").required("Last name is required")
          }
        ),
        /* @__PURE__ */ jsx(
          PhoneField,
          {
            name: "phoneNumber",
            label: "Phone",
            labelAction: /* @__PURE__ */ jsx(CustomerPhonePicker$1, { onSelect: (phone) => pick("phoneNumber", phone) }),
            isRequired: !values?.email,
            value: valueOf("phoneNumber"),
            validation: (x) => x.string().when("$email", {
              is: (email) => !email,
              then: (schema) => schema.required("Phone is required when there is no email"),
              otherwise: (schema) => schema.notRequired()
            })
          }
        ),
        /* @__PURE__ */ jsx(Grid.Item, { colSpan: 3, children: /* @__PURE__ */ jsx(
          TextField,
          {
            type: "email",
            name: "email",
            label: "Email",
            labelAction: /* @__PURE__ */ jsx(CustomerEmailButton$1, { onSelect: (email) => pick("email", email) }),
            isRequired: !values?.phoneNumber,
            value: valueOf("email"),
            validation: (x) => x.string().email("Invalid email").matches(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, {
              message: "Invalid email",
              excludeEmptyString: true
            }).max(100, "Email cannot exceed 100 characters").when("$phoneNumber", {
              is: (phone) => !phone,
              then: (schema) => schema.required("Email is required when there is no phone"),
              otherwise: (schema) => schema.notRequired()
            })
          }
        ) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs(Fieldset, { children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-4", children: [
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Billing address" }),
          /* @__PURE__ */ jsx(Description, { children: "Where statements and receipts are sent." })
        ] }),
        /* @__PURE__ */ jsx(CustomerAddressPicker, { onSelect: (address) => pick("billingAddress", address) })
      ] }),
      /* @__PURE__ */ jsx(AddressForm$1, { name: "billingAddress", value: valueOf("billingAddress") })
    ] }),
    /* @__PURE__ */ jsx(Label, { isRequired: true, children: "Payment type" }),
    /* @__PURE__ */ jsxs(
      TabsField,
      {
        name: "profileType",
        type: "number",
        value: profile?.profileType ?? DefaultPaymentProfileType,
        children: [
          /* @__PURE__ */ jsx(Tabs.ListContainer, { children: /* @__PURE__ */ jsx(Tabs.List, { "aria-label": "Payment type", children: PaymentProfileTypeOptions.map((option) => /* @__PURE__ */ jsxs(
            Tabs.Tab,
            {
              id: String(option.value),
              isDisabled: editing && option.value !== profile.profileType,
              children: [
                /* @__PURE__ */ jsxs("span", { className: "flex items-baseline gap-1.5", children: [
                  /* @__PURE__ */ jsx("i", { className: option.icon }),
                  /* @__PURE__ */ jsx("span", { children: option.label })
                ] }),
                /* @__PURE__ */ jsx(Tabs.Indicator, {})
              ]
            },
            option.value
          )) }) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: String(PaymentProfileType.CreditCard), children: /* @__PURE__ */ jsxs(Fieldset, { children: [
            /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Card details" }),
            /* @__PURE__ */ jsx(Description, { children: "The credit or debit card to charge." }),
            /* @__PURE__ */ jsx(
              CardField,
              {
                name: "paymentDetails",
                editing,
                maskedNumber: profile?.cardNumberMasked,
                value: {
                  expirationMonth: profile?.expirationMonth ?? "",
                  expirationYear: profile?.expirationYear ?? ""
                }
              }
            )
          ] }) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: String(PaymentProfileType.Ach), children: /* @__PURE__ */ jsxs(Fieldset, { children: [
            /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Bank account" }),
            /* @__PURE__ */ jsx(Description, { children: "ACH debit — typically processes within 3–5 business days." }),
            /* @__PURE__ */ jsx(
              BankAccountField,
              {
                name: "paymentDetails",
                editing,
                value: {
                  bankName: profile?.bankName ?? "",
                  accountType: profile?.accountType ?? "Checking"
                }
              }
            )
          ] }) }),
          /* @__PURE__ */ jsx(Tabs.Panel, { id: String(PaymentProfileType.ManualInvoice), children: /* @__PURE__ */ jsx(ManualInvoiceNotice, { name: "paymentDetails", value: {} }) })
        ]
      }
    ),
    /* @__PURE__ */ jsx(ProfileAssignmentFields, { assignment, values })
  ] }) });
};
const NEW = "new";
const EditPanel = ({ label, children }) => /* @__PURE__ */ jsxs("div", { className: "mt-2 overflow-hidden rounded-[10px] border border-[var(--accent)] bg-[var(--surface)]", children: [
  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2.5 border-b border-[var(--accent)] bg-[var(--pill-info-bg)] px-4 py-2.5", children: [
    /* @__PURE__ */ jsx("span", { className: "flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[var(--accent)] text-white", children: /* @__PURE__ */ jsx("i", { className: "ri-edit-line text-[12px]" }) }),
    /* @__PURE__ */ jsx("span", { className: "text-[13.5px] font-semibold text-[var(--accent)]", children: label })
  ] }),
  /* @__PURE__ */ jsx("div", { className: "px-4 py-4", children })
] });
const CustomerPaymentProfilesPanel = ({
  accountId,
  profiles = [],
  hasSubscription,
  isSaving,
  ref,
  onSave,
  onDelete
}) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const open = (target) => {
    setEditing(target);
    setForm(null);
  };
  const close = () => open(null);
  useImperativeHandle(ref, () => ({ open, close }));
  const profile = profiles.find((p) => p.id === editing) ?? null;
  const canSave = !!form?.isValid && !isSaving;
  const save = () => {
    const { paymentDetails, ...values } = form.data;
    onSave({ ...values, ...paymentDetails }, profile);
  };
  const editor = /* @__PURE__ */ jsxs(EditPanel, { label: editing === NEW ? "New payment profile" : "Editing payment profile", children: [
    /* @__PURE__ */ jsx(
      CustomerPaymentProfileForm,
      {
        accountId,
        profile,
        billingAddress: profile?.billingAddress,
        editing: !!profile,
        values: form?.data,
        assignment: profileAssignment({ profiles, profile, hasSubscription }),
        onChange: setForm
      },
      editing
    ),
    /* @__PURE__ */ jsx("div", { className: "mt-3", children: /* @__PURE__ */ jsx(
      ResourceActions,
      {
        saveLabel: "Save profile",
        saveSize: "md",
        cancelSize: "md",
        onSave: save,
        isLoading: isSaving,
        isSaveEnabled: canSave,
        errors: form?.errors ?? [],
        onCancel: close
      }
    ) })
  ] });
  return /* @__PURE__ */ jsx("div", { className: "px-7 py-6", children: /* @__PURE__ */ jsxs(Fieldset, { children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-start justify-between gap-4", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Payment profiles" }),
        /* @__PURE__ */ jsx(Description, { children: `${profiles.length} ${profiles.length === 1 ? "profile" : "profiles"} on file.` })
      ] }),
      /* @__PURE__ */ jsx(
        HorizontalControl,
        {
          hideChips: true,
          hideReload: true,
          hideSort: true,
          hideFilters: true,
          createLabel: "Add profile",
          createEnabled: editing !== NEW,
          onCreate: () => open(NEW)
        }
      )
    ] }),
    profiles.length === 0 && editing !== NEW ? /* @__PURE__ */ jsx("div", { className: "px-5 py-12 text-center text-[13px] text-[var(--fg-3)]", children: "No payment profiles on file." }) : /* @__PURE__ */ jsxs(
      DisclosureGroup,
      {
        className: "flex flex-col gap-2.5",
        expandedKeys: new Set(editing != null ? [editing] : []),
        children: [
          profiles.map((item) => /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx(
              ProfileCard,
              {
                profile: item,
                onEdit: () => open(item.id),
                onDelete: () => onDelete(item.id)
              }
            ),
            /* @__PURE__ */ jsx(Disclosure, { id: item.id, children: /* @__PURE__ */ jsx(Disclosure.Content, { children: /* @__PURE__ */ jsx(Disclosure.Body, { children: editing === item.id && editor }) }) })
          ] }, item.id)),
          /* @__PURE__ */ jsx("div", { className: "mt-2", children: /* @__PURE__ */ jsx(Disclosure, { id: NEW, children: /* @__PURE__ */ jsx(Disclosure.Content, { children: /* @__PURE__ */ jsx(Disclosure.Body, { children: editing === NEW && editor }) }) }) })
        ]
      }
    )
  ] }) });
};
const CustomerPaymentProfilesReactive = reactive.component(
  ({ services, data, monitors, customerId, hasSubscription }) => {
    const panel = useRef(null);
    const closeEditor = () => panel.current?.close();
    useSuccessReactor(services.paymentProfiles.createProfile, closeEditor);
    useSuccessReactor(services.paymentProfiles.updateProfile, closeEditor);
    const save = (values, profile) => profile ? services.paymentProfiles.updateProfile(customerId, profile, values) : services.paymentProfiles.createProfile(customerId, values);
    return /* @__PURE__ */ jsx(
      CustomerPaymentProfilesPanel,
      {
        ref: panel,
        accountId: customerId,
        profiles: data.profiles,
        hasSubscription,
        isSaving: monitors.createProfile.executing || monitors.updateProfile.executing,
        onSave: save,
        onDelete: (id) => services.paymentProfiles.deleteProfile(customerId, id)
      }
    );
  },
  {
    name: "CustomerPaymentProfilesController",
    sync: (services, { customerId }) => [
      {
        execute: () => customerId && services.paymentProfiles.getProfiles(Number(customerId)),
        deps: [customerId]
      },
      {
        execute: () => customerId && services.customers.getCustomerAddresses(Number(customerId), AddressType.Billing),
        deps: [customerId]
      }
    ],
    queries: ({ customerId }) => [
      {
        name: "profiles",
        defaultValue: [],
        execute: async (db) => {
          if (!customerId) return [];
          const profiles = await db.payment_profiles.where("accountId").equals(Number(customerId)).toArray();
          return Promise.all(
            profiles.map(async (profile) => ({
              ...profile,
              billingAddress: profile.addressId ? await db.addresses.get(profile.addressId) : void 0
            }))
          );
        },
        deps: [customerId]
      }
    ],
    monitors: (services) => [
      services.paymentProfiles.createProfile,
      services.paymentProfiles.updateProfile,
      services.paymentProfiles.deleteProfile
    ]
  }
);
const CustomerPaymentProfilesController = () => {
  const customer = useCustomer();
  return /* @__PURE__ */ jsx(
    CustomerPaymentProfilesReactive,
    {
      customerId: customer?.id,
      hasSubscription: customer?.hasSubscription
    }
  );
};
const CustomerLedgerController = () => {
  const customer = useCustomer();
  const CustomerLedger = useComponent("billing.customer-ledger");
  const onSummaryAction = (action) => {
  };
  return /* @__PURE__ */ jsx(CustomerLedger, { customerId: customer?.id, onSummaryAction });
};
const CustomerPaymentPlansController = () => {
  const customer = useCustomer();
  const PaymentPlansTable = useComponent("collections.payment-plans-table");
  return /* @__PURE__ */ jsx("div", { className: "flex min-h-0 flex-1 flex-col gap-4 px-7 py-6", children: customer?.id && /* @__PURE__ */ jsx(PaymentPlansTable, { accountId: customer.id }) });
};
const CustomerDeviceActivityController = () => {
  const customer = useCustomer();
  const DeviceTrafficTable = useComponent("devices.device-traffic-table");
  return /* @__PURE__ */ jsx("div", { className: "flex min-h-0 flex-1 flex-col gap-4 px-7 py-6", children: /* @__PURE__ */ jsx(DeviceTrafficTable, { customerId: customer?.id }) });
};
const CustomerNotificationsController = () => {
  const customer = useCustomer();
  const NotificationsList = useComponent("notifications.notifications-list");
  return /* @__PURE__ */ jsx("div", { className: "flex min-h-0 flex-1 flex-col gap-4 px-7 py-6", children: /* @__PURE__ */ jsx(NotificationsList, { customerId: customer?.id }) });
};
function CustomerNotesTab() {
  return /* @__PURE__ */ jsx("div", { className: "px-7 py-6", children: /* @__PURE__ */ jsx(AccountNotesList, {}) });
}
const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "On Hold" },
  { value: 4, label: "Cancellation Pending" },
  { value: 3, label: "Canceled" }
];
const filters = new Filters([
  new FilterInput({
    name: "fullName",
    title: "Customer Name"
  }),
  new FilterInput({
    name: "dealerName",
    title: "Dealer Name"
  }),
  new FilterSelect({
    name: "subscriptionStatusId",
    title: "Subscription Status",
    render: {
      hideContainer: false
    },
    loader: () => Promise.resolve(SUBSCRIPTION_STATUS_OPTIONS)
  }),
  new FilterInput({
    name: "email",
    title: "Email"
  }),
  new FilterInput({
    name: "mobilePhone",
    title: "Mobile Phone"
  }),
  new FilterToggle({
    name: "hasSubscription",
    title: "Has Subscription",
    defaultValue: false
  })
]);
const sorting = new Sorting(
  [
    { value: "id", title: "Customer ID" },
    { value: "fullName", title: "Customer Name" },
    { value: "subscriptionStatus", title: "Status" },
    { value: "dealerName", title: "Dealer" },
    { value: "dealerId", title: "Dealer ID" },
    { value: "email", title: "Email" },
    { value: "mobilePhone", title: "Mobile Phone" },
    { value: "hasSubscription", title: "Has Subscription" },
    { value: "createdOnUtc", title: "Created On" }
  ],
  "id",
  "desc"
);
const CustomersTable = ({ customers: customers2 = [], onSelect, topContent }) => {
  const renderCell = useCallback((row, columnKey) => {
    switch (columnKey) {
      case "id": {
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
          /* @__PURE__ */ jsx(AccountAvatar, { name: row.fullName, size: 34 }),
          /* @__PURE__ */ jsx(AccountName, { name: row.fullName, id: row.id }),
          /* @__PURE__ */ jsx(InactiveChip, { isActive: row.isActive })
        ] });
      }
      case "subscriptionStatus": {
        const status = row.subscription?.status ?? row.subscriptionStatus ?? "None";
        return /* @__PURE__ */ jsx(Pill, { tone: subscriptionTone(status), children: status });
      }
      case "dealerName":
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(AccountName, { name: row.dealerName, id: row.dealerId }),
          /* @__PURE__ */ jsx(InactiveChip, { isActive: row.dealerIsActive })
        ] });
      case "email":
        return /* @__PURE__ */ jsx(TextWithCopy, { text: row.email, value: row.email });
      default:
        return row[columnKey] ?? "-";
    }
  }, []);
  const gridColumns = useMemo(
    () => [
      {
        id: "id",
        header: "Customer",
        isRowHeader: true,
        allowsSorting: true,
        allowsResizing: true,
        minWidth: 240,
        cell: (row) => renderCell(row, "id")
      },
      {
        id: "dealerName",
        header: "Dealer",
        allowsSorting: true,
        minWidth: 180,
        cell: (row) => renderCell(row, "dealerName")
      },
      {
        id: "subscriptionStatus",
        header: "Subscription",
        allowsSorting: true,
        cell: (row) => renderCell(row, "subscriptionStatus")
      },
      {
        id: "email",
        header: "Email",
        allowsSorting: true,
        cell: (row) => renderCell(row, "email")
      },
      {
        id: "actions",
        header: "",
        pinned: "end",
        width: 64,
        minWidth: 64,
        align: "center",
        cell: (row) => /* @__PURE__ */ jsx(
          Button,
          {
            isIconOnly: true,
            size: "sm",
            variant: "light",
            onPress: () => onSelect?.(row),
            "aria-label": "View details",
            className: "text-[var(--fg-3)] hover:text-[var(--foreground)]",
            children: /* @__PURE__ */ jsx("i", { className: "ri-arrow-right-s-line" })
          }
        )
      }
    ],
    [renderCell, onSelect]
  );
  return /* @__PURE__ */ jsx(
    RequestBrokerDataGrid,
    {
      "aria-label": "Customers list table",
      columns: gridColumns,
      data: customers2,
      getRowId: (row) => row.id,
      allowsColumnResize: true,
      onRowAction: (key) => {
        const selected = customers2.find((item) => String(item.id) === String(key));
        if (selected) onSelect?.(selected);
      }
    }
  );
};
const CustomersListController = reactive.component(
  ({ services, data, monitors }) => {
    const { customers: customers2 } = data;
    const navigate = useNavigate();
    const handleExecute = useCallback((action, metadata) => {
      action(metadata);
    }, []);
    return /* @__PURE__ */ jsx(
      RequestBroker,
      {
        actionKey: "queryCustomers",
        action: services.customers.queryCustomers,
        filters,
        sorting,
        isExecuting: monitors.queryCustomers.executing,
        onExecute: handleExecute,
        children: /* @__PURE__ */ jsx(
          CustomersTable,
          {
            customers: customers2,
            onSelect: (item) => navigate(RoutePaths().customers.details(item.id))
          }
        )
      }
    );
  },
  {
    name: "CustomersListController",
    queries: (_, services) => [
      {
        name: "customers",
        execute: (db) => db.withRequestBroker(services.customers.queryCustomers.action, () => db.customers_page, {
          sorting: true
        }),
        deps: []
      }
    ],
    monitors: (services) => [services.customers.queryCustomers]
  }
);
const CustomersListLayout = () => /* @__PURE__ */ jsxs("div", { className: "flex h-full min-h-0 flex-col", children: [
  /* @__PURE__ */ jsx("div", { className: "mb-4 flex flex-row justify-between", children: /* @__PURE__ */ jsxs(Breadcrumbs, { className: "content-center", children: [
    /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Accounts" }),
    /* @__PURE__ */ jsx(BreadcrumbsItem, { children: "Customers" })
  ] }) }),
  /* @__PURE__ */ jsx("div", { className: "flex min-h-0 flex-1 flex-col", children: /* @__PURE__ */ jsx(Outlet, {}) })
] });
const CustomerContactDetailsFields = ({ customer }) => /* @__PURE__ */ jsxs(Fieldset, { children: [
  /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Contact details" }),
  /* @__PURE__ */ jsx(Description, { children: "Email and phone numbers on file for this customer." }),
  /* @__PURE__ */ jsxs(Grid, { cols: 2, className: "gap-2 grid-cols-[120px_1fr]", children: [
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(IconLabel, { icon: "mail-line", label: "Email" }) }),
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(
      EmailField,
      {
        name: "email",
        "aria-label": "Email",
        value: customer?.email ?? "",
        maxLength: 320,
        label: false
      }
    ) }),
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(IconLabel, { icon: "phone-line", label: "Home", required: true }) }),
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(
      PhoneField,
      {
        name: "homePhone",
        "aria-label": "Home phone",
        isRequired: true,
        value: customer?.homePhone ?? "",
        validation: validators.internationalPhone(),
        label: false
      }
    ) }),
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(IconLabel, { icon: "briefcase-line", label: "Work" }) }),
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
      /* @__PURE__ */ jsx("div", { className: "min-w-0 flex-1", children: /* @__PURE__ */ jsx(
        PhoneField,
        {
          name: "workPhone",
          "aria-label": "Work phone",
          value: customer?.workPhone ?? "",
          label: false
        }
      ) }),
      /* @__PURE__ */ jsx(WorkPhoneExtField, { value: customer?.workPhoneExt, className: "w-24" })
    ] }) }),
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(IconLabel, { icon: "smartphone-line", label: "Cell" }) }),
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(
      PhoneField,
      {
        name: "mobilePhone",
        "aria-label": "Cell phone",
        value: customer?.mobilePhone ?? "",
        label: false
      }
    ) })
  ] })
] });
const CustomerContactInfoForm = ({
  customer,
  onChange,
  address,
  shippingAddress,
  sameAsHome,
  onSameAsHomeChange
}) => {
  return /* @__PURE__ */ jsx("div", { className: "px-7 py-6 pb-20", children: /* @__PURE__ */ jsx(Card, { className: "border border-[var(--border)] bg-[var(--surface)]", children: /* @__PURE__ */ jsx(CardContent, { className: "p-6", children: /* @__PURE__ */ jsxs(Uniformly, { onChange, children: [
    /* @__PURE__ */ jsxs(Fieldset, { children: [
      /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Dealer" }),
      /* @__PURE__ */ jsx(Description, { children: "The dealer this customer is assigned to." }),
      /* @__PURE__ */ jsx(
        DealerSearchField$1,
        {
          name: "dealerId",
          label: "Dealer",
          isRequired: true,
          value: customer?.dealerId ?? 0,
          validation: (v) => v.number().required("Dealer is required"),
          transformOut: (v) => v != null ? Number(v) : null
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(Fieldset, { children: [
      /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Customer" }),
      /* @__PURE__ */ jsx(Description, { children: "Basic customer details" }),
      /* @__PURE__ */ jsxs(Grid, { cols: 4, className: "gap-2", children: [
        /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(TextField, { name: "prefix", label: "Prefix", value: customer?.prefix ?? "" }) }),
        /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(
          TextField,
          {
            name: "firstName",
            label: "First name",
            isRequired: true,
            value: customer?.firstName ?? "",
            validation: (v) => v.string().max(150, "First name cannot exceed 150 characters").required("First name is required")
          }
        ) }),
        /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(
          TextField,
          {
            name: "lastName",
            label: "Last name",
            isRequired: true,
            value: customer?.lastName ?? "",
            validation: (v) => v.string().max(150, "Last name cannot exceed 150 characters").required("Last name is required")
          }
        ) }),
        /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(TextField, { name: "suffix", label: "Suffix", value: customer?.suffix ?? "" }) }),
        /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
          DateField,
          {
            name: "dateOfBirth",
            label: "Date of birth",
            isRequired: true,
            value: customer?.dateOfBirth ?? null,
            validation: (v) => v.string().required("Date of birth is required")
          }
        ) }) })
      ] })
    ] }),
    /* @__PURE__ */ jsx(
      AccountAddressesFields,
      {
        address,
        shippingAddress,
        sameAsHome,
        onSameAsHomeChange,
        homeDescription: "The customer's primary service address"
      }
    ),
    /* @__PURE__ */ jsxs(Fieldset, { children: [
      /* @__PURE__ */ jsx(Fieldset.Legend, { children: "Timezone" }),
      /* @__PURE__ */ jsx(Description, { children: "The customer's local timezone, used for scheduling and alert timing." }),
      /* @__PURE__ */ jsx(
        TimezonesLookup,
        {
          name: "timeZone",
          label: "Timezone",
          value: customer?.timeZone ?? null
        }
      )
    ] }),
    /* @__PURE__ */ jsx(CustomerContactDetailsFields, { customer })
  ] }) }) }) });
};
const CustomerContactInfoReactive = reactive.component(
  ({ data, services, monitors }) => {
    const customer = useCustomer();
    const { physical: address, shipping } = data.addresses;
    const [customerForm, setCustomerForm] = useState({});
    const { sameAsHome, setSameAsHome, isChanged } = useSameAsHome(data.addresses);
    customerForm.isDirty || isChanged;
    const handleSave = () => {
      if (!customerForm.isValid) return;
      const { address: home, shippingAddress, ...rest } = customerForm.data;
      services.customers.updateCustomer(
        customer.id,
        rest,
        addressesPayload({ address: home, shippingAddress }, data.addresses, sameAsHome)
      );
    };
    return /* @__PURE__ */ jsxs(FlatCard, { children: [
      /* @__PURE__ */ jsx(FlatCard.Main, { children: /* @__PURE__ */ jsx(
        CustomerContactInfoForm,
        {
          customer,
          onChange: setCustomerForm,
          address,
          shippingAddress: shipping,
          sameAsHome,
          onSameAsHomeChange: setSameAsHome
        },
        `${customer.id}-${shipping?.id ?? 0}`
      ) }),
      /* @__PURE__ */ jsx(FlatCard.Footer, { children: /* @__PURE__ */ jsx(
        ResourceActions,
        {
          isSaveEnabled: customerForm.isValid,
          onSave: handleSave,
          isLoading: monitors.updateCustomer.executing,
          errors: customerForm.errors
        }
      ) })
    ] });
  },
  {
    name: "CustomerContactInfoController",
    sync: (services, { customerId }) => [
      {
        execute: () => customerId && services.customers.getCustomerAddresses(customerId),
        deps: [customerId]
      }
    ],
    queries: ({ customerId }) => [accountAddressesQuery(customerId)],
    monitors: (services) => [services.customers.updateCustomer]
  }
);
const CustomerContactInfoController = () => {
  const customer = useCustomer();
  return /* @__PURE__ */ jsx(CustomerContactInfoReactive, { customerId: customer?.id });
};
const CustomersRoutes = () => /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(Route, { path: "/", element: /* @__PURE__ */ jsx(CustomersListLayout, {}), children: /* @__PURE__ */ jsx(
    Route,
    {
      index: true,
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "Customers", operation: "view", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(CustomersListController, {}) })
    }
  ) }),
  /* @__PURE__ */ jsxs(
    Route,
    {
      path: ":customerId",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "Customers", operation: "view", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(WithParams, { element: BaseCustomerLayout }) }),
      children: [
        /* @__PURE__ */ jsx(Route, { index: true, element: /* @__PURE__ */ jsx(Navigate, { to: "overview", replace: true }) }),
        /* @__PURE__ */ jsx(Route, { path: "overview", element: /* @__PURE__ */ jsx(CustomerOverviewController, {}) }),
        /* @__PURE__ */ jsx(Route, { path: "contact-info", element: /* @__PURE__ */ jsx(CustomerContactInfoController, {}) }),
        /* @__PURE__ */ jsx(Route, { path: "notes", element: /* @__PURE__ */ jsx(CustomerNotesTab, {}) }),
        /* @__PURE__ */ jsx(
          Route,
          {
            path: "contacts",
            element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "EmergencyContacts", operation: "view", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(CustomerContactsController, {}) })
          }
        ),
        /* @__PURE__ */ jsx(
          Route,
          {
            path: "payment-profiles",
            element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "PaymentProfiles", operation: "view", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(CustomerPaymentProfilesController, {}) })
          }
        ),
        /* @__PURE__ */ jsx(Route, { path: "device-activity", element: /* @__PURE__ */ jsx(CustomerDeviceActivityController, {}) }),
        /* @__PURE__ */ jsx(
          Route,
          {
            path: "ledger",
            element: /* @__PURE__ */ jsx(
              RequirePolicy,
              {
                apiKey: "BillingAPI",
                resource: "Ledgers",
                operation: "view",
                fallback: /* @__PURE__ */ jsx(Forbidden, {}),
                children: /* @__PURE__ */ jsx(CustomerLedgerController, {})
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          Route,
          {
            path: "payment-plans",
            element: /* @__PURE__ */ jsx(
              RequirePolicy,
              {
                apiKey: "CollectionsAPI",
                resource: "PaymentPlans",
                operation: "view",
                fallback: /* @__PURE__ */ jsx(Forbidden, {}),
                children: /* @__PURE__ */ jsx(CustomerPaymentPlansController, {})
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          Route,
          {
            path: "notifications",
            element: /* @__PURE__ */ jsx(
              RequirePolicy,
              {
                apiKey: "NotificationsAPI",
                resource: "Notifications",
                operation: "view",
                fallback: /* @__PURE__ */ jsx(Forbidden, {}),
                children: /* @__PURE__ */ jsx(CustomerNotificationsController, {})
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          Route,
          {
            path: "linked-accounts",
            element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "LinkedAccounts", operation: "view", fallback: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(LinkedAccountsController, {}) })
          }
        )
      ]
    }
  )
] });
const Router = () => /* @__PURE__ */ jsx(BaseLayout, { children: /* @__PURE__ */ jsxs(Routes, { children: [
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "dealers/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "dealers", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(DealersRouter, {}) })
    }
  ),
  /* @__PURE__ */ jsx(
    Route,
    {
      path: "customers/*",
      element: /* @__PURE__ */ jsx(RequirePolicy, { resource: "customers", operation: "view", placeholder: /* @__PURE__ */ jsx(Forbidden, {}), children: /* @__PURE__ */ jsx(CustomersRoutes, {}) })
    }
  )
] }) });
function getAccountsNavigation(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  return {
    isVisible: user.hasApiAccess(apiKey),
    items: [
      {
        title: "Dealers",
        route: "/dealers",
        icon: "ri-user-line",
        isVisible: runtime.config?.features.dealers && user.hasPolicy(apiKey, "Dealers", "View")
      },
      {
        title: "Customers",
        route: "/customers",
        icon: "ri-group-line",
        isVisible: runtime.config?.features.customers && user.hasPolicy(apiKey, "Customers", "View")
      }
    ]
  };
}
function getAccountsResources(runtime) {
  const user = runtime.user;
  const apiKey = runtime.config?.api?.apiKey ?? "";
  const items = [];
  if (user.hasPolicy(apiKey, "Dealers", "Create")) {
    items.push({
      key: "dealer",
      label: "New Dealer",
      route: "/dealers",
      description: "Create new dealer"
    });
  }
  return {
    items,
    handlers: ((navigate) => ({
      dealer: () => navigate(RoutePaths().dealers.create())
    }))
  };
}
const Embed = createEmbed({ runtime: () => appRuntime, render });
const PAYMENT_PROFILES_SELECT_FIELD = "accounts.payment-profiles-field";
const PaymentProfilesSelectField = (props) => createElement(
  Embed,
  null,
  // The field is JS, so its props are inferred rather than declared. The interface above is the
  // contract; this cast is what ties the two together.
  createElement(PaymentProfilesSelectField$1, props)
);
const PaymentProfileCreate$1 = reactive.component(
  ({ services, monitors, data, accountId, onSuccess, onCancel }) => {
    const [form, setForm] = useState(null);
    useSuccessReactor(services.paymentProfiles.createProfile, (payload, args) => {
      if (Number(args[0]) !== Number(accountId)) return;
      services.paymentProfiles.getProfiles(Number(accountId));
      onSuccess?.(payload?.profile ?? payload);
    });
    const save = () => {
      const { paymentDetails, ...values } = form.data;
      services.paymentProfiles.createProfile(Number(accountId), { ...values, ...paymentDetails });
    };
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-3", children: [
      /* @__PURE__ */ jsx(
        CustomerPaymentProfileForm,
        {
          accountId,
          editing: false,
          values: form?.data,
          assignment: profileAssignment({
            profiles: data.profiles,
            hasSubscription: data.customer?.hasSubscription
          }),
          onChange: setForm
        }
      ),
      /* @__PURE__ */ jsxs(DrawerActions, { children: [
        onCancel && /* @__PURE__ */ jsxs(Button, { variant: "secondary", onPress: onCancel, children: [
          /* @__PURE__ */ jsx("i", { className: "ri-close-line" }),
          " Cancel"
        ] }),
        /* @__PURE__ */ jsx(
          SaveButton,
          {
            label: "Save profile",
            iconClass: "ri-bank-card-line",
            withConfirm: false,
            isDisabled: !form?.isValid,
            isLoading: monitors.createProfile.executing,
            onPress: save
          }
        )
      ] })
    ] });
  },
  {
    name: "PaymentProfileCreate",
    sync: (services, { accountId }) => [
      {
        // findCustomer: an account that is not a customer has no subscription, not an error.
        execute: () => accountId && services.customers.findCustomer(Number(accountId)),
        deps: [accountId]
      },
      {
        execute: () => accountId && services.paymentProfiles.getProfiles(Number(accountId)),
        deps: [accountId]
      }
    ],
    queries: ({ accountId }) => [
      {
        name: "customer",
        defaultValue: null,
        execute: (db) => accountId ? db.customers.get(Number(accountId)) : null,
        deps: [accountId]
      },
      {
        name: "profiles",
        defaultValue: [],
        execute: (db) => accountId ? db.payment_profiles.where("accountId").equals(Number(accountId)).toArray() : [],
        deps: [accountId]
      }
    ],
    monitors: (services) => [services.paymentProfiles.createProfile]
  }
);
const PAYMENT_PROFILE_CREATE = "accounts.payment-profile-create";
const PaymentProfileCreate = (props) => createElement(
  Embed,
  null,
  createElement(PaymentProfileCreate$1, props)
);
const DEALER_SEARCH_FIELD = "accounts.dealer-search-field";
const DealerSearchField = (props) => createElement(
  Embed,
  null,
  createElement(DealerSearchField$1, props)
);
const CUSTOMER_SEARCH_FIELD = "accounts.customer-search-field";
const CustomerSearchField = (props) => createElement(
  Embed,
  null,
  createElement(CustomerSearchField$1, props)
);
const CUSTOMER_CONTACTS_PANEL = "accounts.customer-contacts-panel";
const CustomerContactsPanel = (props) => createElement(
  Embed,
  null,
  // The panel itself is JS, so its props are inferred rather than declared. The interface above
  // is the contract; this cast is what ties the two together.
  createElement(CustomerContactsPanel$1, props)
);
const CUSTOMER_PHONE_PICKER = "accounts.customer-phone-picker";
const CUSTOMER_EMAIL_BUTTON = "accounts.customer-email-button";
const Source = CustomerContactSource;
const withCustomerContacts = (Picker) => {
  const Embedded = ({ accountId, ...rest }) => createElement(
    Embed,
    null,
    createElement(
      Source,
      { accountId, withAddresses: false },
      createElement(Picker, rest)
    )
  );
  return Embedded;
};
const CustomerPhonePicker = withCustomerContacts(
  CustomerPhonePicker$1
);
const CustomerEmailButton = withCustomerContacts(
  CustomerEmailButton$1
);
const ACCOUNT_SEARCH_PANEL = "accounts.search-panel";
const AccountSearchPanel = (props) => createElement(
  Embed,
  null,
  createElement(AccountSearchPanel$1, props)
);
const CUSTOMER_SEARCH_COMBO_BOX = "accounts.customer-search-combo-box";
const CustomerSearchComboBox = (props) => createElement(
  Embed,
  null,
  createElement(CustomerSearchComboBox$1, props)
);
const DEALER_SEARCH_COMBO_BOX = "accounts.dealer-search-combo-box";
const DealerSearchComboBox = (props) => createElement(
  Embed,
  null,
  createElement(DealerSearchComboBox$1, props)
);
const ACCOUNT_LINK = "accounts.account-link";
const AccountLink = AccountLink$1;
const LINKED_ACCOUNT_NOTICE = "accounts.linked-account-notice";
const LinkedAccountNotice = LinkedAccountNotice$1;
let appRuntime = null;
const initAccountsRuntime = (hostRuntime) => {
  appRuntime = hostRuntime.registerApp("accounts", {
    description: {
      title: "Accounts",
      iconClass: "ri-user-line",
      defaultRoute: "/dealers",
      isVisible: (runtime) => runtime.user.hasApiAccess(runtime.config?.api?.apiKey ?? "")
    },
    routes: Router,
    navigation: getAccountsNavigation,
    resources: getAccountsResources,
    config: () => AccountConfig.current
  });
  hostRuntime.components.register(PAYMENT_PROFILES_SELECT_FIELD, PaymentProfilesSelectField);
  hostRuntime.components.register(PAYMENT_PROFILE_CREATE, PaymentProfileCreate);
  hostRuntime.components.register(DEALER_SEARCH_FIELD, DealerSearchField);
  hostRuntime.components.register(CUSTOMER_SEARCH_FIELD, CustomerSearchField);
  hostRuntime.components.register(CUSTOMER_CONTACTS_PANEL, CustomerContactsPanel);
  hostRuntime.components.register(CUSTOMER_PHONE_PICKER, CustomerPhonePicker);
  hostRuntime.components.register(CUSTOMER_EMAIL_BUTTON, CustomerEmailButton);
  hostRuntime.components.register(ACCOUNT_SEARCH_PANEL, AccountSearchPanel);
  hostRuntime.components.register(CUSTOMER_SEARCH_COMBO_BOX, CustomerSearchComboBox);
  hostRuntime.components.register(DEALER_SEARCH_COMBO_BOX, DealerSearchComboBox);
  hostRuntime.components.register(ACCOUNT_LINK, AccountLink);
  hostRuntime.components.register(LINKED_ACCOUNT_NOTICE, LinkedAccountNotice);
  hostRuntime.userContexts.register(CurrentDealer);
};
export {
  initAccountsRuntime
};
//# sourceMappingURL=index.js.map
